import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { ReactComponent as RefreshIcon } from '../../../assets/Refresh_icon.svg';
import { ReactComponent as GoogleDriveIcon } from '../../../assets/Google_Drive_icon.svg';
import { ReactComponent as AmazonS3Icon } from '../../../assets/amazon-s3-icon.svg';
import { ReactComponent as GoogleCloudIcon } from '../../../assets/google-cloud-icon.svg';
import { ReactComponent as AzureIcon } from '../../../assets/azure-icon.svg';
import FSAImage from '../../../assetsPipeline/FSA.png';
import FSAInfoImage from '../../../assetsPipeline/FSA1.png';
import ISDImage from '../../../assetsPipeline/ISD.png';
import ISDInfoImage from '../../../assetsPipeline/ISD1.png';
import PSDImage from '../../../assetsPipeline/PSD.png';
import PSDInfoImage from '../../../assetsPipeline/PSD1.png';
import SSDImage from '../../../assetsPipeline/SSD.png';
import SSDInfoImage from '../../../assetsPipeline/SSD1.png';
import HCAImage from '../../../assetsPipeline/HCA.png';
import HCAInfoImage from '../../../assetsPipeline/HCA1.png';
import GWAImage from '../../../assetsPipeline/GWA.png';
import GWAInfoImage from '../../../assetsPipeline/GWA1.png';
import rdvShadowImage from '../../../assets/rdvshadow.png';
import SGAImage from '../../../assetsPipeline/SGA.png';
import SGAInfoImage from '../../../assetsPipeline/SGA1.png';
import CLZImage from '../../../assetsPipeline/CLZ.png';
import CLZInfoImage from '../../../assetsPipeline/CLZ1.png';
import EXGImage from '../../../assetsPipeline/EXG.png';
import EXGInfoImage from '../../../assetsPipeline/EXG1.png';
import EMBImage from '../../../assetsPipeline/EMB.png';
import EMBInfoImage from '../../../assetsPipeline/EMB1.png';
import './RSAAnalysis.css';
import TabsRSA from './Services/TabsRSA/TabsRSA';
import PathSelectorMap, { getPathCoordinatesEveryFeet } from './PathSelectorMap/PathSelectorMap';
import { useLaneEditHistory } from './PathSelectorMap/useLaneEditHistory';
import { getPathLengthMiles } from './PathsPanels/PathsPanelWaypoints';
import IntersectionPanelContent from './PathsPanels/IntersectionPanelContent';
import './PathsPanels/IntersectionPanelContent.css';
import MapsPanelContent from './PathsPanels/MapsPanelContent';
import { useRsaPaths, sanitizePathLabel, makeUniqueLabel } from './PathsPanels/useRsaPaths';
import { RsaPathsContext } from './PathsPanels/RsaPathsContext';
import PathsListPanel from './PathsPanels/PathsListPanel';
import { API_BASE_URL, ENVIRONMENT, processApi } from '../../../API/api';
import PermissionGate from '../../PermissionGate/PermissionGate';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import AnalysisDashboard, { getRunListProgressStats, getDashboardRunActiveStepDisplay } from '../AnalysisDashboard/AnalysisDashboard';
import UploadModal from '../../Projects/ProjectView/FileExplorer/UploadModal';
import FileExplorer from '../../Projects/ProjectView/FileExplorer/FileExplorer';
import DeleteConfirmModal from '../../Projects/ProjectView/FileExplorer/DeleteConfirmModal';
import '../AnalysisDashboard/AnalysisDashboard.css';
import fileService from '../../../services/fileService';
import { useUserRuns } from '../../../hooks/useUserRuns';
import progressService from '../../../services/progressService';
import {
  DEFAULT_GLOBAL_BOUNDS,
  DEFAULT_LOCAL_CONFIG,
  localExtentToLatLngBounds,
} from './mapOverlayCoordinateUtils';
import {
  latLngPointsToBounds,
  projectedBboxToLatLngQuadAsync,
  projectedBboxToLatLngBoundsAsync,
  latLngToProjectedXYAsync,
  projectedPointsToLatLngAsync,
} from './wktTransformApi';
import { normalizeWktInput } from './wktNormalize';
// Using relative URLs for production (CloudFront), API_BASE_URL for local development

// Add debugging to intercept all fetch/axios calls
(() => {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('[SEARCH] NULL FETCH CALL DETECTED:', url);
      console.trace('[SEARCH] Stack trace:', new Error().stack);
    }
    return originalFetch(...args);
  };
  
  const originalAxiosGet = axios.get;
  axios.get = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('[SEARCH] NULL AXIOS GET CALL DETECTED:', url);
      console.trace('[SEARCH] Stack trace:', new Error().stack);
    }
    return originalAxiosGet(...args);
  };
})();

const TASK_OPTIONS = [
  'Stopping Sight Distance (SSD)',
  'Passing Sight Distance (PSD)',
  'Flat Spot Analysis (FSA)',
  'Intersection Sight Distance (ISD)',
  'Sunglare Analysis (GLR)',
  'Guardrails Warrant (GRW)',
  'Horizontal Curve Analysis (HCA)',
  'Existing Guardrails (EXG)',
  'Embankment Analysis (EMB)',
  'Clear Zone Analysis (CLZ)',
];

const DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING = {
  'Stopping Sight Distance (SSD)': '1rlzhq4UcaG89Z6-BgsvkzEoy07c-_FAh', // SSD
  'Passing Sight Distance (PSD)': '1WSPVWSMQm9ZNVZYi63glWnHtovalxiSw', // PSD
  'Flat Spot Analysis (FSA)': '1o5Tkz_1kZ3L9duI1m8iCUru3eg6kkj7o', // FSA
  'Intersection Sight Distance (ISD)': '14Wcpu_nadstIKGkMfydgdCn91JuTfN7a', // ISD
  'Sunglare Analysis (GLR)': '1SLUCIlJJ_Za_5wcOe08NZ_O7S_Y2VIRM', // GLR
  'Guardrails Warrant (GRW)': '1YrgG9gBrxU2qPP0OdeAF875RDvwyR5Ax', // Guardrail_Warrant
  'Horizontal Curve Analysis (HCA)': '1ui2FC_9umPQwJnVRxvGLFG3vheE2jzu8', // Horizontal
  'Existing Guardrails (EXG)': '1_UYE80G3n99vcEHPzOVFappGDXnEyThg', // Existing_Guardrails
  'Embankment Analysis (EMB)': '1wgbWg82DReXKI5SYbLVI4Ib6OGFbY7DY', // Embankment
  'Clear Zone Analysis (CLZ)': '1R7RxChS8YYc1v4zu_1moBj5pnVA-NKmQ', // ClearZone
};

const RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING = {
  'Intersection Sight Distance (ISD)': '1utdS8VLw2hoCf00xZbXhYtcTsWnWDdsm', // ISD-model.json
  'Passing Sight Distance (PSD)': '10bkKVtyerK5Qe20g7Rc15ncwDQn4daih', // PSD-model.json
  'Stopping Sight Distance (SSD)': '1rTfTDLA48ET--fAduVGsI0ad3LFssmLC', // SSD-model.json
  'Flat Spot Analysis (FSA)': '1UATrkaa_ASvlJejyfi0TekiM3nBfO9lC', // FSA-model.json
  'Sunglare Analysis (GLR)': '1irrUhf9npguUDAxHy29tILDiv7DTVO0t', // GLR-model.json
  'Horizontal Curve Analysis (HCA)': '1ElRBuXgNAHakGVVw11671QAn34IeQQKN', // Horizontal-model.json
};

const RSA_MODEL_TASK_OPTIONS = [
  'Stopping Sight Distance (SSD)',
  'Passing Sight Distance (PSD)',
  'Flat Spot Analysis (FSA)',
  'Intersection Sight Distance (ISD)',
  'Sunglare Analysis (GLR)',
  'Horizontal Curve Analysis (HCA)',
];

const normalizeFolderName = (value) => String(value ?? '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');

const TASK_FOLDER_CODE_TO_OPTION = {
  RSASSD: 'Stopping Sight Distance (SSD)',
  RSAPSD: 'Passing Sight Distance (PSD)',
  RSAFSA: 'Flat Spot Analysis (FSA)',
  RSAISD: 'Intersection Sight Distance (ISD)',
  RSAGRW: 'Guardrails Warrant (GRW)',
  RSACLZ: 'Clear Zone Analysis (CLZ)',
  RSAEMB: 'Embankment Analysis (EMB)',
  RSAEXG: 'Existing Guardrails (EXG)',
  RSAGLR: 'Sunglare Analysis (GLR)',
  RSAHCA: 'Horizontal Curve Analysis (HCA)',
};

const RSA_FOLDER_CODE_TO_SUFFIX = {
  RSASSD: 'SSD',
  RSAPSD: 'PSD',
  RSAFSA: 'FSA',
  RSAISD: 'ISD',
  RSAGLR: 'GLR',
  RSAGRW: 'GRW',
  RSAHCA: 'HCA',
  RSAEXG: 'EXG',
  RSAEMB: 'EMB',
  RSACLZ: 'CLZ',
};

const TASK_OPTION_TO_SERVICE = {
  'Stopping Sight Distance (SSD)': 'stopping_sight_distance',
  'Passing Sight Distance (PSD)': 'passing_sight_distance',
  'Flat Spot Analysis (FSA)': 'flat_spot_analysis',
  'Intersection Sight Distance (ISD)': 'intersection_sight_distance',
  'Sunglare Analysis (GLR)': 'sunglare_analysis',
  'Guardrails Warrant (GRW)': 'guardrails_warrant',
  'Horizontal Curve Analysis (HCA)': 'horizontal',
  'Existing Guardrails (EXG)': 'existing_guardrails',
  'Embankment Analysis (EMB)': 'embankment',
  'Clear Zone Analysis (CLZ)': 'clear_zone_analysis',
};

const TASK_OPTION_TO_FOLDER_CODE = Object.entries(TASK_FOLDER_CODE_TO_OPTION).reduce((acc, [folderCode, taskOption]) => {
  acc[taskOption] = folderCode;
  return acc;
}, {});

const SERVICE_TO_TASK_OPTION = Object.entries(TASK_OPTION_TO_SERVICE).reduce((acc, [taskOption, service]) => {
  acc[service] = taskOption;
  return acc;
}, {});

const SERVICE_TO_RSA_FOLDER_CODE = Object.entries(SERVICE_TO_TASK_OPTION).reduce((acc, [service, taskOption]) => {
  const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
  if (folderCode) {
    acc[service] = folderCode;
  }
  return acc;
}, {});

const getFileId = (file) => file?.id ?? file?.Id;
const getParentId = (file) => file?.parent_id ?? file?.parentId ?? null;
const getGoogleDriveId = (file) => file?.google_drive_id ?? file?.googleDriveId ?? null;
const getFileName = (file) => file?.name ?? file?.Name ?? '';
const isFolderItem = (file) => file?.isfolder === 1 || file?.isFolder === true;
const normalizeDriveFolderId = (value) => {
  const id = String(value ?? '').trim();
  if (!id) return '';
  const lower = id.toLowerCase();
  if (lower === '0' || lower === 'null' || lower === 'none' || lower === 'undefined') {
    return '';
  }
  return id;
};
const isXmlFileName = (name) => /\.xml$/i.test(String(name ?? '').trim());
const isDxfFileName = (name) => /\.dxf$/i.test(String(name ?? '').trim());
const isLasLazFileName = (name) => /\.(las|laz)$/i.test(String(name ?? '').trim());
const getDisplayFileName = (name) => String(name ?? '').trim().replace(/\.xml$/i, '');
const normalizeFileNameKey = (name) => String(name ?? '').trim().toLowerCase();
const DIRECTION_TAG_REGEX = /<DIRECTION>\s*([A-Za-z])\s*<\/DIRECTION>/i;
const STATE_TAG_REGEX = /<STATE>\s*([^<]+?)\s*<\/STATE>/i;
const BEGIN_MILE_TAG_REGEX = /<BEGIN_MILE>\s*([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s*<\/BEGIN_MILE>/gi;
const END_MILE_TAG_REGEX = /<END_MILE>\s*([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s*<\/END_MILE>/gi;
const INTERSECTION_POINTS_BLOCK_REGEX = /<INTERSECTION_POINTS\b[\s\S]*?<\/INTERSECTION_POINTS>/gi;
const INTERSECTION_TAG_REGEX = /<INTERSECTION(?=[\s>])/gi;

const computeDirectionalMilesFromValues = (beginValue, endValue, direction) => {
  if (!Number.isFinite(beginValue) || !Number.isFinite(endValue)) {
    return 0;
  }
  const dir = String(direction || '').trim().toUpperCase();
  if (dir === 'N') {
    return beginValue - endValue;
  }
  if (dir === 'P') {
    return endValue - beginValue;
  }
  return endValue - beginValue;
};

const sumDirectionalMilesFromXml = (xmlText) => {
  if (typeof xmlText !== 'string' || !xmlText) {
    return 0;
  }

  const directionMatch = DIRECTION_TAG_REGEX.exec(xmlText);
  const direction = directionMatch?.[1] || '';
  let total = 0;
  let parsedWithDom = false;

  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const parseError = doc.getElementsByTagName('parsererror');
    if (!parseError || parseError.length === 0) {
      const pathNodes = Array.from(doc.getElementsByTagName('PATH'));
      if (pathNodes.length > 0) {
        parsedWithDom = true;
        for (const pathNode of pathNodes) {
          const beginText = pathNode.getElementsByTagName('BEGIN_MILE')[0]?.textContent ?? '';
          const endText = pathNode.getElementsByTagName('END_MILE')[0]?.textContent ?? '';
          const beginValue = Number(beginText);
          const endValue = Number(endText);
          total += computeDirectionalMilesFromValues(beginValue, endValue, direction);
        }
      }
    }
  } catch (e) {
    // Fallback to regex parser below.
  }

  if (!parsedWithDom) {
    const begins = [];
    const ends = [];
    let beginMatch = BEGIN_MILE_TAG_REGEX.exec(xmlText);
    while (beginMatch) {
      begins.push(Number(beginMatch[1]));
      beginMatch = BEGIN_MILE_TAG_REGEX.exec(xmlText);
    }
    BEGIN_MILE_TAG_REGEX.lastIndex = 0;

    let endMatch = END_MILE_TAG_REGEX.exec(xmlText);
    while (endMatch) {
      ends.push(Number(endMatch[1]));
      endMatch = END_MILE_TAG_REGEX.exec(xmlText);
    }
    END_MILE_TAG_REGEX.lastIndex = 0;

    const pairCount = Math.min(begins.length, ends.length);
    for (let i = 0; i < pairCount; i += 1) {
      total += computeDirectionalMilesFromValues(begins[i], ends[i], direction);
    }
  }

  return total;
};

const countIsdManeuversFromXml = (xmlText) => {
  if (typeof xmlText !== 'string' || !xmlText) {
    return 0;
  }

  let total = 0;
  let parsedWithDom = false;

  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const parseError = doc.getElementsByTagName('parsererror');
    if (!parseError || parseError.length === 0) {
      const pointsNodes = Array.from(doc.getElementsByTagName('INTERSECTION_POINTS'));
      if (pointsNodes.length > 0) {
        parsedWithDom = true;
        for (const pointsNode of pointsNodes) {
          total += pointsNode.getElementsByTagName('INTERSECTION').length;
        }
      }
    }
  } catch (e) {
    // Fallback to regex parser below.
  }

  if (!parsedWithDom) {
    const blocks = xmlText.match(INTERSECTION_POINTS_BLOCK_REGEX) || [];
    for (const block of blocks) {
      const intersections = block.match(INTERSECTION_TAG_REGEX) || [];
      total += intersections.length;
    }
  }

  return Number.isFinite(total) ? total : 0;
};

const readStateFromXml = (xmlText) => {
  if (typeof xmlText !== 'string' || !xmlText.trim()) {
    return '';
  }

  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const parseError = doc.getElementsByTagName('parsererror');
    if (!parseError || parseError.length === 0) {
      const stateText = doc.getElementsByTagName('STATE')[0]?.textContent ?? '';
      const normalized = String(stateText).trim();
      if (normalized) {
        return normalized.toUpperCase();
      }
    }
  } catch (e) {
    // Fallback to regex parsing below.
  }

  const stateMatch = STATE_TAG_REGEX.exec(xmlText);
  return String(stateMatch?.[1] ?? '').trim().toUpperCase();
};

const mapFolderCodeToTaskOption = (folderName) => {
  const normalizedCode = normalizeFolderName(folderName);
  return TASK_FOLDER_CODE_TO_OPTION[normalizedCode] || null;
};

const isProjectRootLikeFolder = (item, projectId, knownFolderIds = new Set()) => {
  if (!item) return false;
  const itemId = String(getFileId(item) ?? '').trim();
  const projectIdKey = String(projectId ?? '').trim();
  const parentRaw = getParentId(item);
  const parentId = parentRaw == null ? '' : String(parentRaw).trim();

  if (!parentId) {
    return true;
  }
  if (projectIdKey && parentId === projectIdKey) {
    return true;
  }
  if (itemId && parentId === itemId) {
    return true;
  }
  return !knownFolderIds.has(parentId);
};

const findDeliverablesFolderInItems = (items = [], options = {}) => {
  const {
    projectId = null,
    rootDriveIds = [],
    knownFolderIds = new Set(),
  } = options || {};
  const folders = (Array.isArray(items) ? items : [])
    .filter((item) => isFolderItem(item) && normalizeFolderName(getFileName(item)) === 'DELIVERABLES')
    .sort((a, b) => {
      const aParentDriveId = normalizeDriveFolderId(a?.parentDriveId);
      const bParentDriveId = normalizeDriveFolderId(b?.parentDriveId);
      const aRootScore = rootDriveIds.includes(aParentDriveId)
        ? 0
        : (isProjectRootLikeFolder(a, projectId, knownFolderIds) ? 0 : 1);
      const bRootScore = rootDriveIds.includes(bParentDriveId)
        ? 0
        : (isProjectRootLikeFolder(b, projectId, knownFolderIds) ? 0 : 1);
      if (aRootScore !== bRootScore) {
        return aRootScore - bRootScore;
      }

      const aHasDriveId = normalizeDriveFolderId(getGoogleDriveId(a)) ? 0 : 1;
      const bHasDriveId = normalizeDriveFolderId(getGoogleDriveId(b)) ? 0 : 1;
      if (aHasDriveId !== bHasDriveId) {
        return aHasDriveId - bHasDriveId;
      }

      return String(getFileId(b) ?? '').localeCompare(String(getFileId(a) ?? ''));
    });

  return folders[0] || null;
};

const resolveDeliverablesFolderForProject = async (projectId, filesSnapshot = null) => {
  if (!projectId) {
    return { driveId: '', treeId: '', name: 'Deliverables' };
  }

  const isProductionDomain = window.location.hostname.includes('123bimlive.com');
  const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
  const cacheBust = `_ts=${Date.now()}`;

  let files = Array.isArray(filesSnapshot) ? filesSnapshot : null;
  if (!files) {
    const filesUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${projectId}/files?${cacheBust}`
      : `/api/projects/${projectId}/files?${cacheBust}`;
    const filesResponse = await axios.get(filesUrl);
    files = filesResponse.data?.files || [];
  }

  const allFolders = (files || []).filter(isFolderItem);
  const knownFolderIds = new Set(
    allFolders
      .map((item) => String(getFileId(item) ?? '').trim())
      .filter(Boolean)
  );
  const dbTarget = findDeliverablesFolderInItems(allFolders, {
    projectId,
    knownFolderIds,
  });

  const fallbackInfo = dbTarget
    ? {
        driveId: normalizeDriveFolderId(getGoogleDriveId(dbTarget)),
        treeId: String(getFileId(dbTarget) ?? '').trim(),
        name: String(getFileName(dbTarget) || 'Deliverables').trim() || 'Deliverables',
      }
    : { driveId: '', treeId: '', name: 'Deliverables' };

  if (fallbackInfo.driveId) {
    return fallbackInfo;
  }

  const rootDriveIds = Array.from(new Set(
    allFolders
      .filter((item) => isProjectRootLikeFolder(item, projectId, knownFolderIds))
      .map((item) => normalizeDriveFolderId(getGoogleDriveId(item)))
      .filter(Boolean)
  ));

  if (rootDriveIds.length > 0) {
    const prefetchUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${projectId}/google-drive-prefetch?root_ids=${encodeURIComponent(rootDriveIds.join(','))}&${cacheBust}`
      : `/api/projects/${projectId}/google-drive-prefetch?root_ids=${encodeURIComponent(rootDriveIds.join(','))}&${cacheBust}`;
    const prefetchResponse = await axios.get(prefetchUrl).catch(() => ({ data: { folders: {} } }));
    const prefetchedFolders = prefetchResponse?.data?.folders || {};
    const driveTarget = findDeliverablesFolderInItems(
      Object.values(prefetchedFolders).flatMap((items) => (Array.isArray(items) ? items : [])),
      { projectId, rootDriveIds, knownFolderIds }
    );

    if (driveTarget) {
      const driveId = normalizeDriveFolderId(getGoogleDriveId(driveTarget));
      const treeId = String(getFileId(driveTarget) ?? (driveId ? `gd_${driveId}` : '')).trim();
      return {
        driveId,
        treeId,
        name: String(getFileName(driveTarget) || fallbackInfo.name || 'Deliverables').trim() || 'Deliverables',
      };
    }
  }

  return fallbackInfo;
};

const createIdleWktValidationState = () => ({
  status: 'idle',
  checkedWkt: '',
  valid: false,
  willWork: false,
  message: '',
  location: null,
  crs: null,
  inputType: 'unknown',
});

const buildWktStatusText = (hasWkt, validation) => {
  if (!hasWkt) return 'Not provided';
  const status = validation?.status || 'idle';
  const valid = Boolean(validation?.valid);
  const willWork = Boolean(validation?.willWork);
  if (status === 'checking') return 'Checking...';
  if (status === 'error') return 'Validation error';
  if (valid) return willWork ? 'Valid and compatible' : 'Valid but not compatible';
  return 'Invalid';
};

const buildPersistedWktSummary = (wktText, validation) => {
  const trimmedWkt = String(wktText ?? '').trim();
  const hasWkt = Boolean(trimmedWkt);
  const locationLabel = validation?.location?.label || validation?.crs?.name || 'Unknown';
  return {
    crsDefined: hasWkt ? 'Yes' : 'No',
    validationStatus: buildWktStatusText(hasWkt, validation),
    detectedLocation: hasWkt ? locationLabel : 'Unknown',
    country: hasWkt ? (validation?.location?.country || 'Unknown') : 'Unknown',
    stateRegion: hasWkt ? (validation?.location?.state || 'Unknown') : 'Unknown',
    valid: Boolean(validation?.valid),
    willWork: Boolean(validation?.willWork),
    status: validation?.status || (hasWkt ? 'done' : 'idle'),
    message: validation?.message || '',
    location: validation?.location || null,
    crs: validation?.crs || null,
    inputType: validation?.inputType || 'unknown',
    checkedAt: new Date().toISOString(),
  };
};

const parseSetupWktValidation = (setup) => {
  const wkt = normalizeWktInput(String(setup?.wkt ?? '').trim());
  if (!wkt) return createIdleWktValidationState();

  const summary = (setup?.wktSummary && typeof setup.wktSummary === 'object')
    ? setup.wktSummary
    : ((setup?.wktValidation && typeof setup.wktValidation === 'object') ? setup.wktValidation : null);

  if (!summary) {
    // Legacy setup without cached metadata: let runtime validator execute once.
    return createIdleWktValidationState();
  }

  const statusCandidate = String(summary.status || '').trim().toLowerCase();
  const statusText = String(summary.validationStatus || '').toLowerCase();
  let status = 'done';
  if (statusCandidate === 'checking' || statusCandidate === 'error' || statusCandidate === 'idle' || statusCandidate === 'done') {
    status = statusCandidate;
  } else {
    if (statusText.includes('checking')) status = 'checking';
    else if (statusText.includes('error')) status = 'error';
    else status = 'done';
  }

  const inferredValid = statusText.includes('valid');
  const inferredWillWork = statusText.includes('valid and compatible');
  const valid = typeof summary.valid === 'boolean' ? summary.valid : inferredValid;
  const willWork = typeof summary.willWork === 'boolean' ? summary.willWork : inferredWillWork;

  const location = (summary.location && typeof summary.location === 'object')
    ? { ...summary.location }
    : {};
  if (!location.label && summary.detectedLocation) location.label = summary.detectedLocation;
  if (!location.country && summary.country) location.country = summary.country;
  if (!location.state && summary.stateRegion) location.state = summary.stateRegion;

  return {
    status,
    checkedWkt: wkt,
    valid: Boolean(valid),
    willWork: Boolean(willWork),
    message: summary.message || summary.validationStatus || '',
    location: Object.keys(location).length ? location : null,
    crs: summary.crs || null,
    inputType: summary.inputType || 'unknown',
  };
};

const deriveTaskMetadataFromProjectFiles = (files = []) => {
  if (!Array.isArray(files) || files.length === 0) {
    return { hasSourceFolder: false, taskOptions: null, sourceGoogleDriveIds: [], sourceFolderIds: [], dbFoldersByCode: {} };
  }

  const folders = files.filter(isFolderItem);
  const sourceFolders = folders.filter((item) => normalizeFolderName(item?.name) === 'SOURCE');
  const sourceFolderIds = sourceFolders
    .map((item) => String(getFileId(item)))
    .filter((id) => id && id !== 'undefined' && id !== 'null');

  if (sourceFolderIds.length === 0) {
    return { hasSourceFolder: false, taskOptions: null, sourceGoogleDriveIds: [], sourceFolderIds: [], dbFoldersByCode: {} };
  }

  const sourceIdSet = new Set(sourceFolderIds);
  const detectedTasks = new Set();
  const dbFoldersByCode = {};
  const sourceGoogleDriveIds = Array.from(
    new Set(
      sourceFolders
        .map((folder) => String(getGoogleDriveId(folder) ?? '').trim())
        .filter(Boolean)
    )
  );

  for (const folder of folders) {
    const parentId = getParentId(folder);
    if (parentId == null || !sourceIdSet.has(String(parentId))) {
      continue;
    }
    const taskOption = mapFolderCodeToTaskOption(folder?.name);
    if (taskOption) {
      detectedTasks.add(taskOption);
      const normalizedCode = normalizeFolderName(folder?.name);
      if (!dbFoldersByCode[normalizedCode]) {
        dbFoldersByCode[normalizedCode] = {
          id: getFileId(folder),
          name: folder?.name || normalizedCode,
          googleDriveId: getGoogleDriveId(folder),
          source: 'db',
        };
      }
    }
  }

  return {
    hasSourceFolder: true,
    taskOptions: TASK_OPTIONS.filter((task) => detectedTasks.has(task)),
    sourceGoogleDriveIds,
    sourceFolderIds,
    dbFoldersByCode,
  };
};

const buildRsaFoldersPayloadFromProject = async (projectId) => {
  if (!projectId) {
    return { model_folder_id: [], path_folders: [], output_folder_id: '', output_folder_tree_id: '' };
  }

  const isProductionDomain = window.location.hostname.includes('123bimlive.com');
  const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
  const cacheBust = `_ts=${Date.now()}`;
  const filesUrl = baseUrl
    ? `${API_BASE_URL}/api/projects/${projectId}/files?${cacheBust}`
    : `/api/projects/${projectId}/files?${cacheBust}`;
  const filesResponse = await axios.get(filesUrl);
  const files = filesResponse.data?.files || [];
  const { sourceGoogleDriveIds, sourceFolderIds, dbFoldersByCode } = deriveTaskMetadataFromProjectFiles(files);
  const foldersByCode = {};
  const modelFolderIdsSet = new Set();
  const deliverablesInfo = await resolveDeliverablesFolderForProject(projectId, files);
  const outputFolderId = String(deliverablesInfo?.driveId ?? '').trim();
  const outputFolderTreeId = String(deliverablesInfo?.treeId ?? '').trim();

  // DB fallback: detect MODEL folder directly under SOURCE.
  const sourceIdSet = new Set((sourceFolderIds || []).map((id) => String(id)));
  const dbModelFolders = (files || []).filter((item) => {
    if (!isFolderItem(item)) {
      return false;
    }
    const parentId = getParentId(item);
    if (parentId == null || !sourceIdSet.has(String(parentId))) {
      return false;
    }
    return normalizeFolderName(getFileName(item)) === 'MODEL';
  });
  for (const modelFolder of dbModelFolders) {
    const modelFolderId = String(getGoogleDriveId(modelFolder) ?? getFileId(modelFolder) ?? '').trim();
    if (modelFolderId) {
      modelFolderIdsSet.add(modelFolderId);
    }
  }

  for (const [folderCode, folderInfo] of Object.entries(dbFoldersByCode || {})) {
    const suffix = RSA_FOLDER_CODE_TO_SUFFIX[folderCode];
    if (!suffix) {
      continue;
    }
    const folderGoogleDriveId = String(folderInfo?.googleDriveId ?? '').trim();
    if (!folderGoogleDriveId) {
      continue;
    }
    foldersByCode[folderCode] = {
      config_type: suffix,
      path_folder_id: folderGoogleDriveId,
    };
  }

  if (Array.isArray(sourceGoogleDriveIds) && sourceGoogleDriveIds.length > 0) {
    const sourceChildrenResponses = await Promise.all(
      sourceGoogleDriveIds.map((sourceFolderId) => {
        const childrenUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${projectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
          : `/api/projects/${projectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
        return axios.get(childrenUrl).catch(() => ({ data: { items: [] } }));
      })
    );

    for (const sourceChildrenResponse of sourceChildrenResponses) {
      const items = sourceChildrenResponse?.data?.items || [];
      for (const item of items) {
        if (!isFolderItem(item)) {
          continue;
        }
        const folderCode = normalizeFolderName(item?.name);
        if (folderCode === 'MODEL') {
          const modelFolderId = String(getGoogleDriveId(item) ?? getFileId(item) ?? '').trim();
          if (modelFolderId) {
            modelFolderIdsSet.add(modelFolderId);
          }
          continue;
        }
        const suffix = RSA_FOLDER_CODE_TO_SUFFIX[folderCode];
        if (!suffix || foldersByCode[folderCode]) {
          continue;
        }

        const folderGoogleDriveId = String(getGoogleDriveId(item) ?? getFileId(item) ?? '').trim();
        if (!folderGoogleDriveId) {
          continue;
        }

        foldersByCode[folderCode] = {
          config_type: suffix,
          path_folder_id: folderGoogleDriveId,
        };
      }
    }
  }

  const pathFolders = Object.values(foldersByCode).sort((a, b) => a.config_type.localeCompare(b.config_type));
  const modelFolderIds = Array.from(modelFolderIdsSet);
  return {
    model_folder_id: modelFolderIds,
    path_folders: pathFolders,
    output_folder_id: outputFolderId,
    output_folder_tree_id: outputFolderTreeId,
  };
};

const TASK_IMAGE_CONFIG = {
  'Flat Spot Analysis (FSA)': { code: 'FSA', image: FSAImage },
  'Intersection Sight Distance (ISD)': { code: 'ISD', image: ISDImage },
  'Passing Sight Distance (PSD)': { code: 'PSD', image: PSDImage },
  'Stopping Sight Distance (SSD)': { code: 'SSD', image: SSDImage },
  'Horizontal Curve Analysis (HCA)': { code: 'HCA', image: HCAImage },
  'Guardrails Warrant (GRW)': { code: 'GRW', image: GWAImage },
  'Sunglare Analysis (GLR)': { code: 'GLR', image: SGAImage },
  'Clear Zone Analysis (CLZ)': { code: 'CLZ', image: CLZImage },
  'Existing Guardrails (EXG)': { code: 'EXG', image: EXGImage },
  'Embankment Analysis (EMB)': { code: 'EMB', image: EMBImage },
};

const TASK_INFO_IMAGE_CONFIG = {
  'Flat Spot Analysis (FSA)': FSAInfoImage,
  'Intersection Sight Distance (ISD)': ISDInfoImage,
  'Passing Sight Distance (PSD)': PSDInfoImage,
  'Stopping Sight Distance (SSD)': SSDInfoImage,
  'Horizontal Curve Analysis (HCA)': HCAInfoImage,
  'Guardrails Warrant (GRW)': GWAInfoImage,
  'Sunglare Analysis (GLR)': SGAInfoImage,
  'Clear Zone Analysis (CLZ)': CLZInfoImage,
  'Existing Guardrails (EXG)': EXGInfoImage,
  'Embankment Analysis (EMB)': EMBInfoImage,
};

// --- Recursive tree node component ---
function FolderTreeNode({ folder, onSelect, selectedFolderId, parentPath = '', storageConnection, useSystemDrive }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleExpand = async (e) => {
    // Prevent the click from triggering selection and form submission
    e.preventDefault();
    e.stopPropagation();
    if (!expanded) {
      setLoading(true);
      try {
        let url;
        if (useSystemDrive) {
          // Use system Google Drive
          url = ENVIRONMENT === 'local' 
            ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folder.id}`
            : `/api/google-drive/folders?parent_id=${folder.id}`;
        } else if (storageConnection) {
          // Use user's storage
          url = ENVIRONMENT === 'local' 
            ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`
            : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`;
        }
        
        if (url) {
          console.log("🔧 RSAAnalysis - Making folder request to:", url);
          const response = await axios.get(url);
          setChildren(response.data.folders || []);
        } else {
          console.warn("No URL available for fetching folders");
          setChildren([]);
        }
      } catch (err) {
        console.error("❌ RSAAnalysis - Error fetching children for folder", folder.id, err);
        setChildren([]);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(!expanded);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
    onSelect({ ...folder, fullPath });
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpand(e);
  };

  const isSelected = folder.id === selectedFolderId;

  return (
    <li className={`rsa-tree-node ${isSelected ? 'rsa-tree-node-selected' : ''}`}>
      <div 
        className="rsa-tree-node-content"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <button 
          type="button"
          className={`rsa-tree-toggle ${expanded ? 'rsa-tree-toggle-expanded' : ''}`}
          onClick={toggleExpand}
          disabled={loading}
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="rsa-tree-icon">
          {expanded ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        
        <span className="rsa-tree-label">{folder.name}</span>
        
        {loading && (
          <span className="rsa-tree-loading">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rsa-tree-loading-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
              </circle>
            </svg>
          </span>
        )}
      </div>
      
        {expanded && children.length > 0 && (
          <ul className="rsa-tree-children">
            {children.map(child => (
              <FolderTreeNode
                key={child.id}
                folder={child}
                onSelect={onSelect}
                selectedFolderId={selectedFolderId}
                parentPath={parentPath ? `${parentPath}/${folder.name}` : folder.name}
                storageConnection={storageConnection}
                useSystemDrive={useSystemDrive}
              />
            ))}
          </ul>
        )}
    </li>
  );
}

// --- Storage Connection Modal ---
function StorageConnectionModal({ onClose, provider, onConnectionSuccess }) {
  const [s3Form, setS3Form] = useState({
    connection_name: '',
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    region: 'us-east-1'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isOAuthProvider = ['google_drive', 'azure', 'gcp'].includes(provider);
  const isS3Provider = provider === 's3';

  const providerNames = {
    'google_drive': 'Google Drive',
    'azure': 'Microsoft Azure',
    'gcp': 'Google Cloud Storage',
    's3': 'AWS S3'
  };

  // Helper function to get token from sessionStorage (handles JSON stringified tokens)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log('[OK] [getAuthToken] Got token from Amplify Auth');
          return idToken;
        }
      } catch (amplifyError) {
        console.log('â„¹ï¸ [getAuthToken] Amplify Auth not available, trying other methods:', amplifyError.message);
      }
      
      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem('bimLiveAuthToken'); // Primary key used by this app
      
      if (!token) {
        token = sessionStorage.getItem('token');
      }
      if (!token) {
        token = sessionStorage.getItem('cognito_token');
      }
      if (!token) {
        token = sessionStorage.getItem('idToken');
      }
      if (!token) {
        // Check localStorage as fallback
        token = localStorage.getItem('bimLiveAuthToken') || 
                localStorage.getItem('token') || 
                localStorage.getItem('cognito_token') || 
                localStorage.getItem('idToken');
      }
      
      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'cognito_token' || name === 'token') {
            token = value;
            break;
          }
        }
      }
      
      console.log('🔍 [getAuthToken] Raw token from storage:', token ? `${token.substring(0, 20)}...` : 'null');
      
      if (!token) {
        console.warn('[WARN] [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify');
        console.log('🔍 [getAuthToken] Available sessionStorage keys:', Object.keys(sessionStorage));
        return null;
      }
      
      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log('[OK] [getAuthToken] Token is JSON, parsed structure:', typeof parsed, parsed ? Object.keys(parsed) : 'null');
        
        if (parsed && typeof parsed === 'string') {
          // Token is a JSON stringified string
          console.log('[OK] [getAuthToken] Using parsed string token');
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log('[OK] [getAuthToken] Using parsed.token');
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log('[OK] [getAuthToken] Using parsed.idToken');
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log('[OK] [getAuthToken] Using parsed.accessToken');
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn('[WARN] [getAuthToken] Parsed token is object but no known property found:', parsed);
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log('[OK] [getAuthToken] Token is not JSON, using as-is (raw JWT)');
        return token;
      }
    } catch (e) {
      console.error('❌ [getAuthToken] Error getting auth token:', e);
      return null;
    }
  };

  const handleOAuthConnect = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError('You must be logged in to connect storage. Please log in and try again.');
        setLoading(false);
        return;
      }
      
      console.log('🔐 Starting OAuth flow for provider:', provider);
      
      // Store current page URL to redirect back after OAuth
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      // Add popup=true parameter to indicate this is a popup request
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/oauth/${provider === 'google_drive' ? 'google' : provider}/authorize?return_url=${returnUrl}&popup=true`
        : `/api/storage/oauth/${provider === 'google_drive' ? 'google' : provider}/authorize?return_url=${returnUrl}&popup=true`;
      
      console.log('📡 Calling backend OAuth authorize endpoint:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[OK] Backend response:', response.data);
      
      if (response.data && response.data.authorization_url) {
        const authUrl = response.data.authorization_url;
        console.log('🔗 Opening OAuth popup:', authUrl);
        
        // Open OAuth flow in a popup window
        const popupWidth = 600;
        const popupHeight = 700;
        const left = (window.screen.width - popupWidth) / 2;
        const top = (window.screen.height - popupHeight) / 2;
        
        const popup = window.open(
          authUrl,
          'oauth-popup',
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
        
        if (!popup) {
          setError('Popup blocked. Please allow popups for this site and try again.');
          setLoading(false);
          return;
        }
        
        // Listen for messages from the popup
        const handleMessage = (event) => {
          // Security: Only accept messages from our own origin
          if (event.origin !== window.location.origin) {
            console.warn('[WARN] Ignoring message from different origin:', event.origin);
            return;
          }
          
          if (event.data && event.data.type === 'oauth-callback') {
            console.log('[OK] OAuth callback received:', event.data);
            
            if (event.data.success) {
              // Close popup
              popup.close();
              
              // Remove event listener
              window.removeEventListener('message', handleMessage);
              
              // Reload storage connections
              if (onConnectionSuccess) {
                onConnectionSuccess();
              }
              
              // Close modal and show success
              setLoading(false);
              onClose();
              
              // Show success message
              const providerNames = {
                'google_drive': 'Google Drive',
                'azure': 'Microsoft Azure',
                'gcp': 'Google Cloud Storage',
                's3': 'AWS S3'
              };
              const providerName = providerNames[provider] || provider;
              alert(`${providerName} connected successfully!`);
            } else {
              // Error occurred
              popup.close();
              window.removeEventListener('message', handleMessage);
              setError(event.data.error || 'Failed to connect storage. Please try again.');
              setLoading(false);
            }
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setLoading(false);
            // Don't show error if user just closed the popup
          }
        }, 1000);
        
      } else {
        console.error('❌ No authorization_url in response:', response.data);
        setError('Failed to get authorization URL from server. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error initiating OAuth:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      let errorMessage = 'Failed to connect. Please try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in and try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please check if Google OAuth is Setupd.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleS3Connect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError('You must be logged in to connect storage. Please log in and try again.');
        setLoading(false);
        return;
      }
      
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/connect/s3`
        : '/api/storage/connect/s3';
      
      const response = await axios.post(url, s3Form, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 201) {
        // Success - reload connections and close modal
        if (onConnectionSuccess) {
          onConnectionSuccess();
        }
        onClose();
      }
    } catch (err) {
      console.error('Error connecting S3:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to connect S3. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="TreeModalOverlay">
      <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">Connect {providerNames[provider] || provider}</h2>
        </div>
        
        <div className="modal-content">
          {isOAuthProvider && (
            <div className="modal-section">
              <p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
                You'll be redirected to {providerNames[provider]} to grant permission for our application to access your storage.
                This is a secure OAuth 2.0 flow - you'll just need to click "Allow" on the authorization page.
              </p>
              <div style={{ 
                padding: '12px', 
                background: 'rgba(59, 131, 246, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(59, 131, 246, 0.3)',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#93c5fd', fontSize: '13px', margin: 0 }}>
                  <strong>What happens next:</strong>
                </p>
                <ul style={{ color: '#cbd5e1', fontSize: '13px', margin: '8px 0 0 20px', padding: 0 }}>
                  <li>You'll be redirected to {providerNames[provider]}'s login page</li>
                  <li>After logging in, you'll see a consent screen</li>
                  <li>Click "Allow" to grant access</li>
                  <li>You'll be redirected back automatically</li>
                </ul>
              </div>
            </div>
          )}

          {isS3Provider && (
            <form id="s3-connection-form" onSubmit={handleS3Connect}>
              <div className="modal-section">
                <label className="modal-label">Connection Name (optional)</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="My S3 Bucket"
                  value={s3Form.connection_name}
                  onChange={(e) => setS3Form({...s3Form, connection_name: e.target.value})}
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Access Key ID *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  value={s3Form.access_key_id}
                  onChange={(e) => setS3Form({...s3Form, access_key_id: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Secret Access Key *</label>
                <input
                  type="password"
                  className="modal-input"
                  placeholder="Enter your secret key"
                  value={s3Form.secret_access_key}
                  onChange={(e) => setS3Form({...s3Form, secret_access_key: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Bucket Name *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="my-bucket-name"
                  value={s3Form.bucket_name}
                  onChange={(e) => setS3Form({...s3Form, bucket_name: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Region *</label>
                <select
                  className="modal-select"
                  value={s3Form.region}
                  onChange={(e) => setS3Form({...s3Form, region: e.target.value})}
                  required
                >
                  <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                  <option value="us-east-2">US East (Ohio) - us-east-2</option>
                  <option value="us-west-1">US West (N. California) - us-west-1</option>
                  <option value="us-west-2">US West (Oregon) - us-west-2</option>
                  <option value="eu-west-1">Europe (Ireland) - eu-west-1</option>
                  <option value="eu-central-1">Europe (Frankfurt) - eu-central-1</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                  <option value="ap-southeast-2">Asia Pacific (Sydney) - ap-southeast-2</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</option>
                </select>
              </div>

              {error && (
                <div style={{
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#fca5a5',
                  fontSize: '13px',
                  marginBottom: '20px'
                }}>
                  {error}
                </div>
              )}
            </form>
          )}

          {error && isOAuthProvider && (
            <div style={{
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#fca5a5',
              fontSize: '13px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button 
            type="button"
            className="modal-btn modal-btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {isOAuthProvider && (
            <button 
              type="button"
              className="modal-btn modal-btn-primary" 
              onClick={handleOAuthConnect}
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect & Authorize'}
            </button>
          )}
          {isS3Provider && (
            <button 
              type="submit"
              form="s3-connection-form"
              className="modal-btn modal-btn-primary" 
              disabled={loading || !s3Form.access_key_id || !s3Form.secret_access_key || !s3Form.bucket_name}
            >
              {loading ? 'Connecting...' : 'Connect S3'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Modal popup with the folder tree and shared drive selector ---
// projectId: when set, backend filters shared drives via GoogleDriveForProjects secret (* = all, else drive name)
function FolderTreePopup({ onClose, onFolderSelect, onDriveSelect, storageConnection, useSystemDrive, projectId }) {
  // View: 'tree' = folder tree, 'selected_list' = list of selected folders
  const [view, setView] = useState('tree');
  // Accumulated selected folders (order of selection)
  const [selectedFoldersInModal, setSelectedFoldersInModal] = useState([]);
  // State for shared drives
  const [sharedDrives, setSharedDrives] = useState([]);
  const [noMatchingDrive, setNoMatchingDrive] = useState(false);
  const [selectedDrive, setSelectedDrive] = useState('');
  // Root folder id used to fetch folders (either shared drive id or manually entered)
  const [rootFolderId, setRootFolderId] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch available shared drives from backend (optionally scoped by project via GoogleDriveForProjects)
  const fetchSharedDrives = async () => {
    try {
      const base = ENVIRONMENT === 'local'
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : `/api/google-drive/shared-drives`;
      const url = projectId
        ? `${base}?project_id=${encodeURIComponent(projectId)}`
        : base;
      const response = await axios.get(url);
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      const noMatch = Boolean(response.data?.no_matching_drive);
      setNoMatchingDrive(noMatch);
      setSharedDrives(drivesArray);
      // Cache only when not project-filtered to avoid mixing lists
      if (!projectId) {
        sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
      }
      // If filtered to one drive, auto-select it
      if (drivesArray.length === 1 && drivesArray[0]?.id) {
        setSelectedDrive(drivesArray[0].id);
      }
      if (noMatch || (projectId && drivesArray.length === 0)) {
        setSelectedDrive('');
      }
    } catch (err) {
      console.error("Error fetching shared drives", err);
      setSharedDrives([]);
      setNoMatchingDrive(false);
    }
  };

  // Fetch subfolders for the current rootFolderId
  const fetchFolders = async (folderId) => {
    setLoading(true);
    try {
      if (useSystemDrive) {
        // Use system Google Drive
        console.log("🔧 RSAAnalysis - Fetching folders from system Google Drive, parent_id:", folderId);
        const url = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folderId}`
          : `/api/google-drive/folders?parent_id=${folderId}`;
        const response = await axios.get(url);
        console.log("🔧 RSAAnalysis - Folders response:", response.data);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(`[OK] RSAAnalysis - Set ${folders.length} folders from system Google Drive`);
      } else if (storageConnection) {
        // Use user's storage connection
        console.log("🔧 RSAAnalysis - Fetching folders from user storage:", storageConnection.connection_name);
        const url = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`
          : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`;
        const response = await axios.get(url);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(`[OK] RSAAnalysis - Set ${folders.length} folders from ${storageConnection.connection_name}`);
      }
    } catch (err) {
      console.error("❌ RSAAnalysis - Error fetching folders for", folderId, err);
      setFolders([]);
    }
    setLoading(false);
  };

  // On mount (and when projectId changes), load shared drives - skip cache when project-scoped
  useEffect(() => {
    if (useSystemDrive) {
      if (!projectId) {
        const cachedDrives = sessionStorage.getItem('sharedDrives');
        if (cachedDrives) {
          try {
            const parsedDrives = JSON.parse(cachedDrives);
            setSharedDrives(Array.isArray(parsedDrives) ? parsedDrives : []);
            setNoMatchingDrive(false);
            console.log("[OK] FolderTreePopup - Shared drives loaded from cache:", parsedDrives.length);
          } catch (e) {
            console.error("Error parsing cached shared drives:", e);
          }
        }
      } else {
        setSharedDrives([]);
        setNoMatchingDrive(false);
        setSelectedDrive('');
      }
      fetchSharedDrives();
    } else if (storageConnection) {
      // Use user's storage - fetch root folders
      fetchFolders('root');
    }
  }, [useSystemDrive, storageConnection, projectId]);

  // When a shared drive is selected, update the rootFolderId and fetch its folders
  useEffect(() => {
    if (useSystemDrive && selectedDrive) {
      setRootFolderId(selectedDrive);
      fetchFolders(selectedDrive);
      // Get the drive name and pass it to parent
      const drive = sharedDrives.find(d => d.id === selectedDrive);
      if (drive && onDriveSelect) {
        onDriveSelect(drive.name);
      }
    }
  }, [selectedDrive, sharedDrives, onDriveSelect, useSystemDrive]);

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
  };

  // Add current tree selection to list and switch to selected-list view
  const handleAddSelected = () => {
    if (selectedFolder) {
      setSelectedFoldersInModal(prev => [...prev, selectedFolder]);
      setSelectedFolder(null);
      setView('selected_list');
    } else {
      alert("Please select a folder from the tree.");
    }
  };

  const removeFolderAt = (index) => {
    setSelectedFoldersInModal(prev => prev.filter((_, i) => i !== index));
  };

  // Confirm selection: close modal and pass array to parent
  const handleConfirmSelection = () => {
    if (selectedFoldersInModal.length > 0) {
      onFolderSelect(selectedFoldersInModal);
      onClose();
    } else {
      alert("Please select at least one folder.");
    }
  };

  const showTreeAgain = () => {
    setView('tree');
  };

  const clearAllFoldersInModal = () => {
    setSelectedFoldersInModal([]);
    setView('tree');
  };

  const handleRefresh = async () => {
    setLoading(true);
    if (useSystemDrive) {
      // Refresh shared drives for system Google Drive
      await fetchSharedDrives();
      // If a drive is selected, refresh its folders
      if (rootFolderId) {
        await fetchFolders(rootFolderId);
      }
    } else if (storageConnection) {
      // Refresh folders for user's storage
      await fetchFolders(rootFolderId || 'root');
    }
    setLoading(false);
  };

  return (
          <div className="TreeModalOverlay">
        <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">
            {view === 'tree' ? 'Select LAS Folder' : 'Selected Folders'}
          </h2>
        </div>
        
        <div className="modal-content">
          {view === 'selected_list' ? (
            /* --- Selected folders list view --- */
            <>
              <div className="rsa-selected-folders-list">
                {selectedFoldersInModal.map((folder, index) => {
                  const path = folder.fullPath || folder.name;
                  const displayPath = path.length > 60 ? path.substring(0, 30) + '...' + path.substring(path.length - 25) : path;
                  return (
                    <div key={`${folder.id}-${index}`} className="rsa-selected-folder-item">
                      <span className="rsa-selected-folder-path" title={path}>{displayPath}</span>
                      <button
                        type="button"
                        className="rsa-selected-folder-remove"
                        onClick={() => removeFolderAt(index)}
                        aria-label="Remove folder"
                        title="Remove"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="rsa-selected-folders-actions">
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-select-another"
                  onClick={showTreeAgain}
                >
                  <svg className="rsa-btn-select-another-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Select another folder
                </button>
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-clear-folders"
                  onClick={clearAllFoldersInModal}
                >
                  Clear
                </button>
              </div>
            </>
          ) : (
            /* --- Tree view (unchanged layout) --- */
            <>
          {/* Show storage provider info if using user storage */}
          {!useSystemDrive && storageConnection && (
            <div className="modal-section">
              <div className="modal-section-header">
                <label className="modal-label">Storage Provider:</label>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="rsa-modal-refresh-btn"
                  title="Refresh Storage"
                >
                  <RefreshIcon className="rsa-modal-refresh-icon" />
                </button>
              </div>
              <div style={{ padding: '8px 12px', background: 'rgba(59, 131, 246, 0.1)', borderRadius: '6px', marginBottom: '15px' }}>
                <span style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '500' }}>
                  {storageConnection.connection_name} ({storageConnection.provider_type})
                </span>
              </div>
            </div>
          )}

          {/* Shared Drive Selector - Only show for system Google Drive */}
          {useSystemDrive && (
            <div className="modal-section">
              <div className="modal-section-header">
                <label className="modal-label">Select a Shared Drive:</label>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="rsa-modal-refresh-btn"
                  title="Refresh Google Drive"
                >
                  <RefreshIcon className="rsa-modal-refresh-icon" />
                </button>
              </div>
            <select
              className="modal-select"
              value={selectedDrive}
              onChange={(e) => setSelectedDrive(e.target.value)}
              disabled={noMatchingDrive && sharedDrives.length === 0}
            >
              {noMatchingDrive && sharedDrives.length === 0 ? (
                <option value="" className="modal-option" disabled>
                  No storage drive(s) were associated with this project.
                </option>
              ) : (
                <>
                  <option value="" className="modal-option">-- Select Shared Drive --</option>
                  {Array.isArray(sharedDrives) && sharedDrives.length > 0 ? (
                    sharedDrives.map((drive) => (
                      <option key={drive.id} value={drive.id} className="modal-option">
                        {drive.name}
                      </option>
                    ))
                  ) : (
                    <option value="" className="modal-option" disabled>No shared drives available</option>
                  )}
                </>
              )}
            </select>
          </div>
          )}

          {loading ? (
            <div className="modal-loading">Loading folders...</div>
          ) : (
            Array.isArray(folders) && folders.length > 0 ? (
              <ul className="tree">
                {folders.map(folder => (
                  <FolderTreeNode 
                    key={folder.id} 
                    folder={folder} 
                    onSelect={handleSelect} 
                    selectedFolderId={selectedFolder?.id}
                    parentPath=""
                    storageConnection={storageConnection}
                    useSystemDrive={useSystemDrive}
                  />
                ))}
              </ul>
            ) : useSystemDrive && selectedDrive ? (
              <div className="modal-loading modal-loading-message">
                No folders found in this shared drive. Please select a different drive.
              </div>
            ) : useSystemDrive && !selectedDrive ? (
              <div className="modal-loading modal-loading-message">
                Please select a shared drive to view folders.
              </div>
            ) : !useSystemDrive && storageConnection ? (
              <div className="modal-loading modal-loading-message">
                No folders found. Please select a folder from the tree above.
              </div>
            ) : (
              <div className="modal-loading modal-loading-message">
                Loading folders...
              </div>
            )
          )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="modal-btn modal-btn-secondary" onClick={onClose}>Cancel</button>
          {view === 'tree' ? (
            <button className="modal-btn modal-btn-primary" onClick={handleAddSelected}>Select</button>
          ) : (
            <button className="modal-btn modal-btn-primary" onClick={handleConfirmSelection} disabled={selectedFoldersInModal.length === 0}>Select</button>
          )}
        </div>
      </div>
    </div>
  );
}

/** For each waypoint, find the index in path coordinates that is closest along the routed path. */
function getWaypointCoordinateIndices(pathData, waypoints) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return [];
  const coords = pathData.geometry.coordinates;
  const indices = [];
  for (const wp of waypoints) {
    const lat = wp.lat;
    const lng = wp.lng;
    let bestIdx = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const c = coords[i];
      const clat = Array.isArray(c) ? c[1] : c.lat;
      const clng = Array.isArray(c) ? c[0] : c.lng;
      const d2 = (clat - lat) ** 2 + (clng - lng) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestIdx = i;
      }
    }
    indices.push(bestIdx);
  }
  return indices;
}

/** Slice pathData to the segment between waypoint startIdx and endIdx (inclusive) */
function slicePathDataByWaypointRange(pathData, waypoints, startIdx, endIdx) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return null;
  const coords = pathData.geometry.coordinates;
  const wpIndices = getWaypointCoordinateIndices(pathData, waypoints);
  const i0 = Math.min(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const i1 = Math.max(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const sliced = coords.slice(i0, i1 + 1);
  if (sliced.length < 2) return null;
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: sliced },
    properties: {},
  };
}

// Segment indicator colors (match map waypoint markers when segment is active)
const SEGMENT_COLORS = [
  '#DD4130', /* red */
  '#2563eb', /* blue */
  '#059669', /* emerald */
  '#7c3aed', /* violet */
  '#ea580c', /* orange */
  '#0d9488', /* teal */
  '#dc2626', /* red-6 */
  '#ca8a04', /* yellow/amber */
  '#db2777', /* pink */
  '#0891b2', /* cyan */
];

function parseLinearMetersPerUnitFromWkt(wkt) {
  if (!wkt || typeof wkt !== 'string') return null;
  const re = /UNIT\["[^"]+"\s*,\s*([0-9.+\-eE]+)\s*\]/g;
  let match;
  let last = null;
  while ((match = re.exec(wkt)) !== null) {
    const v = Number(match[1]);
    if (Number.isFinite(v) && v > 0) last = v;
  }
  return last;
}

function parseUnitsTokenFromProj4(proj4String) {
  if (!proj4String || typeof proj4String !== 'string') return '';
  const m = proj4String.match(/\+units=([^\s]+)/i);
  return (m?.[1] || '').toLowerCase();
}

function getProjectedUnitsPerFoot({ wkt, proj4String }) {
  const metersPerUnitFromWkt = parseLinearMetersPerUnitFromWkt(wkt);
  if (Number.isFinite(metersPerUnitFromWkt) && metersPerUnitFromWkt > 0) {
    return 0.3048 / metersPerUnitFromWkt;
  }
  const token = parseUnitsTokenFromProj4(proj4String);
  if (token === 'us-ft' || token === 'ft') return 1;
  if (token === 'm') return 0.3048;
  return 0.3048;
}

function normalizeVector2(x, y) {
  const len = Math.hypot(x, y);
  if (!Number.isFinite(len) || len <= 1e-9) return [0, 0];
  return [x / len, y / len];
}

function offsetPolylineXY(polylineXY, signedOffsetUnits) {
  if (!Array.isArray(polylineXY) || polylineXY.length < 2) return [];
  const pts = polylineXY.map(([x, y]) => [Number(x), Number(y)]);
  if (pts.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) return [];
  const n = pts.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const p = pts[i];
    let tx = 0;
    let ty = 0;
    if (i === 0) {
      tx = pts[1][0] - p[0];
      ty = pts[1][1] - p[1];
    } else if (i === n - 1) {
      tx = p[0] - pts[n - 2][0];
      ty = p[1] - pts[n - 2][1];
    } else {
      const [p0, p1, p2] = [pts[i - 1], p, pts[i + 1]];
      const [aX, aY] = normalizeVector2(p1[0] - p0[0], p1[1] - p0[1]);
      const [bX, bY] = normalizeVector2(p2[0] - p1[0], p2[1] - p1[1]);
      tx = aX + bX;
      ty = aY + bY;
      if (Math.hypot(tx, ty) <= 1e-6) {
        tx = bX || aX;
        ty = bY || aY;
      }
    }
    const [nx, ny] = normalizeVector2(-ty, tx); // left-hand normal
    out.push([p[0] + (signedOffsetUnits * nx), p[1] + (signedOffsetUnits * ny)]);
  }
  return out;
}

function clampNumber(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function projectLatLngToLocalMeters(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const origin = points[0];
  const lat0Rad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.max(0.2, Math.cos(lat0Rad));
  return points.map((p) => ({
    x: (p.lng - origin.lng) * 111320 * cosLat,
    y: (p.lat - origin.lat) * 110540,
  }));
}

function getDistanceMetersXY(a, b) {
  return Math.hypot((b.x - a.x), (b.y - a.y));
}

function computeCurvatureScores(projectedPoints) {
  const n = Array.isArray(projectedPoints) ? projectedPoints.length : 0;
  const scores = new Array(n).fill(0);
  if (n < 3) return scores;

  for (let i = 1; i < n - 1; i += 1) {
    const a = projectedPoints[i - 1];
    const b = projectedPoints[i];
    const c = projectedPoints[i + 1];
    const v1x = b.x - a.x;
    const v1y = b.y - a.y;
    const v2x = c.x - b.x;
    const v2y = c.y - b.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 <= 1e-3 || len2 <= 1e-3) continue;
    const dot = clampNumber(((v1x * v2x) + (v1y * v2y)) / (len1 * len2), -1, 1);
    const angle = Math.acos(dot); // 0..PI
    const normalized = clampNumber(angle / Math.PI, 0, 1);
    scores[i] = normalized;
  }

  const smoothed = new Array(n).fill(0);
  for (let i = 0; i < n; i += 1) {
    const prev = scores[Math.max(0, i - 1)] ?? 0;
    const curr = scores[i] ?? 0;
    const next = scores[Math.min(n - 1, i + 1)] ?? 0;
    smoothed[i] = clampNumber((prev * 0.25) + (curr * 0.5) + (next * 0.25), 0, 1);
  }
  return smoothed;
}

function sampleLaneWaypoints(latLngPoints) {
  const points = (Array.isArray(latLngPoints) ? latLngPoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length < 2) return points;
  const projected = projectLatLngToLocalMeters(points);
  if (projected.length < 2) return points;

  const minSpacingMeters = 10 * 0.3048;   // 10 ft
  const maxSpacingMeters = 200 * 0.3048;  // 200 ft
  const curvature = computeCurvatureScores(projected);
  const cumulativeMeters = new Array(projected.length).fill(0);
  for (let i = 1; i < projected.length; i += 1) {
    cumulativeMeters[i] = cumulativeMeters[i - 1] + getDistanceMetersXY(projected[i - 1], projected[i]);
  }

  const chosenIndices = [0];
  let lastChosen = 0;
  let localCurvePeak = 0;

  for (let i = 1; i < projected.length - 1; i += 1) {
    localCurvePeak = Math.max(localCurvePeak, curvature[i] ?? 0);
    const distanceFromLast = cumulativeMeters[i] - cumulativeMeters[lastChosen];

    const curveFactor = clampNumber(localCurvePeak * 1.35, 0, 1);
    const desiredSpacingMeters = maxSpacingMeters - ((maxSpacingMeters - minSpacingMeters) * curveFactor);

    if (distanceFromLast >= desiredSpacingMeters) {
      chosenIndices.push(i);
      lastChosen = i;
      localCurvePeak = 0;
    }
  }

  if (chosenIndices[chosenIndices.length - 1] !== projected.length - 1) {
    chosenIndices.push(projected.length - 1);
  }

  const deduped = [];
  chosenIndices.forEach((idx) => {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== idx) deduped.push(idx);
  });

  let sampled = deduped.map((idx) => ({ lat: points[idx].lat, lng: points[idx].lng }));
  const maxLaneWaypoints = 48;
  if (sampled.length > maxLaneWaypoints) {
    const n = sampled.length;
    const capped = [];
    for (let k = 0; k < maxLaneWaypoints; k += 1) {
      const i = Math.round((k / (maxLaneWaypoints - 1)) * (n - 1));
      capped.push(sampled[i]);
    }
    sampled = capped.filter((p, j, arr) => j === 0 || p.lat !== arr[j - 1].lat || p.lng !== arr[j - 1].lng);
  }
  return sampled;
}

const EMPTY_WAYPOINTS = [];

// --- PathsList connected component (reads from RsaPathsContext) ---
function RsaPathsListPanelConnected({
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  manualLaneCreationActive,
  activeManualLanePointCount,
  wktAvailable,
  onGoToPathData, // (pathData: GeoJSON.Feature) => void
  maps2000ByPath = {},
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
  onDeletePath: onDeletePathProp,
}) {
  const ctx = React.useContext(RsaPathsContext);
  if (!ctx) return null;
  const { paths, lanes, activePathId, activeLaneId, laneEditMode, speedEditMode, selectedLaneWaypointIndices, actions, SEGMENT_COLORS: colors } = ctx;

  return (
    <PathsListPanel
      paths={paths}
      lanes={lanes}
      activePathId={activePathId}
      activeLaneId={activeLaneId}
      laneEditMode={laneEditMode}
      speedEditMode={speedEditMode}
      selectedLaneWaypointIndices={selectedLaneWaypointIndices}
      segmentColors={colors ?? SEGMENT_COLORS}
      wktAvailable={wktAvailable}
      manualLaneCreationActive={manualLaneCreationActive}
      manualLanePointCount={activeManualLanePointCount}
      onCreateOffsetLane={onCreateOffsetLane}
      onStartManualLane={onStartManualLane}
      onStopManualLane={onStopManualLane}
      onSelectPath={actions.setActivePath}
      onDeletePath={onDeletePathProp || actions.deletePath}
      onTogglePathVisible={(id) => {
        const p = paths.find((x) => x.id === id);
        if (p) actions.updatePath(id, { visible: !p.visible });
      }}
      onGoToPath={(id) => {
        const p = paths.find((x) => x.id === id);
        if (p?.pathData?.geometry?.coordinates?.length) onGoToPathData?.(p.pathData);
      }}
      onRenamePath={(id, label) => {
        const unique = makeUniqueLabel(label, paths, id);
        actions.updatePath(id, { label: unique });
      }}
      onSelectLane={actions.setActiveLane}
      onDeleteLane={actions.deleteLane}
      onToggleLaneVisible={(id) => {
        const l = lanes.find((x) => x.id === id);
        if (l) actions.updateLane(id, { visible: !l.visible });
      }}
      onRenameLane={(id, label) => actions.updateLane(id, { label })}
      onChangeLanePosition={(id, lanePosition) => actions.updateLane(id, { lanePosition })}
      onChangeLaneDirection={(id, direction) => actions.updateLane(id, { direction })}
      onAddPath={actions.addPath}
      onAddLane={actions.setActivePath}
      onEditLanePoints={(laneId) => {
        actions.setActiveLane(laneId);
        actions.enterLaneEditMode();
      }}
      onExitEditLanePoints={actions.exitLaneEditMode}
      onClearLaneSelection={() => actions.setSelectedLaneWaypoints([])}
      onEnterSpeedMode={(laneId) => {
        actions.enterSpeedEditMode();
        // Auto-create the first breakpoint at mile 0 if none exist
        const lane = lanes.find((l) => l.id === laneId);
        if (!lane?.speedSegments?.length) {
          actions.addSpeedBreakpoint(laneId, 0, 45);
        }
      }}
      onExitSpeedMode={actions.exitSpeedEditMode}
      onUpdateSpeedAtBreakpoint={(laneId, idx, mph) => actions.updateSpeedAtBreakpoint(laneId, idx, mph)}
      onRemoveSpeedBreakpoint={(laneId, idx) => actions.removeSpeedBreakpoint(laneId, idx)}
      maps2000ByPath={maps2000ByPath}
      onCreateOrthoMaps={onCreateOrthoMaps}
      creatingOrthoMapsByPath={creatingOrthoMapsByPath}
      orthoProgressByPath={orthoProgressByPath}
    />
  );
}

function RsaMapsPanelContentConnected({ onGoToPathData }) {
  const ctx = React.useContext(RsaPathsContext);
  if (!ctx) return null;

  const {
    paths,
    lanes,
    activePathId,
    activeLaneId,
    activeEntityType,
    actions,
    SEGMENT_COLORS: colors,
  } = ctx;

  const rows = [
    ...paths.map((path, idx) => ({
      ...path,
      entityType: 'path',
      label: path.label || `Route${idx + 1}`,
    })),
    ...lanes.map((lane, idx) => ({
      ...lane,
      entityType: 'lane',
      label: lane.label || `Lane ${idx + 1}`,
    })),
  ];

  const activeRowIndex = rows.findIndex((row) =>
    activeEntityType === 'lane'
      ? row.entityType === 'lane' && row.id === activeLaneId
      : row.entityType === 'path' && row.id === activePathId
  );

  return (
    <MapsPanelContent
      segments={rows}
      activeSegmentIndex={activeRowIndex >= 0 ? activeRowIndex : 0}
      segmentColors={colors ?? SEGMENT_COLORS}
      segmentVisibility={rows.map((row) => row.visible !== false)}
      onToggleSegmentVisibility={(idx) => {
        const row = rows[idx];
        if (!row) return;
        if (row.entityType === 'lane') {
          actions.updateLane(row.id, { visible: !row.visible });
          return;
        }
        actions.updatePath(row.id, { visible: !row.visible });
      }}
      onSelectSegment={(idx) => {
        const row = rows[idx];
        if (!row) return;
        if (row.entityType === 'lane') {
          actions.setActiveLane(row.id);
          return;
        }
        actions.setActivePath(row.id);
      }}
      onGoToSegment={(idx) => {
        const row = rows[idx];
        if (row?.pathData?.geometry?.coordinates?.length) {
          onGoToPathData?.(row.pathData);
        }
      }}
    />
  );
}

// --- Paths Panel Component ---
function PathsPanel({
  mode = 'path-creator',
  lasBoundingBoxes = [],
  orthoTiles = [],
  lasVisible = true,
  onToggleLasVisibility,
  onClearLas,
  orthoVisible = true,
  onToggleOrthoVisibility,
  onClearOrtho,
  onGoToLasBounds,
  onSelectedLasExtentIndexChange,
  loadingLasBounds = false,
  loadingOrthoTiles = false,
  orthoImagesLoading = false,
  orthoLoadedCount = 0,
  orthoTotal = 0,
  maps2000ByPath = {},
  loadingMaps2000Check = false,
  onRescanMaps2000,
  onLoadLasToMap,
  hasFoldersSelected = false,
  onLoadOrthoMaps,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  manualLaneCreationActive = false,
  activeManualLanePointCount = 0,
  overlayOpacity = 0.9,
  onOverlayOpacityChange,
  intersectionDrawingMode = null,
  onIntersectionDrawingModeChange,
  intersectionOverlays = [],
  onIntersectionOverlayUpdate,
  onClearIntersectionOverlays,
  onIntersectionOverlayRemove,
  curvePoints = [],
  measurePoints = [],
  onResetMeasureDistance,
  curvedLineEditPointCount = 5,
  onCurvedLineEditPointCountChange,
  onSavePath,
  onGoToSegment,
  isSavingPath = false,
  wktAvailable = false,
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
  onDeletePath,
}) {
  const ctx = React.useContext(RsaPathsContext);
  const [showClearPathModal, setShowClearPathModal] = useState(false);
  const [selectedLasExtentIndex, setSelectedLasExtentIndex] = useState(0);
  const [selectedOrthoExtentIndex, setSelectedOrthoExtentIndex] = useState(0);

  const activePath = ctx?.paths?.find((path) => path.id === ctx.activePathId) ?? ctx?.paths?.[0] ?? null;
  const activeLane = ctx?.lanes?.find((lane) => lane.id === ctx.activeLaneId) ?? null;
  const activeEntityType = ctx?.activeEntityType === 'lane' && activeLane ? 'lane' : 'path';
  const activeEntity = activeEntityType === 'lane' ? activeLane : activePath;
  const activeEntityLabel = activeEntity?.label || activeEntityType;
  const canSave = Boolean(
    ctx?.paths?.some((path) => Array.isArray(path?.pathData?.geometry?.coordinates) && path.pathData.geometry.coordinates.length >= 2)
  );
  const canClear = Boolean(activeEntity?.waypoints?.length);
  const modeTitle = mode === 'path-creator' ? 'Path Creator' : mode === 'intersection' ? 'Intersections' : 'Map Layers';

  const handleClearPathClick = () => {
    if (!activeEntity?.id) return;
    setShowClearPathModal(true);
  };

  const handleClearPathConfirm = () => {
    if (activeEntityType === 'lane' && activeLane?.id) {
      ctx?.actions?.updateLane(activeLane.id, { pathData: null, waypoints: [] });
    } else if (activePath?.id) {
      ctx?.actions?.updatePath(activePath.id, { pathData: null, waypoints: [] });
    }
    setShowClearPathModal(false);
  };

  useEffect(() => {
    setSelectedLasExtentIndex(0);
  }, [lasBoundingBoxes]);

  useEffect(() => {
    const maxIndex = Math.max(0, lasBoundingBoxes.length - 1);
    const clampedIndex = Math.max(0, Math.min(selectedLasExtentIndex, maxIndex));
    if (clampedIndex !== selectedLasExtentIndex) {
      setSelectedLasExtentIndex(clampedIndex);
      return;
    }
    onSelectedLasExtentIndexChange?.(clampedIndex);
  }, [selectedLasExtentIndex, lasBoundingBoxes.length, onSelectedLasExtentIndexChange]);

  return (
    <div className="tabs w35 paths-panel">
      <div className="summary-header">
        <h3>{modeTitle}</h3>
      </div>
      <div className={`summary-content paths-panel-content paths-panel-content-${mode}`}>
        <div className="paths-panel-shared-map-controls">
          <div className="paths-panel-overlay-opacity paths-panel-overlay-opacity--compact">
            <label className="paths-panel-overlay-opacity-label" htmlFor="paths-panel-overlay-opacity-slider">
              Ortho opacity
            </label>
            <input
              id="paths-panel-overlay-opacity-slider"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={overlayOpacity}
              onChange={(e) => onOverlayOpacityChange?.(Number(e.target.value))}
              className="paths-panel-overlay-opacity-slider"
              title="Ortho map PNG opacity on the map"
              style={{ '--paths-panel-overlay-fill-pct': `${Math.round(Math.min(1, Math.max(0, overlayOpacity)) * 100)}%` }}
            />
            <span className="paths-panel-overlay-opacity-value">{Math.round(overlayOpacity * 100)}%</span>
          </div>

        </div>

        {mode === 'path-creator' && (
          <RsaPathsListPanelConnected
            onCreateOffsetLane={onCreateOffsetLane}
            onStartManualLane={onStartManualLane}
            onStopManualLane={onStopManualLane}
            manualLaneCreationActive={manualLaneCreationActive}
            activeManualLanePointCount={activeManualLanePointCount}
            wktAvailable={wktAvailable}
            onGoToPathData={(pathData) => onGoToSegment?.(pathData)}
            maps2000ByPath={maps2000ByPath}
            onCreateOrthoMaps={onCreateOrthoMaps}
            creatingOrthoMapsByPath={creatingOrthoMapsByPath}
            orthoProgressByPath={orthoProgressByPath}
            onDeletePath={onDeletePath}
          />
        )}

        {mode === 'intersection' && (
          <IntersectionPanelContent
            drawingMode={intersectionDrawingMode}
            onDrawingModeChange={onIntersectionDrawingModeChange}
            drawnOverlays={intersectionOverlays}
            onOverlayUpdate={onIntersectionOverlayUpdate}
            onClearOverlays={onClearIntersectionOverlays}
            onOverlayRemove={onIntersectionOverlayRemove}
            curvePoints={curvePoints}
            measurePoints={measurePoints}
            onResetMeasureDistance={onResetMeasureDistance}
            curvedLineEditPointCount={curvedLineEditPointCount}
            onCurvedLineEditPointCountChange={onCurvedLineEditPointCountChange}
          />
        )}

        {mode === 'maps' && (
          <RsaMapsPanelContentConnected onGoToPathData={onGoToSegment} />
        )}

        <div className={`paths-panel-las-bounds-bottom ${lasBoundingBoxes.length === 0 && !loadingLasBounds ? 'paths-panel-las-bounds-bottom--action' : ''} ${loadingLasBounds ? 'paths-panel-las-bounds-bottom--loading' : ''}`}>
          <span className="paths-panel-las-bounds-bottom__label">Optional</span>
          {lasBoundingBoxes.length > 0 ? (
            <>
              <select
                className="paths-panel-las-bounds-bottom__select"
                value={Math.min(selectedLasExtentIndex, lasBoundingBoxes.length - 1)}
                onChange={(e) => setSelectedLasExtentIndex(Number(e.target.value))}
                aria-label="Select LAS extent"
              >
                {lasBoundingBoxes.map((b, i) => (
                  <option key={`${b?.name || 'las'}-${i}`} value={i}>
                    {b?.name || `LAS ${i + 1}`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="paths-panel-las-bounds-bottom__icon-btn"
                onClick={() => {
                  const idx = Math.min(selectedLasExtentIndex, lasBoundingBoxes.length - 1);
                  const b = lasBoundingBoxes[idx];
                  if (b) onGoToLasBounds?.(b);
                }}
                title="Fit map to selected extent"
              >
                <i className="fas fa-map-marker-alt" aria-hidden />
              </button>
              <button
                type="button"
                className="paths-panel-las-bounds-bottom__icon-btn"
                onClick={() => onToggleLasVisibility?.()}
                title={lasVisible ? 'Hide LAS bounding boxes' : 'Show LAS bounding boxes'}
              >
                <i className={lasVisible ? 'fas fa-eye' : 'fas fa-eye-slash'} aria-hidden />
              </button>
              <button
                type="button"
                className="paths-panel-las-bounds-bottom__icon-btn paths-panel-las-bounds-bottom__icon-btn--delete"
                onClick={() => onClearLas?.()}
                title="Remove LAS bounding boxes from map"
              >
                <i className="fas fa-trash-alt" aria-hidden />
              </button>
            </>
          ) : loadingLasBounds ? (
            <div className="paths-panel-las-bounds-bottom__progress-wrap" aria-busy="true">
              <div className="paths-panel-las-bounds-bottom__progress-track">
                <div className="paths-panel-las-bounds-bottom__progress-fill" />
              </div>
              <span className="paths-panel-las-bounds-bottom__progress-label">Loading LAS bounds</span>
            </div>
          ) : (
            <button
              type="button"
              className="paths-panel-las-bounds-bottom__action-btn"
              onClick={() => onLoadLasToMap?.()}
              disabled={!hasFoldersSelected}
              title={hasFoldersSelected ? "Load LAS/LAZ file bounding boxes from selected folder(s) and show them on the map" : "Select a LAS folder first"}
            >
              <i className="fas fa-cube" aria-hidden />
              <span>Load LAS Bounds</span>
            </button>
          )}
        </div>
      </div>

      {mode === 'path-creator' && (
        <div className="paths-panel-footer">
          <button
            type="button"
            className="paths-panel-save-btn"
            disabled={!canSave || isSavingPath}
            onClick={() => onSavePath?.()}
            title={canSave ? (isSavingPath ? 'Saving' : 'Save path (creates LasSeparationPath.json in selected LAS folder(s))') : 'Add at least 2 waypoints to save'}
          >
            <i className="fas fa-save" aria-hidden />
            {isSavingPath ? 'Saving' : 'Save path'}
          </button>
          <button
            type="button"
            className="paths-panel-clear-footer-btn"
            disabled={!canClear}
            onClick={handleClearPathClick}
            title={canClear ? `Clear active ${activeEntityType}` : 'No path to clear'}
          >
            <i className="fas fa-times" aria-hidden />
            Clear {activeEntityType}
          </button>
        </div>
      )}

      {showClearPathModal && (
        <div className="TreeModalOverlay paths-clear-path-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="paths-clear-path-modal-title">
          <div className="TreeModalContainer paths-clear-path-modal">
            <div className="modal-header">
              <h2 className="modal-title" id="paths-clear-path-modal-title">
                <i className="fa-solid fa-triangle-exclamation paths-clear-path-modal-icon" aria-hidden />
                Clear {activeEntityType}
              </h2>
            </div>
            <div className="modal-content">
              <p className="clear-popup-message paths-clear-path-modal-message">
                {`Are you sure you want to clear ${activeEntityLabel}? Its waypoints will be removed.`}
              </p>
            </div>
            <div className="modal-actions">
              <button type="button" className="modal-btn modal-btn-secondary" onClick={() => setShowClearPathModal(false)}>
                Cancel
              </button>
              <button type="button" className="modal-btn modal-btn-primary" onClick={handleClearPathConfirm}>
                Clear {activeEntityType}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// --- Dashboard Projects Panel Component ---
function DashboardProjectsPanel({
  runs,
  selectedRun,
  onSelectRun,
  onRequestDeleteRun,
  onCancelDeleteRun,
  onConfirmDeleteRun,
  confirmingRunId,
  deletingRunId,
  runProgressMap = {},
}) {
  const [hasScroll, setHasScroll] = useState(false);
  const projectsListRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      if (projectsListRef.current) {
        const hasOverflow = projectsListRef.current.scrollHeight > projectsListRef.current.clientHeight;
        setHasScroll(hasOverflow);
      }
    };

    checkScroll();
    // Check on resize and when projects change
    window.addEventListener('resize', checkScroll);
    const timeoutId = setTimeout(checkScroll, 100); // Small delay to ensure DOM is updated

    return () => {
      window.removeEventListener('resize', checkScroll);
      clearTimeout(timeoutId);
    };
  }, [runs]);

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'processing':
      case 'running': return 'status-running';
      case 'failed': return 'status-failed';
      case 'pending': return 'status-pending';
      default: return 'status-pending';
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="tabs w35 dashboard-projects-panel">
      <div className="summary-header">
        <h3>Analysis Runs</h3>
        <span className="project-count">
          {runs.length} runs
        </span>
      </div>
      <div className="summary-content dashboard-projects-content">
        <div className={`projects-list ${hasScroll ? 'has-scroll' : ''}`} ref={projectsListRef}>
          {runs.map((run) => {
            const isSelected = selectedRun && selectedRun.run_id === run.run_id;
            const isDeleting = deletingRunId && String(deletingRunId) === String(run.run_id);
            const isConfirming = confirmingRunId && String(confirmingRunId) === String(run.run_id);
            const { progressPct, stepsCount } = getRunListProgressStats(run, runProgressMap[run.run_id]);
            const progress = progressPct;
            const runStatus = run.status === 'processing' ? 'running' : (run.status || 'pending');
            const updatedAt = run.last_updated || run.created_at || new Date();
            const runDisplayId = run.run_display_id || run.run_id;
            const runProjectLabel = `Project ${run.project_id ?? 'N/A'}`;
            const runRunLabel = `Run ${runDisplayId}`;
            const activeStepLabel = getDashboardRunActiveStepDisplay(run, runProgressMap[run.run_id]);

            return (
              <div
                key={run.run_id}
                role="button"
                tabIndex={0}
                className={`dashboard-tile-bg dashboard-run-item ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (isDeleting || isConfirming) return;
                  onSelectRun && onSelectRun(run);
                }}
                onKeyDown={(e) => {
                  if (isDeleting || isConfirming) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectRun && onSelectRun(run);
                  }
                }}
              >
                <div className="dashboard-project-item-header-row">
                  <div className="analysis-dashboard-project-header">
                    <div className="dashboard-run-item-project-row">
                      <div className="dashboard-run-item-title-with-step">
                        <h4 className="dashboard-run-item-project" title={runProjectLabel}>
                          {runRunLabel}
                        </h4>
                  <span className={`project-status ${getStatusClass(runStatus)}`}>{run._isQueuing ? 'Queuing...' : runStatus}</span>

                      </div>
                      {onRequestDeleteRun && (
                        <button
                          type="button"
                          className="dashboard-project-item-delete"
                          title={isDeleting ? 'Removing run' : 'Remove run'}
                          aria-label={isDeleting ? 'Removing run' : 'Remove run'}
                          disabled={isDeleting || isConfirming}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestDeleteRun(run);
                          }}
                        >
                          <i className={`fa-solid ${isDeleting ? 'fa-spinner fa-spin' : 'fa-trash-can'}`} aria-hidden />
                        </button>
                      )}
                    </div>
                    {activeStepLabel && (
                      <span className="step-info" title={activeStepLabel}>
                        {activeStepLabel}
                      </span>
                    )}
                  </div>
                </div>
                <div className="project-stats">
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Progress</span>
                    <span className="analysis-dashboard-stat-value">{progress}%</span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Project</span>
                    <span className="analysis-dashboard-stat-value">{run.project_id ?? 'N/A'}</span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Services</span>
                    <span className="analysis-dashboard-stat-value running">{stepsCount}</span>
                  </div>
                </div>
                {isConfirming && (
                  <div
                    className="dashboard-run-delete-overlay"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Confirm run removal"
                  >
                    <div className="dashboard-run-delete-overlay-content">
                      <p className="dashboard-run-delete-overlay-message">
                        Are you sure you want to remove this item?
                      </p>
                      <p className="dashboard-run-delete-overlay-submessage">
                        This is an irreverbsible action
                      </p>
                      <div className="dashboard-run-delete-overlay-actions">
                        <button
                          type="button"
                          className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelDeleteRun && onCancelDeleteRun();
                          }}
                          disabled={isDeleting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfirmDeleteRun && onConfirmDeleteRun(run);
                          }}
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Projected CRS example WKT (NAD83 Pennsylvania South, Lambert Conformal Conic). */
const WKT_EXAMPLE_PROJECTED = `PROJCS["PA83-S",
  GEOGCS["LL83",
    DATUM["NAD83",
      SPHEROID["GRS1980",6378137,298.25722210]
    ],
    PRIMEM["Greenwich",0],
    UNIT["Degree",0.017453292519943295]
  ],
  PROJECTION["Lambert_Conformal_Conic_2SP"],
  PARAMETER["false_easting",600000],
  PARAMETER["false_northing",0],
  PARAMETER["central_meridian",-77.75],
  PARAMETER["latitude_of_origin",39.33333333333333],
  PARAMETER["standard_parallel_1",40.96666666666667],
  PARAMETER["standard_parallel_2",39.93333333333333],
  UNIT["Meter",1]
]`;

/** Geographic CRS example WKT (NAD83 unprojected, latitude/longitude). */
const WKT_EXAMPLE_GEOGRAPHIC = `GEOGCS["GCS_NAD83",
  DATUM["NAD83",
    SPHEROID["GRS1980",6378137,298.25722210]
  ],
  PRIMEM["Greenwich",0],
  UNIT["Degree",0.017453292519943295]
]`;

/** Shared WKT format / requirements dialog (Setup panel info + summary error hints). */
function WktRequirementsHelpModal({ open, onClose }) {
  const [exampleTab, setExampleTab] = useState('projected');
  if (!open) return null;
  return (
    <div
      className="TreeModalOverlay setup-wkt-requirements-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="setup-wkt-requirements-title"
      onClick={onClose}
    >
      <div
        className="TreeModalContainer setup-wkt-requirements-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header setup-wkt-requirements-modal-header">
          <h2 className="modal-title setup-wkt-requirements-modal-title" id="setup-wkt-requirements-title">
            <i className="fa-solid fa-circle-info setup-wkt-requirements-modal-title-icon" aria-hidden />
            Coordinate System Format (WKT)
          </h2>
        </div>
        <div className="modal-content setup-wkt-requirements-modal-body">
          <p className="setup-wkt-requirements-intro">
            <strong>WKT (Well-Known Text)</strong> is the standard text format for describing a coordinate
            reference system (CRS). It defines the datum, projection, units, and other parameters needed
            to correctly interpret spatial data. Any valid WKT is accepted — NAD83, WGS84, state plane,
            UTM, custom datums, and more.
          </p>
          <p className="setup-wkt-requirements-subhead">How to obtain your WKT</p>
          <ul className="setup-wkt-requirements-obtain-list">
            <li>
              <strong>.prj file</strong> — open the <code>.prj</code> sidecar file that accompanies your
              shapefile or LAS/LAZ data. Its entire contents are valid WKT; paste them as-is.
            </li>
            <li>
              <strong>EPSG.io</strong> — search your CRS name or EPSG code, then copy the{' '}
              <em>WKT1</em> export.
            </li>
            <li>
              <strong>QGIS</strong> — Layer Properties → Information → copy the WKT shown under
              "Coordinate Reference System".
            </li>
            <li>
              <strong>ArcGIS / ArcMap</strong> — Data Frame Properties → Coordinate System →
              export as WKT.
            </li>
          </ul>
          <p className="setup-wkt-requirements-subhead">Examples</p>
          <div className="setup-wkt-requirements-example-wrap">
            <div
              className="setup-wkt-requirements-example-tablist"
              role="tablist"
              aria-label="WKT example by CRS type"
            >
              <button
                type="button"
                role="tab"
                id="setup-wkt-tab-projected"
                aria-selected={exampleTab === 'projected'}
                aria-controls="setup-wkt-example-panel"
                className={`setup-wkt-requirements-example-tab${exampleTab === 'projected' ? ' setup-wkt-requirements-example-tab--active' : ''}`}
                onClick={() => setExampleTab('projected')}
              >
                Projected CRS (PROJCS)
              </button>
              <button
                type="button"
                role="tab"
                id="setup-wkt-tab-geographic"
                aria-selected={exampleTab === 'geographic'}
                aria-controls="setup-wkt-example-panel"
                className={`setup-wkt-requirements-example-tab${exampleTab === 'geographic' ? ' setup-wkt-requirements-example-tab--active' : ''}`}
                onClick={() => setExampleTab('geographic')}
              >
                Geographic CRS (GEOGCS)
              </button>
            </div>
            <pre
              id="setup-wkt-example-panel"
              role="tabpanel"
              aria-labelledby={exampleTab === 'projected' ? 'setup-wkt-tab-projected' : 'setup-wkt-tab-geographic'}
              className="setup-wkt-requirements-example"
              tabIndex={0}
            >
              {exampleTab === 'projected' ? WKT_EXAMPLE_PROJECTED : WKT_EXAMPLE_GEOGRAPHIC}
            </pre>
          </div>
          <p className="setup-wkt-requirements-tip">
            Paste your WKT exactly as exported from your source — do not edit values manually.
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const formatUploadTimestamp = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return 'Unknown';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// --- Summary Component ---
function RSASummary({
  activeMainTab,
  mapOverlayLocalConfig,
  wktValidation,
  mapOverlaySelectedFolders = [],
  setupFolderAvailable = false,
  lasBoundingBoxes = [],
  orthoTiles = [],
  existingProjectName,
  selectedProjectId,
  rsaGroupsMileages = {},
  rsaProjectsPerGroup = {},
  rsaGroupsIsdManeuvers = {},
  services,
  selectedFolders,
  selectedDriveName,
  roadDetectionCategory,
  horizontalData,
  EmbankmentData,
  selectedStorageProvider,
  onSubmit,
  onClear,
  onSaveSetup,
  savingSetup = false,
  wktInputLocked = false,
  onOpenWktRequirementsHelp,
  isRsaModelProject = false,
  rsaModelRouteInfoRef = null,
  rsaRunMetricsRef = null,
  rsaUsageLedgerState = null,
  rsaUsageLedgerLoading = false,
  routesSourceFiles = [],
  routesXmlFiles = [],
  modelFiles = [],
  modelFolderId = null,
  existingSourceFolderId = '',
  existingSourceFiles = [],
  routesMatrixSelection = {},
  onToggleRouteFile,
  routesMatrixSaving = false,
}) {
  const isUploadTab = activeMainTab === 'upload';
  const isSetupTab = activeMainTab === 'setup';
  const isRoutesTab = activeMainTab === 'routes' || activeMainTab === 'analysis';
  const config = mapOverlayLocalConfig || {};
  const hasWkt = !!(config.wkt || '').trim();
  const validationStatus = wktValidation?.status || 'idle';

  const [isRunInProgress, setIsRunInProgress] = useState(false);
  const [mongoOverwriteWarning, setMongoOverwriteWarning] = useState(null);
  const [showRouteSelectionValidation, setShowRouteSelectionValidation] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasAnySelectedRoute = useMemo(() => {
    if (routesXmlFiles.length === 0) return true;
    return routesXmlFiles.some((routeFile) => {
      const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
      if (!fileKey) return false;
      return services.some((service) => {
        const taskOption = SERVICE_TO_TASK_OPTION[service];
        return taskOption && Boolean(routesMatrixSelection?.[fileKey]?.[taskOption]);
      });
    });
  }, [routesXmlFiles, services, routesMatrixSelection]);

  useEffect(() => {
    if (hasAnySelectedRoute && showRouteSelectionValidation) {
      setShowRouteSelectionValidation(false);
    }
  }, [hasAnySelectedRoute, showRouteSelectionValidation]);

  const handleRunButtonClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRunInProgress || routesMatrixSaving || !onSubmit) {
      return;
    }

    if (!hasAnySelectedRoute) {
      setShowRouteSelectionValidation(true);
      return;
    }

    // Build per-file check list from only the checked routes in the matrix.
    // Each entry is {file_drive_id, analysis_code} — one per (checked file × service) pair.
    const filesToCheck = [];
    for (const row of checkedRouteMatrixRows) {
      const routeFile = routesXmlFiles.find(
        (f) => normalizeDriveFolderId(f.googleDriveId) === row.key
      );
      if (!routeFile?.googleDriveId) continue;
      for (const assignment of row.assignments) {
        if (!assignment.checked) continue;
        const folderCode = SERVICE_TO_RSA_FOLDER_CODE[assignment.service];
        const analysisCode = folderCode ? RSA_FOLDER_CODE_TO_SUFFIX[folderCode] : null;
        if (analysisCode) {
          filesToCheck.push({ file_drive_id: routeFile.googleDriveId, analysis_code: analysisCode });
        }
      }
    }

    if (filesToCheck.length > 0) {
      try {
        const isProductionDomain = window.location.hostname.includes('123bimlive.com');
        const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
        const checkUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/check-mongo-overwrite`
          : `/api/projects/${selectedProjectId}/check-mongo-overwrite`;
        const checkRes = await axios.post(checkUrl, { files: filesToCheck });
        const existing = checkRes?.data?.existing_collections || [];
        if (existing.length > 0) {
          setMongoOverwriteWarning({ collections: existing, pendingEvent: e });
          return;
        }
      } catch (checkErr) {
        // Non-fatal: if check fails, proceed with the run
        console.warn('[mongo_check] pre-run check failed, proceeding:', checkErr);
      }
    }
    // (no else needed — if filesToCheck is empty, no conflicts possible)

    setIsRunInProgress(true);
    try {
      setShowRouteSelectionValidation(false);
      await onSubmit(e);
    } finally {
      if (isMountedRef.current) {
        setIsRunInProgress(false);
      }
    }
  };

  const handleMongoOverwriteConfirm = async () => {
    const pendingEvent = mongoOverwriteWarning?.pendingEvent;
    setMongoOverwriteWarning(null);
    setIsRunInProgress(true);
    try {
      setShowRouteSelectionValidation(false);
      await onSubmit(pendingEvent);
    } finally {
      if (isMountedRef.current) {
        setIsRunInProgress(false);
      }
    }
  };
  const isWktValidated = Boolean(wktValidation?.valid);
  const isWktCompatible = Boolean(wktValidation?.willWork);
  const wktValid = hasWkt && isWktValidated && isWktCompatible;
  const setupSaveEnabled = wktValid;
  const wktStatusText = !hasWkt
    ? 'Not provided'
    : validationStatus === 'checking'
      ? 'Checking...'
      : validationStatus === 'error'
        ? 'Validation error'
        : isWktValidated
          ? (isWktCompatible ? 'Valid and compatible' : 'Valid but not compatible')
          : 'Invalid';
  const wktStatusClass = !hasWkt
    ? ''
    : validationStatus === 'checking'
      ? 'setup-summary-status-pending'
      : (isWktValidated && isWktCompatible ? 'setup-summary-status-ok' : 'setup-summary-status-error');
  const showWillWorkRow = hasWkt && validationStatus !== 'idle' && validationStatus !== 'checking' && !isWktCompatible;
  const wktLocationLabel = wktValidation?.location?.label || wktValidation?.crs?.name || 'Unknown';
  const wktCountryLabel = wktValidation?.location?.country || 'Unknown';
  const wktStateLabel = wktValidation?.location?.state || 'Unknown';
  const serviceMapping = {
    'tiling': 'Tiling',
    'csf': 'CSF',
    'road_detection': 'Road Detection',
    'car_removal': 'Car Removal',
    'file_transfer': 'File Transfer',
    'create_rsa': 'Analysis',
    'psd_classification': 'PSD Classification',
    'horizontal': 'Horizontal Curve Analysis (HCA)',
    'embankment': 'Embankment Analysis (EMB)',
    'automatic_reports':'Automatic Reports',
    // New service value to display name mappings (with abbreviations)
    'stopping_sight_distance': 'Stopping Sight Distance (SSD)',
    'passing_sight_distance': 'Passing Sight Distance (PSD)',
    'flat_spot_analysis': 'Flat Spot Analysis (FSA)',
    'intersection_sight_distance': 'Intersection Sight Distance (ISD)',
    'sunglare_analysis': 'Sunglare Analysis (GLR)',
    'guardrails_warrant': 'Guardrails Warrant (GRW)',
    'existing_guardrails': 'Existing Guardrails (EXG)',
    'clear_zone_analysis': 'Clear Zone Analysis (CLZ)'
  };

  const analysisMapping = {
    'SSD': 'SSD',
    'PSD': 'PSD',
    'FSA': 'FSA',
    'ISD': 'ISD',
    'GLR': 'GLR',
  };

  const storageProviderMapping = {
    'system_google_drive': { name: 'System Drive', icon: GoogleDriveIcon, iconSize: 16 },
    'google_drive': { name: 'Google Drive', icon: GoogleDriveIcon, iconSize: 16 },
    's3': { name: 'AWS S3', icon: AmazonS3Icon, iconSize: 16 },
    'azure': { name: 'Microsoft Azure', icon: AzureIcon, iconSize: 16 },
    'gcp': { name: 'Google Cloud Storage', icon: GoogleCloudIcon, iconSize: 16 }
  };

  const selectedAnalysisCount = Array.isArray(services) ? services.length : 0;
  const selectedRouteCount = routesXmlFiles.filter((routeFile) => {
      const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
      if (!fileKey) return false;
      return services.some((service) => {
        const taskOption = SERVICE_TO_TASK_OPTION[service];
        return taskOption && Boolean(routesMatrixSelection?.[fileKey]?.[taskOption]);
      });
    }).length;
  const routeDxfCount = routesSourceFiles.filter((file) => isDxfFileName(file?.name)).length;
  const latestRoutesUploadTime = (() => {
    const latest = routesSourceFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  })();
  const latestModelUploadTime = (() => {
    const latest = modelFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  })();
  const latestExistingSourceUploadTime = (() => {
    const latest = existingSourceFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  })();
  const summaryTitle = isUploadTab
    ? 'Upload Summary'
    : isSetupTab
      ? 'Setup Summary'
      : isRoutesTab
        ? 'Tasks & Routes Summary'
        : 'Analysis Selection Summary';

  const renderStorageProviderButton = () => {
    if (!selectedStorageProvider) return null;
    
    const provider = storageProviderMapping[selectedStorageProvider];
    if (!provider) return null;

    const IconComponent = provider.icon;
    
    return (
      <button 
        className="toggle-btn-rsa task-button storage-provider-btn storage-provider-btn-summary selected"
        type="button"
        disabled
        style={{ cursor: 'default' }}
      >
        <IconComponent className="storage-provider-icon" style={{ width: `${provider.iconSize}px`, height: `${provider.iconSize}px` }} />
        <span>{provider.name}</span>
      </button>
    );
  };

  const extent = config.extent || {};
  const originXY = config.originXY || {};
  const originLatLng = config.originLatLng || {};

  const [rsaFoldersByCode, setRsaFoldersByCode] = useState({});
  const [projectFilesSnapshot, setProjectFilesSnapshot] = useState([]);
  const [expandedServiceTags, setExpandedServiceTags] = useState({});
  const [xmlFilesByService, setXmlFilesByService] = useState({});
  const [xmlEndMileTotalsByService, setXmlEndMileTotalsByService] = useState({});
  const [xmlIsdManeuverTotalsByService, setXmlIsdManeuverTotalsByService] = useState({});
  const [xmlPerFileMilesByService, setXmlPerFileMilesByService] = useState({}); // { service: { fileName: miles } }
  const [xmlPerFileManeuversByService, setXmlPerFileManeuversByService] = useState({}); // { service: { fileName: count } }
  const [xmlLoadingByService, setXmlLoadingByService] = useState({});
  const [refreshingAllXml, setRefreshingAllXml] = useState(false);
  const [routesPerFileMileage, setRoutesPerFileMileage] = useState({}); // { fileName: miles } for RSA model route files
  const [routesPerFileManeuvers, setRoutesPerFileManeuvers] = useState({}); // { fileName: maneuvers } for RSA model route files
  const [routesPerFileDataLoading, setRoutesPerFileDataLoading] = useState(false);
  const analysisServices = useMemo(
    () => (Array.isArray(services) ? services.filter((service) => Boolean(SERVICE_TO_RSA_FOLDER_CODE[service])) : []),
    [services]
  );
  const checkedRouteMatrixColumns = useMemo(
    () => analysisServices
      .map((service) => ({
        service,
        taskOption: SERVICE_TO_TASK_OPTION[service] || null,
        label: serviceMapping[service] || service,
        shortLabel: (serviceMapping[service] || service).match(/\(([^)]+)\)/)?.[1] || (serviceMapping[service] || service),
      }))
      .filter((column) => column.taskOption),
    [analysisServices]
  );
  const checkedRouteMatrixRows = useMemo(() => {
    if (checkedRouteMatrixColumns.length === 0 || routesXmlFiles.length === 0) {
      return [];
    }

    return routesXmlFiles
      .map((routeFile) => {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey) return null;

        const assignments = checkedRouteMatrixColumns.map((column) => ({
          ...column,
          checked: Boolean(routesMatrixSelection?.[fileKey]?.[column.taskOption]),
        }));

        if (!assignments.some((assignment) => assignment.checked)) {
          return null;
        }

        return {
          key: fileKey,
          name: routeFile.name,
          assignments,
        };
      })
      .filter(Boolean);
  }, [routesXmlFiles, checkedRouteMatrixColumns, routesMatrixSelection]);

  useEffect(() => {
    if (!rsaModelRouteInfoRef) {
      return;
    }
    const next = {};
    for (const service of analysisServices) {
      if (!SERVICE_TO_RSA_FOLDER_CODE[service]) {
        continue;
      }
      const hasLoaded = Object.prototype.hasOwnProperty.call(xmlFilesByService, service);
      next[service] = {
        loading: !!xmlLoadingByService[service],
        count: (xmlFilesByService[service] || []).length,
        hasLoaded,
      };
    }
    rsaModelRouteInfoRef.current = next;
  }, [rsaModelRouteInfoRef, analysisServices, xmlFilesByService, xmlLoadingByService]);

  useEffect(() => {
    setExpandedServiceTags({});
    setXmlFilesByService({});
    setXmlEndMileTotalsByService({});
    setXmlIsdManeuverTotalsByService({});
    setXmlPerFileMilesByService({});
    setXmlPerFileManeuversByService({});
    setXmlLoadingByService({});
    setRsaFoldersByCode({});
    setProjectFilesSnapshot([]);
    setRoutesPerFileMileage({});
    setRoutesPerFileManeuvers({});
    setRoutesPerFileDataLoading(false);
  }, [selectedProjectId]);

  const invalidateXmlSummaryCaches = useCallback(() => {
    setXmlFilesByService({});
    setXmlEndMileTotalsByService({});
    setXmlIsdManeuverTotalsByService({});
    setXmlPerFileMilesByService({});
    setXmlPerFileManeuversByService({});
    setXmlLoadingByService({});
  }, []);

  // After developer resync (ProjectView DB button), MySQL totals update but this panel keeps stale state.
  useEffect(() => {
    const onCachesSynced = (ev) => {
      const pid = ev?.detail?.projectId;
      if (pid == null || String(pid).trim() === '') {
        return;
      }
      if (String(selectedProjectId ?? '') !== String(pid)) {
        return;
      }
      invalidateXmlSummaryCaches();
    };
    window.addEventListener('rsa-mileage-caches-synced', onCachesSynced);
    return () => window.removeEventListener('rsa-mileage-caches-synced', onCachesSynced);
  }, [selectedProjectId, invalidateXmlSummaryCaches]);

  // Same-tab: user resynced on Files then opened Analysis (RSA remounts); event may have fired while unmounted.
  useEffect(() => {
    if (!selectedProjectId) {
      return;
    }
    let ts = null;
    try {
      ts = sessionStorage.getItem(`rsaMileageCachesSynced:${selectedProjectId}`);
    } catch (e) {
      return;
    }
    if (!ts) {
      return;
    }
    try {
      sessionStorage.removeItem(`rsaMileageCachesSynced:${selectedProjectId}`);
    } catch (e) {
      /* ignore */
    }
    invalidateXmlSummaryCaches();
  }, [selectedProjectId, invalidateXmlSummaryCaches]);

  useEffect(() => {
    let cancelled = false;

    const loadRsaFoldersForSummary = async () => {
      if (!selectedProjectId) {
        setRsaFoldersByCode({});
        setProjectFilesSnapshot([]);
        return;
      }

      try {
        const isProductionDomain = window.location.hostname.includes('123bimlive.com');
        const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;
        const filesUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/files?${cacheBust}`
          : `/api/projects/${selectedProjectId}/files?${cacheBust}`;
        const filesResponse = await axios.get(filesUrl);
        const files = filesResponse.data?.files || [];
        const metadata = deriveTaskMetadataFromProjectFiles(files);
        const mergedFoldersByCode = { ...(metadata.dbFoldersByCode || {}) };

        if (Array.isArray(metadata.sourceGoogleDriveIds) && metadata.sourceGoogleDriveIds.length > 0) {
          const sourceChildrenRequests = metadata.sourceGoogleDriveIds.map((sourceFolderId) => {
            const childrenUrl = baseUrl
              ? `${API_BASE_URL}/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
              : `/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
            return axios.get(childrenUrl).catch(() => ({ data: { items: [] } }));
          });
          const sourceChildrenResponses = await Promise.all(sourceChildrenRequests);

          for (const sourceChildrenResponse of sourceChildrenResponses) {
            const items = sourceChildrenResponse?.data?.items || [];
            for (const item of items) {
              if (!isFolderItem(item)) {
                continue;
              }
              const normalizedCode = normalizeFolderName(item?.name);
              const taskOption = mapFolderCodeToTaskOption(item?.name);
              if (!taskOption) {
                continue;
              }
              mergedFoldersByCode[normalizedCode] = {
                id: getFileId(item),
                name: item?.name || normalizedCode,
                googleDriveId: getGoogleDriveId(item),
                source: 'drive',
              };
            }
          }
        }

        if (cancelled) {
          return;
        }
        setProjectFilesSnapshot(Array.isArray(files) ? files : []);
        setRsaFoldersByCode(mergedFoldersByCode);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load RSA Source folders for summary tags:', error);
          setProjectFilesSnapshot([]);
          setRsaFoldersByCode({});
        }
      }
    };

    loadRsaFoldersForSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  // Fetch per-file mileage/maneuvers directly from the Routes folder
  // so the token bar can sum up only the checked files rather than relying on RSA_SSD_* folders.
  useEffect(() => {
    if (!selectedProjectId || routesXmlFiles.length === 0) {
      setRoutesPerFileMileage({});
      setRoutesPerFileManeuvers({});
      return;
    }
    const routesFolderDriveId = routesXmlFiles.find((f) => f.parentDriveId)?.parentDriveId;
    if (!routesFolderDriveId) return;

    let cancelled = false;
    setRoutesPerFileDataLoading(true);
    const isProductionDomain = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
    const perFileUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(routesFolderDriveId)}`
      : `/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(routesFolderDriveId)}`;
    axios.get(perFileUrl).then((res) => {
      if (cancelled) return;
      const files = res?.data?.files || [];
      const milesMap = {};
      const maneuversMap = {};
      files.forEach((f) => {
        if (f.computed_miles != null) milesMap[f.file_name] = f.computed_miles;
        if (f.computed_maneuvers != null) maneuversMap[f.file_name] = f.computed_maneuvers;
      });
      setRoutesPerFileMileage(milesMap);
      setRoutesPerFileManeuvers(maneuversMap);
    }).catch(() => {
      if (!cancelled) {
        setRoutesPerFileMileage({});
        setRoutesPerFileManeuvers({});
      }
    }).finally(() => {
      if (!cancelled) setRoutesPerFileDataLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedProjectId, routesXmlFiles]);

  const loadXmlFilesForService = useCallback(async (service, forceReload = false) => {
    if (!service || !selectedProjectId) {
      return;
    }

    setXmlLoadingByService((prev) => ({ ...prev, [service]: true }));
    try {
      const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
      const folderInfo = folderCode ? rsaFoldersByCode[folderCode] : null;
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const xmlFiles = [];

      if (folderInfo?.googleDriveId) {
        const cacheBust = `_ts=${Date.now()}`;
        const childrenUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(folderInfo.googleDriveId)}&${cacheBust}`
          : `/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(folderInfo.googleDriveId)}&${cacheBust}`;
        const childrenResponse = await axios.get(childrenUrl);
        const items = childrenResponse?.data?.items || [];
        xmlFiles.push(...items
          .filter((item) => !isFolderItem(item) && isXmlFileName(item?.name))
          .map((item) => ({
            name: String(item?.name ?? '').trim(),
            fileId: getFileId(item),
            googleDriveId: getGoogleDriveId(item),
          }))
          .filter((item) => item.name));
      } else if (folderInfo?.id != null) {
        xmlFiles.push(...(Array.isArray(projectFilesSnapshot) ? projectFilesSnapshot : [])
          .filter((item) => String(getParentId(item) ?? '') === String(folderInfo.id) && !isFolderItem(item) && isXmlFileName(item?.name))
          .map((item) => ({
            name: String(item?.name ?? '').trim(),
            fileId: getFileId(item),
            googleDriveId: getGoogleDriveId(item),
          }))
          .filter((item) => item.name));
      }

      xmlFiles.sort((a, b) => a.name.localeCompare(b.name));
      const xmlFileNames = xmlFiles.map((item) => item.name);
      const isIsdService = service === 'intersection_sight_distance';
      let totalEndMileForService = 0;
      let totalManeuversForService = 0;

      if (folderInfo?.googleDriveId) {
        const folderDriveKey = String(folderInfo.googleDriveId).trim();
        const pickTotalFromByFolder = (totalsByFolder, key) => {
          if (!totalsByFolder || !key) {
            return null;
          }
          if (Object.prototype.hasOwnProperty.call(totalsByFolder, key)) {
            return Number(totalsByFolder[key]);
          }
          const lower = key.toLowerCase();
          const matchKey = Object.keys(totalsByFolder).find(
            (k) => String(k).toLowerCase() === lower
          );
          return matchKey != null ? Number(totalsByFolder[matchKey]) : null;
        };
        const cacheBustTotals = `_ts=${Date.now()}`;
        if (isIsdService) {
          try {
            const maneuversUrl = baseUrl
              ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-isd-maneuver-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`
              : `/api/projects/${selectedProjectId}/rsa-isd-maneuver-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`;
            const maneuversResponse = await axios.get(maneuversUrl);
            const totalsByFolder = maneuversResponse?.data?.totals_by_folder || {};
            const cachedTotal = pickTotalFromByFolder(totalsByFolder, folderDriveKey);
            if (cachedTotal != null && Number.isFinite(cachedTotal)) {
              totalManeuversForService = cachedTotal;
            } else {
              const fallbackTotal = Number(maneuversResponse?.data?.grand_total);
              totalManeuversForService = Number.isFinite(fallbackTotal) ? fallbackTotal : 0;
            }
          } catch (maneuverError) {
            console.warn(`Failed to fetch cached ISD maneuver total for folder ${folderDriveKey}:`, maneuverError);
            totalManeuversForService = 0;
          }
        } else {
          try {
            const mileageUrl = baseUrl
              ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`
              : `/api/projects/${selectedProjectId}/rsa-mileage-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`;
            const mileageResponse = await axios.get(mileageUrl);
            try {
              if (typeof window !== 'undefined' && window.localStorage?.getItem('rsaMileageDebug') === '1') {
                // eslint-disable-next-line no-console
                console.info('[rsaMileageDebug]', mileageUrl, mileageResponse?.data);
              }
            } catch (e) {
              /* ignore */
            }
            const totalsByFolder = mileageResponse?.data?.totals_by_folder || {};
            const cachedTotal = pickTotalFromByFolder(totalsByFolder, folderDriveKey);
            if (cachedTotal != null && Number.isFinite(cachedTotal)) {
              totalEndMileForService = cachedTotal;
            } else {
              const fallbackTotal = Number(mileageResponse?.data?.grand_total);
              totalEndMileForService = Number.isFinite(fallbackTotal) ? fallbackTotal : 0;
            }
          } catch (mileageError) {
            console.warn(`Failed to fetch cached mileage total for folder ${folderDriveKey}:`, mileageError);
            totalEndMileForService = 0;
          }
        }
      } else {
        const xmlValues = await Promise.all(xmlFiles.map(async (xmlFile) => {
          try {
            const driveId = String(xmlFile.googleDriveId ?? '').trim();
            let xmlText = '';
            if (driveId) {
              const mediaUrl = baseUrl
                ? `${API_BASE_URL}/api/google-drive/file/${encodeURIComponent(driveId)}/media`
                : `/api/google-drive/file/${encodeURIComponent(driveId)}/media`;
              const mediaResponse = await axios.get(mediaUrl, { responseType: 'text' });
              xmlText = typeof mediaResponse?.data === 'string' ? mediaResponse.data : '';
            } else {
              const fileId = String(xmlFile.fileId ?? '').trim();
              if (/^\d+$/.test(fileId)) {
                const downloadUrl = baseUrl
                  ? `${API_BASE_URL}/api/files/${encodeURIComponent(fileId)}/download?project_id=${encodeURIComponent(selectedProjectId)}`
                  : `/api/files/${encodeURIComponent(fileId)}/download?project_id=${encodeURIComponent(selectedProjectId)}`;
                const downloadResponse = await axios.get(downloadUrl, { responseType: 'text' });
                xmlText = typeof downloadResponse?.data === 'string' ? downloadResponse.data : '';
              }
            }
            if (isIsdService) {
              return countIsdManeuversFromXml(xmlText);
            }
            return sumDirectionalMilesFromXml(xmlText);
          } catch (fileError) {
            console.warn(`Failed to read XML content for ${xmlFile.name}:`, fileError);
            return 0;
          }
        }));
        if (isIsdService) {
          totalManeuversForService = xmlValues.reduce((sum, value) => sum + value, 0);
        } else {
          totalEndMileForService = xmlValues.reduce((sum, value) => sum + value, 0);
        }
      }

      setXmlFilesByService((prev) => ({ ...prev, [service]: xmlFileNames }));
      setXmlEndMileTotalsByService((prev) => ({ ...prev, [service]: totalEndMileForService }));
      setXmlIsdManeuverTotalsByService((prev) => ({ ...prev, [service]: totalManeuversForService }));

      // Fetch per-file breakdown so we can show miles/maneuvers next to each filename.
      // Fire-and-forget — doesn't block the totals from rendering.
      const perFileFolderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
      const perFileFolderInfo = perFileFolderCode ? rsaFoldersByCode[perFileFolderCode] : null;
      if (perFileFolderInfo?.googleDriveId) {
        const isProductionDomain = window.location.hostname.includes('123bimlive.com');
        const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
        const perFileUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(perFileFolderInfo.googleDriveId)}`
          : `/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(perFileFolderInfo.googleDriveId)}`;
        axios.get(perFileUrl).then((res) => {
          const files = res?.data?.files || [];
          const milesMap = {};
          const maneuversMap = {};
          files.forEach((f) => {
            if (f.computed_miles != null) milesMap[f.file_name] = f.computed_miles;
            if (f.computed_maneuvers != null) maneuversMap[f.file_name] = f.computed_maneuvers;
          });
          setXmlPerFileMilesByService((prev) => ({ ...prev, [service]: milesMap }));
          setXmlPerFileManeuversByService((prev) => ({ ...prev, [service]: maneuversMap }));
        }).catch(() => {
          // Non-fatal — file list still shows, just without inline miles
        });
      }
    } catch (error) {
      console.error(`Failed to load XML files for service ${service}:`, error);
      setXmlFilesByService((prev) => ({ ...prev, [service]: [] }));
      setXmlEndMileTotalsByService((prev) => ({ ...prev, [service]: 0 }));
      setXmlIsdManeuverTotalsByService((prev) => ({ ...prev, [service]: 0 }));
    } finally {
      setXmlLoadingByService((prev) => ({ ...prev, [service]: false }));
    }
  }, [projectFilesSnapshot, rsaFoldersByCode, selectedProjectId]);

  const refreshAllServiceXmlFiles = useCallback(async () => {
    if (!selectedProjectId || analysisServices.length === 0) {
      return;
    }
    setRefreshingAllXml(true);
    try {
      await Promise.all(analysisServices.map((service) => loadXmlFilesForService(service, true)));
    } finally {
      setRefreshingAllXml(false);
    }
  }, [analysisServices, loadXmlFilesForService, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      return;
    }
    const servicesWithDetectedFolders = Object.entries(SERVICE_TO_RSA_FOLDER_CODE)
      .filter(([, folderCode]) => Boolean(rsaFoldersByCode[folderCode]))
      .map(([service]) => service);
    if (servicesWithDetectedFolders.length === 0) {
      return;
    }

    const servicesToPrefetch = servicesWithDetectedFolders.filter((service) => (
      !Object.prototype.hasOwnProperty.call(xmlFilesByService, service) &&
      !xmlLoadingByService[service]
    ));
    if (servicesToPrefetch.length === 0) {
      return;
    }

    Promise.all(servicesToPrefetch.map((service) => loadXmlFilesForService(service)))
      .catch((error) => {
        console.warn('XML prefetch for summary tags completed with errors:', error);
      });
  }, [selectedProjectId, rsaFoldersByCode, xmlFilesByService, xmlLoadingByService, loadXmlFilesForService]);

  const toggleServiceTag = useCallback((service) => {
    if (!service) {
      return;
    }
    const isExpanded = !!expandedServiceTags[service];
    if (!isExpanded) {
      const hasLoadedXml = Object.prototype.hasOwnProperty.call(xmlFilesByService, service);
      if (!hasLoadedXml) {
        loadXmlFilesForService(service);
      }
    }
    setExpandedServiceTags((prev) => ({ ...prev, [service]: !isExpanded }));
  }, [expandedServiceTags, loadXmlFilesForService, xmlFilesByService]);

  const hasSelectedProject = selectedProjectId !== null && selectedProjectId !== undefined && String(selectedProjectId) !== '';
  const selectedProjectKey = hasSelectedProject ? String(selectedProjectId) : '';
  const normalizeProjectId = (value) => String(value ?? '').trim();
  const normalizedSelectedProjectId = normalizeProjectId(selectedProjectKey);
  let associatedGroup = null;
  if (normalizedSelectedProjectId && rsaProjectsPerGroup && typeof rsaProjectsPerGroup === 'object') {
    const direct =
      rsaProjectsPerGroup[normalizedSelectedProjectId] ??
      rsaProjectsPerGroup[`project_${normalizedSelectedProjectId}`];
    if (typeof direct === 'string' || typeof direct === 'number') {
      associatedGroup = String(direct);
    }

    if (!associatedGroup) {
      const entries = Object.entries(rsaProjectsPerGroup);
      for (const [groupName, assignment] of entries) {
        if (Array.isArray(assignment)) {
          if (assignment.some((item) => normalizeProjectId(item) === normalizedSelectedProjectId)) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (typeof assignment === 'string') {
          const tokens = assignment.split(/[,\s;|]+/).map((t) => t.trim()).filter(Boolean);
          if (tokens.some((token) => normalizeProjectId(token) === normalizedSelectedProjectId)) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (typeof assignment === 'number') {
          if (normalizeProjectId(assignment) === normalizedSelectedProjectId) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (assignment && typeof assignment === 'object') {
          const projectKeys = Object.keys(assignment);
          if (projectKeys.some((projectKey) => normalizeProjectId(projectKey) === normalizedSelectedProjectId)) {
            associatedGroup = groupName;
            break;
          }
        }
      }
    }
  }

  let rawGroupMileage = null;
  if (associatedGroup && rsaGroupsMileages && typeof rsaGroupsMileages === 'object') {
    rawGroupMileage = rsaGroupsMileages[associatedGroup];
    if (rawGroupMileage === undefined) {
      const matchKey = Object.keys(rsaGroupsMileages).find(
        (k) => String(k).toLowerCase() === String(associatedGroup).toLowerCase()
      );
      if (matchKey) {
        rawGroupMileage = rsaGroupsMileages[matchKey];
      }
    }
    if (rawGroupMileage && typeof rawGroupMileage === 'object') {
      rawGroupMileage =
        rawGroupMileage.mileage ??
        rawGroupMileage.mileages ??
        rawGroupMileage.total_mileage ??
        rawGroupMileage.total ??
        rawGroupMileage.value ??
        null;
    }
  }
  const parsedGroupMileage = Number(rawGroupMileage);
  const totalGroupMileageDisplay = rawGroupMileage === null || rawGroupMileage === undefined || rawGroupMileage === ''
    ? '-'
    : (Number.isFinite(parsedGroupMileage) ? String(Math.round(parsedGroupMileage)) : String(rawGroupMileage));
  const isIsdSelected = Array.isArray(services) && services.includes('intersection_sight_distance');
  let rawGroupIsdManeuvers = null;
  if (associatedGroup && rsaGroupsIsdManeuvers && typeof rsaGroupsIsdManeuvers === 'object') {
    rawGroupIsdManeuvers = rsaGroupsIsdManeuvers[associatedGroup];
    if (rawGroupIsdManeuvers === undefined) {
      const matchKey = Object.keys(rsaGroupsIsdManeuvers).find(
        (k) => String(k).toLowerCase() === String(associatedGroup).toLowerCase()
      );
      if (matchKey) {
        rawGroupIsdManeuvers = rsaGroupsIsdManeuvers[matchKey];
      }
    }
    if (rawGroupIsdManeuvers && typeof rawGroupIsdManeuvers === 'object') {
      rawGroupIsdManeuvers =
        rawGroupIsdManeuvers.maneuvers ??
        rawGroupIsdManeuvers.isd_maneuvers ??
        rawGroupIsdManeuvers.total_maneuvers ??
        rawGroupIsdManeuvers.total ??
        rawGroupIsdManeuvers.value ??
        null;
    }
  }
  const parsedGroupIsdManeuvers = Number(rawGroupIsdManeuvers);
  const totalGroupIsdManeuversDisplay = rawGroupIsdManeuvers === null || rawGroupIsdManeuvers === undefined || rawGroupIsdManeuvers === ''
    ? '-'
    : (Number.isFinite(parsedGroupIsdManeuvers)
      ? String(Math.round(parsedGroupIsdManeuvers))
      : String(rawGroupIsdManeuvers));
  const isdServices = analysisServices.filter((service) => service === 'intersection_sight_distance');
  const mileageServices = analysisServices.filter((service) => service !== 'intersection_sight_distance');

  const taskManeuversTotal = isdServices.reduce((sum, service) => {
    const parsed = Number(xmlIsdManeuverTotalsByService[service]);
    return Number.isFinite(parsed) ? sum + parsed : sum;
  }, 0);
  const taskManeuversLoading = hasSelectedProject && isdServices.some((service) => {
    const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
    const hasFolder = Boolean(folderCode && rsaFoldersByCode[folderCode]);
    if (!hasFolder) {
      return false;
    }
    const hasComputedValue = Object.prototype.hasOwnProperty.call(xmlIsdManeuverTotalsByService, service);
    return !hasComputedValue && !!xmlLoadingByService[service];
  });

  const taskMileageTotalMiles = mileageServices.reduce((sum, service) => {
    const parsed = Number(xmlEndMileTotalsByService[service]);
    return Number.isFinite(parsed) ? sum + parsed : sum;
  }, 0);
  const taskMileageLoading = hasSelectedProject && mileageServices.some((service) => {
    const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
    const hasFolder = Boolean(folderCode && rsaFoldersByCode[folderCode]);
    if (!hasFolder) {
      return false;
    }
    const hasComputedValue = Object.prototype.hasOwnProperty.call(xmlEndMileTotalsByService, service);
    return !hasComputedValue && !!xmlLoadingByService[service];
  });

  const toSafeInt = (value, fallback = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return fallback;
    }
    return Math.trunc(parsed);
  };

  const usageMileageUsed = toSafeInt(rsaUsageLedgerState?.mileage?.used, 0);
  const usageManeuverUsed = toSafeInt(rsaUsageLedgerState?.maneuver?.used, 0);

  // For RSA model projects: sum mileage/maneuvers from checked route files, accumulating
  // per service — the same file assigned to N services contributes N times (mirrors how
  // the pipeline copies the file into each RSA_SSD_* / RSA_HCA_* etc. folder).
  let pendingMileageTokens;
  let pendingManeuverTokens;
  if (routesXmlFiles.length > 0) {
    let routesMileageTotal = 0;
    for (const service of mileageServices) {
      const taskOption = SERVICE_TO_TASK_OPTION[service];
      if (!taskOption) continue;
      for (const routeFile of routesXmlFiles) {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey || !routesMatrixSelection?.[fileKey]?.[taskOption]) continue;
        const miles = Number(routesPerFileMileage[routeFile.name]);
        if (Number.isFinite(miles)) routesMileageTotal += miles;
      }
    }
    let routesManeuversTotal = 0;
    for (const service of isdServices) {
      const taskOption = SERVICE_TO_TASK_OPTION[service];
      if (!taskOption) continue;
      for (const routeFile of routesXmlFiles) {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey || !routesMatrixSelection?.[fileKey]?.[taskOption]) continue;
        const maneuvers = Number(routesPerFileManeuvers[routeFile.name]);
        if (Number.isFinite(maneuvers)) routesManeuversTotal += maneuvers;
      }
    }
    pendingMileageTokens = hasSelectedProject && !routesPerFileDataLoading
      ? Math.max(0, Math.ceil(routesMileageTotal))
      : null;
    pendingManeuverTokens = hasSelectedProject && !routesPerFileDataLoading
      ? Math.max(0, Math.ceil(routesManeuversTotal))
      : null;
  } else {
    pendingMileageTokens = hasSelectedProject && !taskMileageLoading
      ? Math.max(0, Math.ceil(taskMileageTotalMiles))
      : null;
    pendingManeuverTokens = hasSelectedProject && !taskManeuversLoading
      ? Math.max(0, Math.ceil(taskManeuversTotal))
      : null;
  }

  const hasMileageLimit = Number.isFinite(parsedGroupMileage);
  const hasManeuverLimit = Number.isFinite(parsedGroupIsdManeuvers);

  const mileageBaseLimit = hasMileageLimit ? Math.round(parsedGroupMileage) : null;
  const maneuverBaseLimit = hasManeuverLimit ? Math.round(parsedGroupIsdManeuvers) : null;

  const buildTokenBarModel = (baseLimit, usedTokens, pendingTokens, totalTokens = null) => {
    const resolvedBase = Number.isFinite(baseLimit) ? Math.max(0, toSafeInt(baseLimit, 0)) : null;
    const used = Math.max(0, toSafeInt(usedTokens, 0));
    const pending = Number.isFinite(pendingTokens) ? Math.max(0, toSafeInt(pendingTokens, 0)) : 0;
    const consumedTotal = used + pending;
    const requestedTotal = Number.isFinite(totalTokens)
      ? Math.max(0, toSafeInt(totalTokens, consumedTotal))
      : consumedTotal;
    const totalNeeded = Math.max(consumedTotal, requestedTotal);
    const scaleTotal = Math.max(1, resolvedBase ?? 0, totalNeeded);
    const overage = resolvedBase === null ? 0 : Math.max(0, totalNeeded - resolvedBase);
    const pendingSafe = resolvedBase === null
      ? pending
      : Math.max(0, Math.min(pending, Math.max(0, resolvedBase - used)));
    const pendingOverage = Math.max(0, pending - pendingSafe);
    const remaining = resolvedBase === null ? null : resolvedBase - totalNeeded;

    const usedWidthPct = Math.max(0, Math.min(100, (used / scaleTotal) * 100));
    const pendingSafeWidthPct = Math.max(0, Math.min(100, (pendingSafe / scaleTotal) * 100));
    const pendingOverageWidthPct = Math.max(0, Math.min(100, (pendingOverage / scaleTotal) * 100));
    const limitMarkerPct = resolvedBase === null ? null : Math.max(0, Math.min(100, (resolvedBase / scaleTotal) * 100));

    return {
      baseLimit: resolvedBase,
      used,
      pending,
      totalNeeded,
      scaleTotal,
      overage,
      remaining,
      pendingSafe,
      pendingOverage,
      usedWidthPct,
      pendingSafeWidthPct,
      pendingOverageWidthPct,
      limitMarkerPct,
      isOverage: overage > 0,
    };
  };

  const mileageModel = buildTokenBarModel(mileageBaseLimit, usageMileageUsed, pendingMileageTokens);
  const maneuverModel = buildTokenBarModel(maneuverBaseLimit, usageManeuverUsed, pendingManeuverTokens);

  const renderSummaryTokenBar = (model, metricLabel) => {
    const showLimitMarker = model.isOverage && model.limitMarkerPct !== null;
    return (
      <div className="summary-token-widget" aria-label={`${metricLabel} usage summary`}>
        <div className="summary-token-widget-header">
          <span className="summary-label">{metricLabel}:</span>
          <span className={`summary-token-status${model.isOverage ? ' is-overage' : ''}`}>
            {model.isOverage
              ? `Burst +${model.overage}`
              : `Remaining: ${model.remaining === null ? '--' : model.remaining}`}
          </span>
        </div>

        <div className="summary-token-bar-shell">
          <div className={`summary-token-bar-track${model.isOverage ? ' is-overage' : ''}`}>
            <div className="summary-token-bar-segments">
              {model.usedWidthPct > 0 && (
                <span
                  className="summary-token-segment summary-token-segment-used"
                  style={{ width: `${model.usedWidthPct}%` }}
                />
              )}
              {model.pendingSafeWidthPct > 0 && (
                <span
                  className={`summary-token-segment summary-token-segment-pending-safe${model.used > 1 ? ' summary-token-segment-pending-safe-has-used' : ''}`}
                  style={{ width: `${model.pendingSafeWidthPct}%` }}
                />
              )}
              {model.pendingOverageWidthPct > 0 && (
                <span
                  className="summary-token-segment summary-token-segment-pending-overage"
                  style={{ width: `${model.pendingOverageWidthPct}%` }}
                />
              )}
            </div>
            {showLimitMarker && (
              <span
                className="summary-token-limit-marker"
                style={{ left: `${model.limitMarkerPct}%` }}
                aria-hidden
              />
            )}
          </div>
          <div className="summary-token-meta-row">
            <span className="summary-token-meta-item">
              <span className="summary-token-meta-dot summary-token-meta-dot-used" aria-hidden />
              Used {model.used}
            </span>
            <span className="summary-token-meta-item">
              <span className="summary-token-meta-dot summary-token-meta-dot-safe" aria-hidden />
              Required {model.pendingSafe}
            </span>
            {model.pendingOverage > 0 && (
              <span className="summary-token-meta-item summary-token-meta-item-overage">
                <span className="summary-token-meta-dot summary-token-meta-dot-overage" aria-hidden />
                Burst {model.pendingOverage}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!rsaRunMetricsRef) {
      return;
    }

    const mileageByService = {};
    const maneuversByService = {};
    const routeCountByService = {};

    for (const service of analysisServices) {
      const mileage = Number(xmlEndMileTotalsByService[service]);
      const maneuvers = Number(xmlIsdManeuverTotalsByService[service]);
      const routes = (xmlFilesByService[service] || []).length;

      mileageByService[service] = Number.isFinite(mileage) ? mileage : 0;
      maneuversByService[service] = Number.isFinite(maneuvers) ? maneuvers : 0;
      routeCountByService[service] = Number.isFinite(routes) ? routes : 0;
    }

    rsaRunMetricsRef.current = {
      selected_project_id: selectedProjectId ?? null,
      selected_services: [...analysisServices],
      mileage_by_service: mileageByService,
      maneuvers_by_service: maneuversByService,
      route_count_by_service: routeCountByService,
      mileage_total_miles: Number.isFinite(taskMileageTotalMiles) ? taskMileageTotalMiles : 0,
      maneuvers_total: Number.isFinite(taskManeuversTotal) ? taskManeuversTotal : 0,
      route_total: Object.values(routeCountByService).reduce((sum, value) => sum + (Number(value) || 0), 0),
      metrics_generated_at: new Date().toISOString(),
    };
  }, [
    rsaRunMetricsRef,
    selectedProjectId,
    analysisServices,
    xmlEndMileTotalsByService,
    xmlIsdManeuverTotalsByService,
    xmlFilesByService,
    taskMileageTotalMiles,
    taskManeuversTotal,
  ]);

  return (
    <div className={`tabs w35 ${isSetupTab ? 'setup-panel' : ''}`}>
      <div className="summary-header">
        <h3>{summaryTitle}</h3>
      </div>
      <div className="summary-content">
        {isUploadTab ? (
          <>
            <div className="summary-section">
              <h4>Source / Routes</h4>
              <div className="summary-item">
                <span className="summary-label">Route files:</span>
                <span className="summary-value">{routesXmlFiles.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">DXF files:</span>
                <span className="summary-value">{routeDxfCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Latest upload:</span>
                <span className="summary-value">{latestRoutesUploadTime}</span>
              </div>
            </div>

            {isRsaModelProject && (
              <div className="summary-section">
                <h4>Source / Model</h4>
                <div className="summary-item">
                  <span className="summary-label">Folder status:</span>
                  <span className="summary-value">{modelFolderId ? 'Found' : 'Missing'}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Model files:</span>
                  <span className="summary-value">{modelFiles.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Latest upload:</span>
                  <span className="summary-value">{latestModelUploadTime}</span>
                </div>
              </div>
            )}

            {!isRsaModelProject && (
              <div className="summary-section">
                <h4>Source</h4>
                <div className="summary-item">
                  <span className="summary-label">Folder status:</span>
                  <span className="summary-value">{existingSourceFolderId ? 'Found' : 'Missing'}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">LAS/LAZ files:</span>
                  <span className="summary-value">{existingSourceFiles.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Latest upload:</span>
                  <span className="summary-value">{latestExistingSourceUploadTime}</span>
                </div>
              </div>
            )}

            <div className="summary-section">
              <h4>Wizard Status</h4>
              <div className="summary-item">
                <span className="summary-label">Ready for setup:</span>
                <span className="summary-value">{(routesXmlFiles.length > 0 || existingSourceFiles.length > 0) ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </>
        ) : isSetupTab ? (
          /* Setup tab: unique content for map overlay & coordinate system */
          <>
            <div className="summary-section setup-summary-section">
              <div className="setup-summary-section-heading">
                <h4>Coordinate system (WKT)</h4>
                {wktValid && <i className="fas fa-check setup-summary-section-check" aria-hidden title="Required data set" />}
              </div>
              <div className="summary-item">
                <span className="summary-label">CRS defined:</span>
                <span className="summary-value">
                  {(config.wkt || '').trim() ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Validation status:</span>
                {wktStatusClass === 'setup-summary-status-error' ? (
                  <span className="setup-summary-value-with-wkt-hint">
                    <span className={`summary-value ${wktStatusClass}`}>
                      {wktStatusText}
                    </span>
                    <button
                      type="button"
                      className="setup-wkt-requirements-info-btn"
                      onClick={() => onOpenWktRequirementsHelp?.()}
                      aria-label="Open supported coordinate system format (WKT) requirements"
                      title="Supported coordinate system format (WKT)"
                    >
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                    </button>
                  </span>
                ) : (
                  <span className={`summary-value ${wktStatusClass}`}>
                    {wktStatusText}
                  </span>
                )}
              </div>
              {showWillWorkRow && (
                <div className="summary-item">
                  <span className="summary-label">Will work in map:</span>
                  <span className="setup-summary-value-with-wkt-hint">
                    <span className="summary-value setup-summary-status-error">
                      No
                    </span>
                    <button
                      type="button"
                      className="setup-wkt-requirements-info-btn"
                      onClick={() => onOpenWktRequirementsHelp?.()}
                      aria-label="Open supported coordinate system format (WKT) requirements"
                      title="Supported coordinate system format (WKT)"
                    >
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                    </button>
                  </span>
                </div>
              )}
              {hasWkt && (
                <>
                  <div className="summary-item">
                    <span className="summary-label">Detected location:</span>
                    <span className="summary-value" title={wktLocationLabel}>{wktLocationLabel}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Country:</span>
                    <span className="summary-value">{wktCountryLabel}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">State/Region:</span>
                    <span className="summary-value">{wktStateLabel}</span>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          /* Analysis / Settings: existing Analysis Selection Summary */
          <>
            <div className="summary-section">
              <div className="summary-section-header">
                <h4>Selected Services:</h4>
                {analysisServices.length > 0 && (
                  <button
                    type="button"
                    className="summary-services-refresh-btn"
                    onClick={refreshAllServiceXmlFiles}
                    disabled={refreshingAllXml}
                    title="Refresh XML list for selected services"
                    aria-label="Refresh XML list for selected services"
                  >
                    <i className={`fas fa-rotate-right ${refreshingAllXml ? 'fa-spin' : ''}`} aria-hidden />
                  </button>
                )}
              </div>
              {services.length > 0 ? (
                <>
                  <div className="summary-list">
                    {services.map((service) => {
                      const serviceLabel = serviceMapping[service] || service;
                      const isAnalysisService = Boolean(SERVICE_TO_RSA_FOLDER_CODE[service]);
                      const isExpanded = !!expandedServiceTags[service];
                      const isLoadingXml = !!xmlLoadingByService[service];
                      const xmlFileNames = xmlFilesByService[service] || [];
                      const isIsdSvc = service === 'intersection_sight_distance';
                      const perFileMiles = xmlPerFileMilesByService[service] || {};
                      const perFileManeuvers = xmlPerFileManeuversByService[service] || {};
                      const xmlCountLabel = isLoadingXml
                        ? 'Loading...'
                        : `${xmlFileNames.length} routes`;
                      const hasXmlLoadedForService = Object.prototype.hasOwnProperty.call(xmlFilesByService, service);
                      const hasRoutes = routesXmlFiles.length > 0;
                      const showZeroRoutesPayloadWarning = Boolean(
                        hasRoutes && hasXmlLoadedForService && !isLoadingXml && xmlFileNames.length === 0
                      );

                      if (!isAnalysisService) {
                        return (
                          <div key={service} className="summary-tag">
                            {serviceLabel}
                          </div>
                        );
                      }

                      const taskOption = SERVICE_TO_TASK_OPTION[service] || null;
                      const assignedCount = hasRoutes && taskOption
                        ? routesXmlFiles.filter(
                            (f) => Boolean(routesMatrixSelection?.[normalizeDriveFolderId(f.googleDriveId)]?.[taskOption])
                          ).length
                        : null;
                      const metaLabel = hasRoutes && taskOption
                        ? (routesXmlFiles.length > 0
                            ? `${assignedCount} / ${routesXmlFiles.length} routes`
                            : 'No route files')
                        : xmlCountLabel;
                      const zeroWarning = hasRoutes && taskOption
                        ? (routesXmlFiles.length > 0 && assignedCount === 0)
                        : showZeroRoutesPayloadWarning;

                      return (
                        <div key={service} className={`summary-tag summary-tag-expandable ${isExpanded ? 'expanded' : ''}`}>
                          <button
                            type="button"
                            className={`summary-tag-toggle${showRouteSelectionValidation && hasRoutes && taskOption ? ' summary-tag-toggle-route-error' : ''}`}
                            onClick={() => toggleServiceTag(service)}
                            aria-expanded={isExpanded}
                          >
                            <i className={`fas fa-chevron-right summary-tag-chevron ${isExpanded ? 'expanded' : ''}`} aria-hidden />
                            <span className="summary-tag-title">{serviceLabel}</span>
                            <span
                              className={`summary-tag-meta${zeroWarning ? ' summary-tag-meta-zero-routes' : ''}`}
                              title={zeroWarning ? 'No route files selected — this task will not run' : undefined}
                            >
                              {metaLabel}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="summary-tag-files">
                              {hasRoutes && taskOption ? (
                                /* Interactive route file checkboxes */
                                routesXmlFiles.length === 0 ? (
                                  <div className="summary-tag-file summary-tag-file-empty">
                                    No route files in Source / Routes
                                  </div>
                                ) : (
                                  <>
                                    <label className="rsa-task-card-file-row rsa-task-card-file-row-bulk">
                                      <input
                                        type="checkbox"
                                        className="rsa-task-card-file-checkbox"
                                        checked={routesXmlFiles.every((f) => {
                                          const key = normalizeDriveFolderId(f.googleDriveId);
                                          return key && routesMatrixSelection?.[key]?.[taskOption];
                                        })}
                                        onChange={(event) => {
                                          if (typeof onToggleRouteFile !== 'function') return;
                                          const shouldSelectAll = event.target.checked;
                                          routesXmlFiles.forEach((f) => {
                                            const key = normalizeDriveFolderId(f.googleDriveId);
                                            const isSelected = Boolean(key && routesMatrixSelection?.[key]?.[taskOption]);
                                            if (key && isSelected !== shouldSelectAll) {
                                              onToggleRouteFile(key, taskOption);
                                            }
                                          });
                                        }}
                                        disabled={routesMatrixSaving}
                                      />
                                      <span className="rsa-task-card-file-name rsa-task-card-file-name-bulk">
                                        Select all
                                      </span>
                                    </label>
                                    {routesXmlFiles.map((routeFile) => {
                                      const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
                                      const isChecked = Boolean(routesMatrixSelection?.[fileKey]?.[taskOption]);
                                      const fileMiles = isIsdSvc ? null : routesPerFileMileage[routeFile.name];
                                      const fileManeuvers = isIsdSvc ? routesPerFileManeuvers[routeFile.name] : null;
                                      const valueLabel = isIsdSvc
                                        ? (fileManeuvers != null ? `${fileManeuvers} mnv` : null)
                                        : (fileMiles != null ? `${Number(fileMiles).toFixed(2)} mi` : null);
                                      return (
                                        <label
                                          key={`${service}-route-${routeFile.googleDriveId}`}
                                          className={`rsa-task-card-file-row${isChecked ? ' checked' : ''}`}
                                          title={valueLabel ? `${routeFile.name} — ${valueLabel}` : routeFile.name}
                                        >
                                          <input
                                            type="checkbox"
                                            className="rsa-task-card-file-checkbox"
                                            checked={isChecked}
                                            onChange={() => typeof onToggleRouteFile === 'function' && onToggleRouteFile(fileKey, taskOption)}
                                            disabled={routesMatrixSaving}
                                          />
                                          <span className="rsa-task-card-file-name">
                                            {routeFile.name}
                                          </span>
                                          {valueLabel && (
                                            <span className="summary-tag-file-miles">{valueLabel}</span>
                                          )}
                                        </label>
                                      );
                                    })}
                                  </>
                                )
                              ) : (
                                /* Non-RSA-model: original static file listing */
                                isLoadingXml ? (
                                  <div className="summary-tag-file summary-tag-file-empty">Loading XML files...</div>
                                ) : xmlFileNames.length > 0 ? (
                                  xmlFileNames.map((xmlName) => {
                                    const fileMiles = isIsdSvc ? null : perFileMiles[xmlName];
                                    const fileManeuvers = isIsdSvc ? perFileManeuvers[xmlName] : null;
                                    const hasValue = fileMiles != null || fileManeuvers != null;
                                    const valueLabel = isIsdSvc
                                      ? (fileManeuvers != null ? `${fileManeuvers} mnv` : null)
                                      : (fileMiles != null ? `${Number(fileMiles).toFixed(2)} mi` : null);
                                    return (
                                      <div
                                        key={`${service}-${xmlName}`}
                                        className="summary-tag-file"
                                        title={hasValue ? `${xmlName} — ${valueLabel}` : xmlName}
                                      >
                                        <span className="summary-tag-file-name">{xmlName}</span>
                                        {valueLabel && <span className="summary-tag-file-miles">{valueLabel}</span>}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="summary-tag-file summary-tag-file-empty">No XML files found</div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {showRouteSelectionValidation && !hasAnySelectedRoute && (
                    <div className="summary-route-selection-warning" role="alert">
                      Please select at least one route.
                    </div>
                  )}
                </>
              ) : (
                <span className="summary-empty">No services selected</span>
              )}
            </div>

            {/* <div className="summary-section">
              <h4>Cloud Storage Service</h4>
              {selectedStorageProvider ? (
                <div className="summary-storage-provider">
                  {renderStorageProviderButton()}
                </div>
              ) : (
                <span className="summary-empty">No storage provider selected</span>
              )}
            </div> */}

            {/* <div className="summary-section">
              <h4>Folder Selection</h4>
              {selectedDriveName && (
                <div className="summary-item">
                  <span className="summary-label">Shared Drive:</span>
                  <span className="summary-value">{selectedDriveName}</span>
                </div>
              )}
              {selectedFolders && selectedFolders.length > 0 ? (
                <div className="summary-folders-list">
                  <span className="summary-label">Folder{selectedFolders.length > 1 ? 's' : ''}:</span>
                  {selectedFolders.map((folder, index) => {
                    const path = folder.fullPath || folder.name;
                    const displayPath = path.length > 50 ? path.substring(0, 25) + '...' + path.substring(path.length - 20) : path;
                    return (
                      <div key={folder.id + (folder.name || '') + index} className="summary-item summary-folder-item">
                        <span className="summary-value" title={path}>{displayPath}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="summary-empty">No folder selected</span>
              )}
            </div> */}

            {routesXmlFiles.length > 0 && checkedRouteMatrixColumns.length > 0 && (
              <div className="summary-section summary-route-matrix-section">
                <h4>Checked Routes Summary Table:</h4>
                {checkedRouteMatrixRows.length > 0 ? (
                  <div className="summary-route-matrix-wrap">
                    <table className="summary-route-matrix-table">
                      <thead>
                        <tr>
                          <th className="summary-route-matrix-file-col">Route File</th>
                          {checkedRouteMatrixColumns.map((column) => (
                            <th
                              key={column.service}
                              className="summary-route-matrix-analysis-col"
                              title={column.label}
                            >
                              {column.shortLabel}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {checkedRouteMatrixRows.map((row) => (
                          <tr key={row.key}>
                            <td className="summary-route-matrix-file-name" title={row.name}>
                              {getDisplayFileName(row.name)}
                            </td>
                            {row.assignments.map((assignment) => (
                              <td
                                key={`${row.key}-${assignment.service}`}
                                className={`summary-route-matrix-cell${assignment.checked ? ' checked' : ''}`}
                              >
                                {assignment.checked ? <i className="fa-solid fa-check" aria-hidden /> : '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <span className="summary-empty">No checked route assignments yet</span>
                )}
              </div>
            )}

            <div className="summary-section summary-mileage-section">
              {/* Per-service accumulated breakdown */}
              {analysisServices.length > 0 && (
                <div className="summary-per-service-breakdown">
                  {routesXmlFiles.length > 0 ? (
                    /* Sum per-service from checked route files */
                    <>
                      {mileageServices.map((service) => {
                        const taskOption = SERVICE_TO_TASK_OPTION[service];
                        const serviceTotal = taskOption
                          ? routesXmlFiles.reduce((sum, f) => {
                              const fKey = normalizeDriveFolderId(f.googleDriveId);
                              if (!fKey || !routesMatrixSelection?.[fKey]?.[taskOption]) return sum;
                              const miles = Number(routesPerFileMileage[f.name]);
                              return Number.isFinite(miles) ? sum + miles : sum;
                            }, 0)
                          : 0;
                        const label = serviceMapping[service] || service;
                        return (
                          <div key={service} className="summary-per-service-row">
                            <span className="summary-per-service-label" title={label}>{label}</span>
                            <span className="summary-per-service-value">
                              {routesPerFileDataLoading
                                ? <i className="fas fa-spinner fa-spin" aria-hidden />
                                : `${serviceTotal.toFixed(2)} mi`}
                            </span>
                          </div>
                        );
                      })}
                      {isdServices.map((service) => {
                        const taskOption = SERVICE_TO_TASK_OPTION[service];
                        const serviceTotal = taskOption
                          ? routesXmlFiles.reduce((sum, f) => {
                              const fKey = normalizeDriveFolderId(f.googleDriveId);
                              if (!fKey || !routesMatrixSelection?.[fKey]?.[taskOption]) return sum;
                              const maneuvers = Number(routesPerFileManeuvers[f.name]);
                              return Number.isFinite(maneuvers) ? sum + maneuvers : sum;
                            }, 0)
                          : 0;
                        const label = serviceMapping[service] || service;
                        return (
                          <div key={service} className="summary-per-service-row">
                            <span className="summary-per-service-label" title={label}>{label}</span>
                            <span className="summary-per-service-value">
                              {routesPerFileDataLoading
                                ? <i className="fas fa-spinner fa-spin" aria-hidden />
                                : `${Number.isFinite(serviceTotal) ? serviceTotal : 0} mnv`}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    /* Standard project: totals from RSA_SSD_* etc. folders */
                    <>
                      {mileageServices.map((service) => {
                        const miles = Number(xmlEndMileTotalsByService[service]);
                        const isLoading = !!xmlLoadingByService[service];
                        const hasLoaded = Object.prototype.hasOwnProperty.call(xmlEndMileTotalsByService, service);
                        if (!hasLoaded && !isLoading) return null;
                        const label = serviceMapping[service] || service;
                        return (
                          <div key={service} className="summary-per-service-row">
                            <span className="summary-per-service-label" title={label}>{label}</span>
                            <span className="summary-per-service-value">
                              {isLoading
                                ? <i className="fas fa-spinner fa-spin" aria-hidden />
                                : `${Number.isFinite(miles) ? miles.toFixed(2) : '0.00'} mi`}
                            </span>
                          </div>
                        );
                      })}
                      {isdServices.map((service) => {
                        const maneuvers = Number(xmlIsdManeuverTotalsByService[service]);
                        const isLoading = !!xmlLoadingByService[service];
                        const hasLoaded = Object.prototype.hasOwnProperty.call(xmlIsdManeuverTotalsByService, service);
                        if (!hasLoaded && !isLoading) return null;
                        const label = serviceMapping[service] || service;
                        return (
                          <div key={service} className="summary-per-service-row">
                            <span className="summary-per-service-label" title={label}>{label}</span>
                            <span className="summary-per-service-value">
                              {isLoading
                                ? <i className="fas fa-spinner fa-spin" aria-hidden />
                                : `${Number.isFinite(maneuvers) ? maneuvers : 0} mnv`}
                            </span>
                          </div>
                        );
                      })}
                      {(mileageServices.some((s) => Object.prototype.hasOwnProperty.call(xmlEndMileTotalsByService, s)) ||
                        isdServices.some((s) => Object.prototype.hasOwnProperty.call(xmlIsdManeuverTotalsByService, s))) && (
                        <div className="summary-per-service-row summary-per-service-total">
                          <span className="summary-per-service-label">Total</span>
                          <span className="summary-per-service-value">
                            {taskMileageLoading
                              ? <i className="fas fa-spinner fa-spin" aria-hidden />
                              : `${Number.isFinite(taskMileageTotalMiles) ? taskMileageTotalMiles.toFixed(2) : '0.00'} mi`}
                            {isIsdSelected && !taskManeuversLoading && (
                              <span className="summary-per-service-maneuvers-total">
                                {` · ${Number.isFinite(taskManeuversTotal) ? taskManeuversTotal : 0} mnv`}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="summary-item summary-maneuvers-item">
                <div className={`summary-value summary-mileage-ratio ${maneuverModel.isOverage ? 'summary-ratio-overage' : ''}`}>
                  {(routesXmlFiles.length > 0 ? routesPerFileDataLoading : taskManeuversLoading) || rsaUsageLedgerLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin summary-ratio-loading-spinner" aria-hidden />
                      {' '}Computing...
                    </>
                  ) : (
                    renderSummaryTokenBar(maneuverModel, 'Maneuvers')
                  )}
                </div>
              </div>
              <div className="summary-item summary-mileage-item">
                <div className={`summary-value summary-mileage-ratio ${mileageModel.isOverage ? 'summary-ratio-overage' : ''}`}>
                  {(routesXmlFiles.length > 0 ? routesPerFileDataLoading : taskMileageLoading) || rsaUsageLedgerLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin summary-ratio-loading-spinner" aria-hidden />
                      {' '}Computing...
                    </>
                  ) : (
                    renderSummaryTokenBar(mileageModel, 'Mileage')
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
     
      {isSetupTab && onSaveSetup && (
        <div className="summary-button-container">
          <button
            type="button"
            className="setup-wkt-btn setup-save-setup-btn"
            onClick={onSaveSetup}
            disabled={savingSetup || !setupSaveEnabled}
          >
            {savingSetup ? (
              <>
                <i className="fas fa-spinner fa-spin setup-save-setup-spinner" aria-hidden />
                Saving Setup...
              </>
            ) : (
              <>
                <i className="fas fa-floppy-disk" aria-hidden />
                Save Setup
              </>
            )}
          </button>
        </div>
      )}
      {!isSetupTab && !isUploadTab && (
        <div className="summary-button-container">
          <div className="summary-buttons-row">
            <button
              className={`toggle-btn-rsa-run${isRunInProgress ? ' toggle-btn-rsa-run--loading' : ''}`}
              type="button"
              onClick={handleRunButtonClick}
              disabled={isRunInProgress || routesMatrixSaving}
            >
              {isRunInProgress ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  {' '}Running...
                </>
              ) : (
                'Run Analysis'
              )}
            </button>
            <button className='toggle-btn-rsa-clear' type="button" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      )}
      {mongoOverwriteWarning && (
        <div className="mongo-overwrite-modal-overlay">
          <div className="mongo-overwrite-modal">
            <h3 className="mongo-overwrite-modal-title">
              <i className="fa-solid fa-triangle-exclamation mongo-overwrite-modal-title-icon" aria-hidden="true" />
              Existing Data Will Be Overwritten
            </h3>
            <p className="mongo-overwrite-modal-body">
              The following MongoDB collections already contain data and will be overwritten by this run:
            </p>
            <ul className="mongo-overwrite-modal-list">
              {mongoOverwriteWarning.collections.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <div className="mongo-overwrite-modal-actions">
              <button
                className="mongo-overwrite-btn-cancel"
                onClick={() => setMongoOverwriteWarning(null)}
              >
                Cancel
              </button>
              <button
                className="mongo-overwrite-btn-confirm"
                onClick={handleMongoOverwriteConfirm}
              >
                Overwrite &amp; Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RSA_ORTHO_TILES_STORAGE_KEY = 'rsa_ortho_tiles';

// ─── IndexedDB cache helpers for ortho tile metadata ─────────────────────────
const ORTHO_IDB_NAME = 'rsa_ortho_tiles_cache';
const ORTHO_IDB_STORE = 'tiles';
const ORTHO_IDB_VERSION = 1;

function openOrthoCacheDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(ORTHO_IDB_NAME, ORTHO_IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ORTHO_IDB_STORE)) {
        db.createObjectStore(ORTHO_IDB_STORE, { keyPath: 'projectId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveOrthoTilesToCache(projectId, tiles) {
  try {
    const db = await openOrthoCacheDb();
    const tx = db.transaction(ORTHO_IDB_STORE, 'readwrite');
    tx.objectStore(ORTHO_IDB_STORE).put({ projectId, tiles, _ts: Date.now() });
    db.close();
  } catch (_) { /* IndexedDB unavailable — skip silently */ }
}

async function loadOrthoTilesFromCache(projectId) {
  try {
    const db = await openOrthoCacheDb();
    return new Promise((resolve) => {
      const tx = db.transaction(ORTHO_IDB_STORE, 'readonly');
      const req = tx.objectStore(ORTHO_IDB_STORE).get(projectId);
      req.onsuccess = () => {
        db.close();
        const data = req.result;
        if (!data || !Array.isArray(data.tiles)) resolve(null);
        else resolve(data.tiles);
      };
      req.onerror = () => { db.close(); resolve(null); };
    });
  } catch (_) {
    return null;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const RSA_MODEL_ALLOWED_MAIN_TABS = ['upload', 'setup', 'routes', 'dashboard', 'deliverables'];
const RSA_MAIN_TABS = ['upload', 'setup', 'routes', 'paths', 'analysis', 'dashboard', 'deliverables', 'settings'];
const normalizeRsaMainTab = (tab) => {
  const normalized = String(tab || '').trim().toLowerCase();
  return RSA_MAIN_TABS.includes(normalized) ? normalized : 'upload';
};

const getRoutesXmlCacheKey = (projectId) => `rsaRoutesXmlFiles:${projectId}`;
const getRoutesUploadStatusCacheKey = (projectId) => `rsaUploadStepStatus:${projectId}`;

const readRoutesXmlCache = (projectId) => {
  if (typeof window === 'undefined' || !projectId) return [];
  try {
    const sessionRaw = window.sessionStorage.getItem(getRoutesUploadStatusCacheKey(projectId));
    if (sessionRaw) {
      const sessionParsed = JSON.parse(sessionRaw);
      if (Array.isArray(sessionParsed?.routeFiles)) {
        return sessionParsed.routeFiles
          .map((item) => ({
            id: item?.id ?? '',
            googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
            name: String(item?.name || '').trim(),
            parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
          }))
          .filter((item) => item.googleDriveId && item.name);
      }
    }

    const raw = window.localStorage.getItem(getRoutesXmlCacheKey(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        id: item?.id ?? '',
        googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
        name: String(item?.name || '').trim(),
        parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
      }))
      .filter((item) => item.googleDriveId && item.name);
  } catch (error) {
    return [];
  }
};

const writeRoutesXmlCache = (projectId, routeFiles) => {
  if (typeof window === 'undefined' || !projectId) return;
  try {
    const safeRows = (Array.isArray(routeFiles) ? routeFiles : [])
      .map((item) => ({
        id: item?.id ?? '',
        googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
        name: String(item?.name || '').trim(),
        parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
      }))
      .filter((item) => item.googleDriveId && item.name);
    window.sessionStorage.setItem(
      getRoutesUploadStatusCacheKey(projectId),
      JSON.stringify({
        checkedAt: Date.now(),
        hasFiles: safeRows.length > 0,
        routeFiles: safeRows,
      })
    );
    window.localStorage.setItem(getRoutesXmlCacheKey(projectId), JSON.stringify(safeRows));
  } catch (error) {
    // ignore storage errors
  }
};

const hasRoutesUploadStatusCache = (projectId) => {
  if (typeof window === 'undefined' || !projectId) return false;
  try {
    return Boolean(window.sessionStorage.getItem(getRoutesUploadStatusCacheKey(projectId)));
  } catch (error) {
    return false;
  }
};

function PayloadForm({
  projectId,
  projectName = '',
  preloadedRsaSetup,
  preloadedRsaSetupFolderId,
  preloadedRsaSetupFolderName,
  preloadRsaSetupLoading = false,
  projectType,
  initialActiveMainTab = 'upload',
  onActiveMainTabChange,
  onGoToFiles,
}) {
  // Cached project list and dropdown options
  const [allProjects, setAllProjects] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  
  // State for selected project from dropdown
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? null);
  // Store an array of selected folder objects (order of selection)
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedDriveName, setSelectedDriveName] = useState('');
  const [roadDetectionCategory, setRoadDetectionCategory] = useState('');
  const [lidarType, setLidarType] = useState('');
  const [horizontalData, setHorizontalData] = useState(null);
  const [EmbankmentData, setEmbankmentData] = useState(null); // State for Embankment data
  const [manualLaneCreationActive, setManualLaneCreationActive] = useState(false);
  const [activeManualLaneId, setActiveManualLaneId] = useState(null);
  const [fitToPathData, setFitToPathData] = useState(null); // when set, map fits view to this path then clears
  const [fitToBounds, setFitToBounds] = useState(null); // when set, map fits view to this bounds (e.g. LAS extent) then clears
  const [pathsPanelMode, setPathsPanelMode] = useState('path-creator'); // 'path-creator' | 'intersection' | 'maps' â€“ controls right-side paths panel content
  const [overlayOpacity, setOverlayOpacity] = useState(0.9); // PNG overlay opacity 0â€“1
  // Map overlay coordinate system: 'global' (lat/lon) or 'local' (x,y + WKT for production)
  const [mapOverlayCoordinateMode, setMapOverlayCoordinateMode] = useState('local');
  const [mapOverlayBoundsGlobal, setMapOverlayBoundsGlobal] = useState(() => ({
    ...DEFAULT_GLOBAL_BOUNDS,
  }));
  const [mapOverlayLocalConfig, setMapOverlayLocalConfig] = useState(() => ({
    extent: { ...DEFAULT_LOCAL_CONFIG.extent },
    originXY: { ...DEFAULT_LOCAL_CONFIG.originXY },
    originLatLng: { ...DEFAULT_LOCAL_CONFIG.originLatLng },
    scaleLatPerUnit: DEFAULT_LOCAL_CONFIG.scaleLatPerUnit,
    scaleLngPerUnit: DEFAULT_LOCAL_CONFIG.scaleLngPerUnit,
    wkt: DEFAULT_LOCAL_CONFIG.wkt,
    state: DEFAULT_LOCAL_CONFIG.state,
    routeNumber: DEFAULT_LOCAL_CONFIG.routeNumber,
    pathNameSegment: DEFAULT_LOCAL_CONFIG.pathNameSegment,
  }));
  const [wktInputLocked, setWktInputLocked] = useState(false); // lock WKT textarea to prevent accidental changes
  const [wktValidation, setWktValidation] = useState(createIdleWktValidationState);
  const [wktRequirementsHelpModalOpen, setWktRequirementsHelpModalOpen] = useState(false);
  const wktValidationRequestRef = useRef(0);
  const [intersectionDrawingMode, setIntersectionDrawingMode] = useState(null); // null | 'marker' | 'polyline' | 'polyline-curved' | 'measure-distance' | 'polygon' | 'circle' | 'rectangle'
  const [intersectionOverlays, setIntersectionOverlays] = useState([]); // { id, type, coordinates }[]
  const [curvePoints, setCurvePoints] = useState([]); // points for curved line in progress
  const [measurePoints, setMeasurePoints] = useState([]); // points for distance measurement in progress
  const handleIntersectionDrawingModeChange = useCallback((nextMode) => {
    setIntersectionDrawingMode(nextMode);
    if (nextMode !== 'polyline-curved') setCurvePoints([]);
    if (nextMode !== 'measure-distance') setMeasurePoints([]);
  }, []);
  const [curvedLineEditPointCount, setCurvedLineEditPointCount] = useState(5); // 3â€“10 edit points along curve
  // Storage provider state
  const [selectedStorageProvider, setSelectedStorageProvider] = useState('system_google_drive');
  const [storageConnections, setStorageConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [useSystemGoogleDrive, setUseSystemGoogleDrive] = useState(true);
  
  // Only existing projects are supported
  const [existingProjectName, setExistingProjectName] = useState('');
  const [rsaGroupsMileages, setRsaGroupsMileages] = useState({});
  const [rsaProjectsPerGroup, setRsaProjectsPerGroup] = useState({});
  const [rsaGroupsIsdManeuvers, setRsaGroupsIsdManeuvers] = useState({});
  const [rsaUsageLedgerState, setRsaUsageLedgerState] = useState(null);
  const [rsaUsageLedgerLoading, setRsaUsageLedgerLoading] = useState(false);

  // State for the analysis types and services (toggle buttons)
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [services, setServices] = useState([]);
  // State to track selected task buttons and their analysis_template_ids
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [hasSubmittedRsaRun, setHasSubmittedRsaRun] = useState(false);
  const [visibleTaskOptions, setVisibleTaskOptions] = useState([]);
  const [isLoadingVisibleTaskOptions, setIsLoadingVisibleTaskOptions] = useState(false);
  // Instead of a global loading state, we now track each payload request.
  const [requests, setRequests] = useState([]);
  // Additional state for our new custom radio button group (e.g. Time selection)
  const [timeOption, setTimeOption] = useState('');

  // --- State for Folder Tree Popup ---
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [mapOverlaySelectedFolders, setMapOverlaySelectedFolders] = useState([]);
  const [projectSetupFolderId, setProjectSetupFolderId] = useState(null);
  const effectiveSetupFolderId = useMemo(() => {
    const folders = mapOverlaySelectedFolders || [];
    const selectedFolderId = folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId;
    return selectedFolderId ?? preloadedRsaSetupFolderId ?? projectSetupFolderId ?? null;
  }, [mapOverlaySelectedFolders, preloadedRsaSetupFolderId, projectSetupFolderId]);
  const [savingLasSeparationPath, setSavingLasSeparationPath] = useState(false);
  const [creatingOrthoMapsByPath, setCreatingOrthoMapsByPath] = useState({}); // { [pathId]: true|false }
  const [orthoProgressByPath, setOrthoProgressByPath] = useState({}); // { [pathId]: { progress: 0-100, message: '' } }
  const [lasBoundingBoxes, setLasBoundingBoxes] = useState([]); // [{ name, south, north, west, east }] in lat/lon for map overlay
  const [selectedLasBoundingBoxIndex, setSelectedLasBoundingBoxIndex] = useState(0);
  const [loadingLasBounds, setLoadingLasBounds] = useState(false);
  const [orthoTiles, setOrthoTiles] = useState([]); // [{ name, pngFileId, bounds: { south, north, west, east }, quad: [{lat,lng} x4] }] for map overlay
  const [loadingOrthoTiles, setLoadingOrthoTiles] = useState(false);
  const [maps2000ByPath, setMaps2000ByPath] = useState({}); // { [pathId]: true|false|null }
  const [loadingMaps2000Check, setLoadingMaps2000Check] = useState(false);
  const [imageOverlaysLoadedCount, setImageOverlaysLoadedCount] = useState(0);
  const [imageOverlaysTotal, setImageOverlaysTotal] = useState(0);
  const [lasBoundingBoxesVisible, setLasBoundingBoxesVisible] = useState(true);
  const [orthoTilesVisible, setOrthoTilesVisible] = useState(true);
  const [groupSize, setGroupSize] = useState(25);
  
  // --- State for custom dropdown ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const dropdownRef = useRef(null);
  
  const [projectFilesSnapshot, setProjectFilesSnapshot] = useState([]);

  // --- State for clear confirmation popup ---
  const [showClearPopup, setShowClearPopup] = useState(false);
  const [taskInfoModal, setTaskInfoModal] = useState({ isOpen: false, title: '', image: null });
  const effectiveProjectId = selectedProjectId ?? projectId ?? null;

  // ─── Multi-path / multi-lane system ─────────────────────────────────────────
  const rsaPathsHook = useRsaPaths(effectiveProjectId);
  const laneEditHistory = useLaneEditHistory(rsaPathsHook.laneEditMode);
  // Initialize undo/redo history when entering lane edit mode
  const laneEditModeRef = useRef(false);
  useEffect(() => {
    if (rsaPathsHook.laneEditMode && !laneEditModeRef.current) {
      const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
      if (lane?.waypoints) laneEditHistory.initialize(lane.waypoints);
    }
    laneEditModeRef.current = rsaPathsHook.laneEditMode;
  }, [rsaPathsHook.laneEditMode, rsaPathsHook.lanes, rsaPathsHook.activeLaneId, laneEditHistory]);
  const activeRsaPath = useMemo(
    () => rsaPathsHook.paths.find((p) => p.id === rsaPathsHook.activePathId) ?? rsaPathsHook.paths[0] ?? null,
    [rsaPathsHook.paths, rsaPathsHook.activePathId]
  );
  const activeRsaLane = useMemo(
    () => rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId) ?? null,
    [rsaPathsHook.lanes, rsaPathsHook.activeLaneId]
  );
  const activeRsaEntityType = rsaPathsHook.activeEntityType === 'lane' && activeRsaLane ? 'lane' : 'path';
  const activeRsaEntity = activeRsaEntityType === 'lane' ? activeRsaLane : activeRsaPath;
  const activeRsaEntityId = activeRsaEntity?.id ?? null;
  const activeRsaWaypoints = Array.isArray(activeRsaEntity?.waypoints) ? activeRsaEntity.waypoints : EMPTY_WAYPOINTS;
  const activeRsaPathData = activeRsaEntity?.pathData ?? null;

  // Refs that always hold the latest active path/lane state.
  // Used by pathSetWaypoints so stale closures (e.g. geocoding callbacks)
  // always read current data instead of an outdated capture.
  const activeRsaPathRef = useRef(activeRsaPath);
  const activeRsaLaneRef = useRef(activeRsaLane);
  const activeRsaEntityTypeRef = useRef(activeRsaEntityType);
  useEffect(() => { activeRsaPathRef.current = activeRsaPath; }, [activeRsaPath]);
  useEffect(() => { activeRsaLaneRef.current = activeRsaLane; }, [activeRsaLane]);
  useEffect(() => { activeRsaEntityTypeRef.current = activeRsaEntityType; }, [activeRsaEntityType]);
  const activeRsaColorIndex = activeRsaEntity?.colorIndex ?? 0;
  const activeRsaColor = SEGMENT_COLORS[activeRsaColorIndex % SEGMENT_COLORS.length];
  const rsaLegacySegments = useMemo(() => {
    const paths = rsaPathsHook.paths.map((path, idx) => ({
      ...path,
      type: 'path',
      entityType: 'path',
      entityId: path.id,
      label: path.label || `Route${idx + 1}`,
    }));
    const lanes = rsaPathsHook.lanes.map((lane, idx) => ({
      ...lane,
      type: 'lane',
      entityType: 'lane',
      entityId: lane.id,
      manualLane: lane.creationMode === 'manual',
      label: lane.label || `Lane ${idx + 1}`,
    }));
    return [...paths, ...lanes];
  }, [rsaPathsHook.paths, rsaPathsHook.lanes]);
  const activeRsaLegacyIndex = useMemo(() => {
    if (!activeRsaEntityId) return 0;
    const idx = rsaLegacySegments.findIndex((seg) => seg.entityId === activeRsaEntityId);
    return idx >= 0 ? idx : 0;
  }, [rsaLegacySegments, activeRsaEntityId]);
  const rsaSegmentVisibility = useMemo(
    () => rsaLegacySegments.map((seg) => seg.visible !== false),
    [rsaLegacySegments]
  );

  const normalizedProjectType = String(projectType || '').trim().toLowerCase();
  const isRsaModelProject = normalizedProjectType === 'rsa-model';
  const rsaModelRouteInfoRef = useRef({});
  const rsaRunMetricsRef = useRef({});
  const allowedTaskOptions = isRsaModelProject ? RSA_MODEL_TASK_OPTIONS : TASK_OPTIONS;
  const analysisTemplateIdMapping = isRsaModelProject
    ? RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING
    : DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING;

  useEffect(() => {
    if (projectId !== null && projectId !== undefined && String(projectId) !== '') {
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    const loadVisibleTasksFromProjectFiles = async () => {
      if (!effectiveProjectId) {
        setVisibleTaskOptions([]);
        setIsLoadingVisibleTaskOptions(false);
        setProjectFilesSnapshot([]);
        return;
      }

      setIsLoadingVisibleTaskOptions(true);
      setVisibleTaskOptions([]);
      // Recheck once project loading completes to avoid stale folder snapshots.
      if (preloadRsaSetupLoading) {
        return;
      }

      try {
        const isProductionDomain = window.location.hostname.includes('123bimlive.com');
        const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;
        const url = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
          : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;
        const response = await axios.get(url);
        const files = response.data?.files || [];
        if (!cancelled) {
          setProjectFilesSnapshot(Array.isArray(files) ? files : []);
        }
        const {
          hasSourceFolder,
          taskOptions: dbTaskOptions,
          sourceGoogleDriveIds,
        } = deriveTaskMetadataFromProjectFiles(files);

        if (!hasSourceFolder) {
          if (!cancelled) {
            setVisibleTaskOptions(allowedTaskOptions);
            setIsLoadingVisibleTaskOptions(false);
          }
          return;
        }

        const detectedTasks = new Set(Array.isArray(dbTaskOptions) ? dbTaskOptions : []);

        if (Array.isArray(sourceGoogleDriveIds) && sourceGoogleDriveIds.length > 0) {
          const driveChildrenRequests = sourceGoogleDriveIds.map((sourceFolderId) => {
            const childrenUrl = baseUrl
              ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
              : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
            return axios.get(childrenUrl).catch((err) => {
              console.warn('Failed to fetch Source children from Google Drive:', sourceFolderId, err);
              return { data: { items: [] } };
            });
          });

          const driveChildrenResponses = await Promise.all(driveChildrenRequests);
          for (const driveResponse of driveChildrenResponses) {
            const items = driveResponse?.data?.items || [];
            for (const item of items) {
              if (!isFolderItem(item)) {
                continue;
              }
              const taskOption = mapFolderCodeToTaskOption(item?.name);
              if (taskOption) {
                detectedTasks.add(taskOption);
              }
            }
          }
        }

        const derivedTaskOptions = allowedTaskOptions.filter((task) => detectedTasks.has(task));
        if (cancelled) {
          return;
        }
        setVisibleTaskOptions(derivedTaskOptions);
        setIsLoadingVisibleTaskOptions(false);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to derive RSA tasks from project files:', error);
          setVisibleTaskOptions(allowedTaskOptions);
          setIsLoadingVisibleTaskOptions(false);
        }
      }
    };

    loadVisibleTasksFromProjectFiles();
    return () => {
      cancelled = true;
    };
  }, [effectiveProjectId, preloadRsaSetupLoading, allowedTaskOptions, isRsaModelProject]);

  useEffect(() => {
    const visibleTasksSet = new Set(visibleTaskOptions);
    const allTaskServiceValues = new Set(Object.values(TASK_OPTION_TO_SERVICE));
    const allowedTaskServiceValues = new Set(
      visibleTaskOptions
        .map((task) => TASK_OPTION_TO_SERVICE[task])
        .filter(Boolean)
    );

    setSelectedTasks((prev) => prev.filter((task) => visibleTasksSet.has(task)));
    setServices((prev) =>
      prev.filter((service) => !allTaskServiceValues.has(service) || allowedTaskServiceValues.has(service))
    );
  }, [visibleTaskOptions]);

  const openTaskInfoModal = useCallback((taskType) => {
    const infoImage = TASK_INFO_IMAGE_CONFIG[taskType];
    if (!infoImage) return;
    setTaskInfoModal({ isOpen: true, title: taskType, image: infoImage });
  }, []);

  const closeTaskInfoModal = useCallback(() => {
    setTaskInfoModal({ isOpen: false, title: '', image: null });
  }, []);

  // --- State for Save Setup overwrite confirmation modal ---
  const [showSaveSetupOverwriteModal, setShowSaveSetupOverwriteModal] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);

  // --- Slide-down notification (replaces alert) ---
  const [notification, setNotification] = useState({ message: null, leaving: false });
  const notificationTimeoutRef = useRef(null);
  const showNotification = useCallback((message, durationMs = 4000) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, leaving: false });
    const displayDuration = Number.isFinite(Number(durationMs)) ? Math.max(0, Number(durationMs)) : 4000;
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, leaving: true }));
      notificationTimeoutRef.current = setTimeout(() => {
        setNotification({ message: null, leaving: false });
        notificationTimeoutRef.current = null;
      }, 300);
    }, displayDuration);
  }, []);
  useEffect(() => () => {
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
  }, []);

  const validateWktInput = useCallback(async (rawWkt, { silent = true } = {}) => {
    const trimmedRaw = String(rawWkt ?? '').trim();
    const wkt = normalizeWktInput(trimmedRaw);
    if (!wkt) {
      wktValidationRequestRef.current += 1;
      setWktValidation(createIdleWktValidationState());
      return null;
    }

    const requestId = wktValidationRequestRef.current + 1;
    wktValidationRequestRef.current = requestId;
    setWktValidation((prev) => ({
      ...prev,
      status: 'checking',
      checkedWkt: wkt,
      message: '',
    }));

    try {
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const url = baseUrl ? `${API_BASE_URL}/api/wkt/validate` : '/api/wkt/validate';
      const response = await axios.post(url, { wkt }, { timeout: 15000 });
      if (requestId !== wktValidationRequestRef.current) {
        return null;
      }

      const payload = response?.data || {};
      const nextValidation = {
        status: 'done',
        checkedWkt: wkt,
        valid: Boolean(payload.valid),
        willWork: Boolean(payload.willWork),
        message: payload.message || '',
        location: payload.location || null,
        crs: payload.crs || null,
        inputType: payload.inputType || 'unknown',
      };
      setWktValidation(nextValidation);

      // Replace JSON-escaped paste (e.g. PROJCS[\"a\"]) with valid WKT in the textarea after successful parse
      if (nextValidation.valid && wkt !== trimmedRaw) {
        setMapOverlayLocalConfig((c) =>
          String(c.wkt ?? '').trim() === trimmedRaw ? { ...c, wkt } : c
        );
      }

      if (!silent && !nextValidation.valid) {
        showNotification(nextValidation.message || 'WKT is invalid.');
      } else if (!silent && nextValidation.valid && !nextValidation.willWork) {
        showNotification(nextValidation.message || 'WKT is valid but not compatible with map conversion.');
      }

      return nextValidation;
    } catch (err) {
      if (requestId !== wktValidationRequestRef.current) {
        return null;
      }
      const msg = err.response?.data?.error || err.message || 'Failed to validate WKT.';
      const failedValidation = {
        status: 'error',
        checkedWkt: wkt,
        valid: false,
        willWork: false,
        message: msg,
        location: null,
        crs: null,
        inputType: 'unknown',
      };
      setWktValidation(failedValidation);
      if (!silent) {
        showNotification(`WKT validation failed: ${msg}`);
      }
      return failedValidation;
    }
  }, [showNotification, setMapOverlayLocalConfig]);

  useEffect(() => {
    const wktRaw = (mapOverlayLocalConfig?.wkt ?? '').trim();
    const wkt = normalizeWktInput(wktRaw);
    if (!wkt) {
      wktValidationRequestRef.current += 1;
      setWktValidation(createIdleWktValidationState());
      return;
    }

    if (
      wkt === wktValidation.checkedWkt &&
      (wktValidation.status === 'checking' || wktValidation.status === 'done')
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      validateWktInput(mapOverlayLocalConfig?.wkt ?? '', { silent: true });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [mapOverlayLocalConfig?.wkt, wktValidation.checkedWkt, wktValidation.status, validateWktInput]);

  // Load LAS/LAZ bounding boxes from selected map-overlay folder(s) and show on map (triggered by "Load Las to Map" button)
  const loadLasToMap = useCallback(() => {
    const folders = mapOverlaySelectedFolders || [];
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    if (folders.length === 0) {
      showNotification('Select at least one LAS folder first.');
      return;
    }
    if (!useSystemGoogleDrive) {
      showNotification('Load Las to Map is only available when using System Drive.');
      return;
    }
    if (!wkt) {
      showNotification('Enter WKT CRS in Local (x, y + WKT) to load LAS bounds.');
      return;
    }
    setLoadingLasBounds(true);
    const isProductionDomain = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain)
      ? ''
      : API_BASE_URL;
    const fetchOne = (folderId) =>
      axios.get(`${baseUrl}/api/google-drive/las-laz-bounds`, { params: { folder_id: folderId } })
        .then((res) => res.data?.bounds || [])
        .catch(() => []);
    const proj4FromValidate = wktValidation?.crs?.proj4;
    Promise.all(
      folders.map((f) => f.id ?? f.folder_id ?? f.folderId).filter(Boolean).map(fetchOne)
    )
      .then(async (arrays) => {
        const all = arrays.flat();
        if (all.length === 0) {
          setLasBoundingBoxes([]);
          setSelectedLasBoundingBoxIndex(0);
          showNotification('No LAS/LAZ bounds found in selected folder(s).');
          return;
        }
        // Batch all bbox corners into a single projection call for speed
        const allCorners = [];
        const validItems = [];
        for (const item of all) {
          const bb = item.bbox;
          if (!bb || typeof bb.min_x !== 'number' || typeof bb.max_x !== 'number' ||
              typeof bb.min_y !== 'number' || typeof bb.max_y !== 'number') continue;
          allCorners.push(
            [bb.min_x, bb.max_y], [bb.max_x, bb.max_y],  // TL, TR
            [bb.max_x, bb.min_y], [bb.min_x, bb.min_y],  // BR, BL
          );
          validItems.push(item);
        }
        const projected = await projectedPointsToLatLngAsync(allCorners, wkt, proj4FromValidate);
        const bounds = [];
        if (projected && projected.length === validItems.length * 4) {
          for (let i = 0; i < validItems.length; i++) {
            const quad = projected.slice(i * 4, i * 4 + 4);
            const b = latLngPointsToBounds(quad);
            if (b) bounds.push({ ...b, name: validItems[i]?.name || '' });
          }
        } else {
          // Fallback: transform one-by-one if batch failed
          for (const item of all) {
            const b = await projectedBboxToLatLngBoundsAsync(item.bbox, wkt, proj4FromValidate);
            if (b) bounds.push({ ...b, name: item?.name || '' });
          }
        }
        setLasBoundingBoxes(bounds);
        setSelectedLasBoundingBoxIndex(0);
        showNotification(bounds.length ? `Loaded ${bounds.length} LAS/LAZ bounding box(es) to map.` : 'No LAS/LAZ bounds found in selected folder(s).');
      })
      .catch(() => {
        setLasBoundingBoxes([]);
        setSelectedLasBoundingBoxIndex(0);
        showNotification('Failed to load LAS bounds.');
      })
      .finally(() => setLoadingLasBounds(false));
  }, [mapOverlaySelectedFolders, mapOverlayLocalConfig?.wkt, wktValidation?.crs?.proj4, useSystemGoogleDrive, showNotification]);

  // Load ortho map tiles (PNG) from maps_2000 in selected folder(s) and show on map
  const loadOrthoMaps = useCallback(() => {
    const folders = mapOverlaySelectedFolders || [];
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    if (folders.length === 0) {
      showNotification('Select at least one LAS folder first.');
      return;
    }
    if (!useSystemGoogleDrive) {
      showNotification('Load ortho maps is only available when using System Drive.');
      return;
    }
    if (!wkt) {
      showNotification('Enter WKT CRS in Local (x, y + WKT) to load ortho maps.');
      return;
    }
    setLoadingOrthoTiles(true);
    const isProductionDomain = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
    const orthoUrl = baseUrl ? `${API_BASE_URL}/api/google-drive/ortho-tiles` : '/api/google-drive/ortho-tiles';
    // Fetch tiles from per-path folders + legacy bare maps_2000
    const currentPaths = rsaPathsHook.paths;
    const fetchOne = (folderId, pathName) =>
      axios.get(orthoUrl, { params: { folder_id: folderId, ...(pathName ? { path_name: pathName } : {}) } })
        .then((res) => res.data?.tiles || [])
        .catch(() => []);
    const proj4FromValidate = wktValidation?.crs?.proj4;
    const folderIds = folders.map((f) => f.id ?? f.folder_id ?? f.folderId).filter(Boolean);
    const fetchPromises = [];
    for (const folderId of folderIds) {
      // Fetch per-path folders
      for (const p of currentPaths) {
        fetchPromises.push(fetchOne(folderId, sanitizePathLabel(p.label)));
      }
      // Also fetch bare maps_2000 for backward compat
      fetchPromises.push(fetchOne(folderId, null));
    }
    Promise.all(fetchPromises)
      .then(async (arrays) => {
        // Deduplicate tiles by png_file_id (same tile may appear in legacy + per-path queries)
        const seen = new Set();
        const all = arrays.flat().filter((t) => {
          if (!t?.png_file_id || seen.has(t.png_file_id)) return false;
          seen.add(t.png_file_id);
          return true;
        });
        const withLatLng = [];
        for (const tile of all) {
          let quad = null;
          const projectedCorners = Array.isArray(tile?.projected_corners) ? tile.projected_corners : [];
          if (projectedCorners.length === 4) {
            const xy = projectedCorners.map((p) => [Number(p?.x), Number(p?.y)]);
            if (xy.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y))) {
              quad = await projectedPointsToLatLngAsync(xy, wkt, proj4FromValidate);
            }
          }
          if (!quad) {
            quad = await projectedBboxToLatLngQuadAsync(tile.bounds, wkt, proj4FromValidate);
          }
          const b = latLngPointsToBounds(quad) || await projectedBboxToLatLngBoundsAsync(tile.bounds, wkt, proj4FromValidate);
          if (b && tile.png_file_id) {
            withLatLng.push({
              name: tile.name,
              pngFileId: tile.png_file_id,
              bounds: b,
              quad,
            });
          }
        }
        setOrthoTiles(withLatLng);
        if (withLatLng.length === 0) setMaps2000ByPath((prev) => {
          const next = {};
          Object.keys(prev).forEach((k) => { next[k] = false; });
          return next;
        });
        if (projectId && withLatLng.length > 0) {
          saveOrthoTilesToCache(projectId, withLatLng);
        }
        showNotification(withLatLng.length ? `Loaded ${withLatLng.length} ortho tile(s) on map.` : 'No ortho tiles found in maps_2000.');
      })
      .catch(() => {
        setOrthoTiles([]);
        setMaps2000ByPath((prev) => {
          const next = {};
          Object.keys(prev).forEach((k) => { next[k] = false; });
          return next;
        });
        showNotification('Failed to load ortho maps.');
      })
      .finally(() => setLoadingOrthoTiles(false));
  }, [mapOverlaySelectedFolders, mapOverlayLocalConfig?.wkt, wktValidation?.crs?.proj4, useSystemGoogleDrive, projectId, showNotification, rsaPathsHook.paths]);

  // Restore ortho tiles from IndexedDB when projectId is set (e.g. on load or project switch)
  const orthoRestoredFromCacheRef = useRef(false);
  useEffect(() => {
    if (!projectId) return;
    orthoRestoredFromCacheRef.current = false;
    let cancelled = false;
    loadOrthoTilesFromCache(projectId).then((cached) => {
      if (cancelled) return;
      if (Array.isArray(cached) && cached.length > 0) {
        setOrthoTiles(cached);
        orthoRestoredFromCacheRef.current = true;
      } else {
        setOrthoTiles([]);
      }
    });
    return () => { cancelled = true; };
  }, [projectId]);

  // When we have ortho tiles we know maps_2000 exists — mark all paths
  useEffect(() => {
    if (orthoTiles.length > 0) {
      setMaps2000ByPath((prev) => {
        const next = { ...prev };
        rsaPathsHook.paths.forEach((p) => { next[p.id] = true; });
        return next;
      });
    }
  }, [orthoTiles.length, rsaPathsHook.paths]);

  // Check which per-path maps_2000 folders exist using the batch endpoint
  // Delete path with Drive cleanup of maps_2000_{path_name} and LasSeparationPath_{path_name}.json
  const handleDeletePathWithCleanup = useCallback((pathId) => {
    const path = rsaPathsHook.paths.find((p) => p.id === pathId);
    const sanitizedName = path ? sanitizePathLabel(path.label) : null;
    rsaPathsHook.actions.deletePath(pathId);
    setMaps2000ByPath((prev) => {
      const next = { ...prev };
      delete next[pathId];
      return next;
    });
    if (sanitizedName && effectiveSetupFolderId && useSystemGoogleDrive) {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const base = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
      const apiBase = base ? `${API_BASE_URL}/api/google-drive` : '/api/google-drive';
      axios.post(`${apiBase}/ortho-cleanup-path`, {
        folder_id: effectiveSetupFolderId,
        path_name: sanitizedName,
      }).catch((err) => {
        console.warn('[handleDeletePathWithCleanup] Drive cleanup failed (non-critical):', err.message);
      });
    }
  }, [rsaPathsHook.paths, rsaPathsHook.actions, effectiveSetupFolderId, useSystemGoogleDrive]);

  const checkMaps2000ForPaths = useCallback(() => {
    const folders = mapOverlaySelectedFolders || [];
    const folderId = folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId;
    if (!folderId || !useSystemGoogleDrive) {
      setMaps2000ByPath({});
      return;
    }
    const currentPaths = rsaPathsHook.paths;
    if (!currentPaths.length) {
      setMaps2000ByPath({});
      return;
    }
    setLoadingMaps2000Check(true);
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const apiBase = baseUrl ? `${API_BASE_URL}/api/google-drive` : '/api/google-drive';
    const pathNames = currentPaths.map((p) => sanitizePathLabel(p.label));
    axios
      .post(`${apiBase}/ortho-has-maps2000-batch`, {
        folder_id: folderId,
        path_names: pathNames,
      }, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        const results = res.data?.results || {};
        const hasLegacy = res.data?.hasLegacy === true;
        const byPath = {};
        currentPaths.forEach((p, i) => {
          const sanitized = pathNames[i];
          byPath[p.id] = results[sanitized] === true || (hasLegacy && !Object.values(results).some(Boolean));
        });
        setMaps2000ByPath(byPath);
      })
      .catch(() => {
        const byPath = {};
        currentPaths.forEach((p) => { byPath[p.id] = false; });
        setMaps2000ByPath(byPath);
      })
      .finally(() => setLoadingMaps2000Check(false));
  }, [mapOverlaySelectedFolders, useSystemGoogleDrive, rsaPathsHook.paths]);

  useEffect(() => {
    if (orthoTiles.length > 0) return;
    const folders = mapOverlaySelectedFolders || [];
    const folderId = folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId;
    if (!folderId || !useSystemGoogleDrive) {
      setMaps2000ByPath({});
      return;
    }
    checkMaps2000ForPaths();
  }, [orthoTiles.length, mapOverlaySelectedFolders, useSystemGoogleDrive, checkMaps2000ForPaths]);

  // Auto-load ortho tiles from Drive when maps exist but no cached tiles were restored
  useEffect(() => {
    if (orthoRestoredFromCacheRef.current) return;
    if (orthoTiles.length > 0) return;
    if (loadingOrthoTiles || loadingMaps2000Check) return;
    const hasAnyMaps = Object.values(maps2000ByPath).some(Boolean);
    if (!hasAnyMaps) return;
    const folders = mapOverlaySelectedFolders || [];
    if (folders.length === 0 || !useSystemGoogleDrive) return;
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    if (!wkt) return;
    loadOrthoMaps();
  }, [maps2000ByPath, orthoTiles.length, loadingOrthoTiles, loadingMaps2000Check, mapOverlaySelectedFolders, useSystemGoogleDrive, mapOverlayLocalConfig?.wkt, loadOrthoMaps]);

  // Reset image-overlay load progress when ortho tile list changes (e.g. after new Load Ortho Maps)
  useEffect(() => {
    setImageOverlaysLoadedCount(0);
    setImageOverlaysTotal(0);
  }, [orthoTiles]);

  const onImageOverlaysLoadProgress = useCallback((loaded, total) => {
    setImageOverlaysLoadedCount(loaded);
    setImageOverlaysTotal(total);
  }, []);

  // Memoize so progress callback updates don't change reference and restart ImageOverlays effect (avoids 0/34 1/34 flicker)
  const imageOverlaysForMap = useMemo(() => {
    const isProd = window.location.hostname.includes('123bimlive.com');
    const base = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const mediaBase = base ? `${API_BASE_URL}/api/google-drive/file` : '/api/google-drive/file';
    const orthoOverlays = (orthoTiles || []).length > 0
      ? (orthoTiles || []).map((t) => ({
          url: t.localUrl || `${mediaBase}/${t.pngFileId}/media`,
          bounds: t.bounds,
          quad: t.quad,
        }))
      : [];
    return orthoOverlays;
  }, [orthoTiles]);

  // Build setup payload for .rsa/setup.json (same shape as mapOverlayLocalConfig)
  const buildSetupPayload = useCallback((validationOverride = null) => {
    const c = mapOverlayLocalConfig || {};
    const activeValidation = validationOverride || wktValidation;
    const wktForSetup = normalizeWktInput(String(c.wkt ?? '').trim());
    const wktSummary = buildPersistedWktSummary(wktForSetup, activeValidation);
    return {
      wkt: wktForSetup,
      wktSummary,
      wktValidation: {
        status: wktSummary.status,
        valid: wktSummary.valid,
        willWork: wktSummary.willWork,
        message: wktSummary.message,
        location: wktSummary.location,
        crs: wktSummary.crs,
        inputType: wktSummary.inputType,
      },
      extent: c.extent ? { ...c.extent } : undefined,
      originXY: c.originXY ? { ...c.originXY } : undefined,
      originLatLng: c.originLatLng ? { ...c.originLatLng } : undefined,
      scaleLatPerUnit: c.scaleLatPerUnit,
      scaleLngPerUnit: c.scaleLngPerUnit,
      state: String(c.state ?? '').trim(),
      routeNumber: String(c.routeNumber ?? '').trim(),
      pathNameSegment: String(c.pathNameSegment ?? '').trim(),
    };
  }, [mapOverlayLocalConfig, wktValidation]);

  const fetchProjectSetupFolderId = useCallback(async (targetProjectId) => {
    if (!targetProjectId) return null;
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const cacheBust = `_ts=${Date.now()}`;
    const filesUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${targetProjectId}/files?${cacheBust}`
      : `/api/projects/${targetProjectId}/files?${cacheBust}`;

    try {
      const filesResponse = await axios.get(filesUrl);
      const files = filesResponse.data?.files || [];
      const { sourceGoogleDriveIds } = deriveTaskMetadataFromProjectFiles(files);
      if (Array.isArray(sourceGoogleDriveIds) && sourceGoogleDriveIds.length > 0) {
        return String(sourceGoogleDriveIds[0]).trim();
      }

      const fallbackFolder = (files || []).find((item) => {
        if (!isFolderItem(item)) return false;
        const driveId = String(getGoogleDriveId(item) ?? '').trim();
        return Boolean(driveId);
      });
      const fallbackFolderId = String(getGoogleDriveId(fallbackFolder) ?? '').trim();
      return fallbackFolderId || null;
    } catch (err) {
      console.warn('Failed to resolve setup folder from project files:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!projectId) {
      setProjectSetupFolderId(null);
      return;
    }
    if (preloadedRsaSetupFolderId) {
      setProjectSetupFolderId(String(preloadedRsaSetupFolderId));
      return;
    }
    let cancelled = false;
    fetchProjectSetupFolderId(projectId).then((folderId) => {
      if (!cancelled && folderId) {
        setProjectSetupFolderId(folderId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, preloadedRsaSetupFolderId, fetchProjectSetupFolderId]);

  // Save Setup to central registry (Shared Drive): validate (project, folder, WKT), check if registry already has setup for overwrite modal, then POST
  const performSaveSetup = useCallback(async (overwrite = false) => {
    try {
      if (!projectId) {
        showNotification('Open a project to save setup (setup is stored per project).');
        return;
      }
      let folderId = effectiveSetupFolderId;
      const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
      if (!folderId) {
        folderId = await fetchProjectSetupFolderId(projectId);
        if (folderId) {
          setProjectSetupFolderId(folderId);
        }
      }
      if (!folderId) {
        showNotification('Failed to save setup: project folder ID not found.');
        return;
      }
      if (!wkt) {
        showNotification('Enter a valid WKT in Map overlay & coordinate system first.');
        return;
      }
      const validationResult = await validateWktInput(wkt, { silent: false });
      if (!validationResult?.valid) {
        setWktInputLocked(false);
        return;
      }
      if (!validationResult?.willWork) {
        setWktInputLocked(false);
        showNotification('WKT is valid, but not compatible with current map conversion support.');
        return;
      }
      if (!useSystemGoogleDrive) {
        showNotification('Save Setup is only available when using System Drive.');
        return;
      }
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
      const apiBase = baseUrl ? `${API_BASE_URL}/api/google-drive/rsa` : '/api/google-drive/rsa';
      const existsRes = await axios.get(`${apiBase}/registry/setup`, { params: { project_id: projectId } }).catch(() => ({ status: 404 }));
      const exists = existsRes.status === 200 && existsRes.data?.folderId;
      if (exists && !overwrite) {
        setShowSaveSetupOverwriteModal(true);
        return;
      }
      const setup = buildSetupPayload(validationResult);
      await axios.post(`${apiBase}/registry/setup`, { project_id: projectId, folder_id: folderId, setup });
      setWktInputLocked(true);
      showNotification('Setup saved to registry for this project.');
      setShowSaveSetupOverwriteModal(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save setup.';
      showNotification(msg);
    } finally {
      setSavingSetup(false);
    }
  }, [projectId, effectiveSetupFolderId, mapOverlayLocalConfig, useSystemGoogleDrive, buildSetupPayload, showNotification, validateWktInput, fetchProjectSetupFolderId]);

  const handleSaveWktClick = useCallback(async () => {
    const wkt = (mapOverlayLocalConfig?.wkt ?? '').trim();
    if (!wkt) {
      showNotification('Enter a WKT value before saving.');
      setWktInputLocked(false);
      return;
    }
    const validationResult = await validateWktInput(wkt, { silent: false });
    if (!validationResult?.valid || !validationResult?.willWork) {
      setWktInputLocked(false);
      return;
    }
    setWktInputLocked(true);
  }, [mapOverlayLocalConfig?.wkt, showNotification, validateWktInput]);

  const handleSaveSetupClick = useCallback(() => {
    setSavingSetup(true);
    performSaveSetup(false);
  }, [performSaveSetup]);

  const handleSaveSetupOverwriteConfirm = useCallback(() => {
    setShowSaveSetupOverwriteModal(false);
    setSavingSetup(true);
    performSaveSetup(true);
  }, [performSaveSetup]);

  const handleSaveSetupOverwriteCancel = useCallback(() => {
    setShowSaveSetupOverwriteModal(false);
  }, []);

  const lastLoadedSetupKeyRef = useRef(null);
  const [loadingSetupFromFolder, setLoadingSetupFromFolder] = useState(false);
  // Apply preloaded folder/setup when opening Analysis (fetched by ProjectView on project load)
  const appliedPreloadFolderKeyRef = useRef(null);
  const appliedPreloadSetupKeyRef = useRef(null);
  useEffect(() => {
    if (!projectId || !preloadedRsaSetupFolderId) return;
    const folderKey = `${preloadedRsaSetupFolderId}:${projectId}`;
    if (appliedPreloadFolderKeyRef.current !== folderKey) {
      appliedPreloadFolderKeyRef.current = folderKey;
      lastLoadedSetupKeyRef.current = folderKey;
      setMapOverlaySelectedFolders([{ id: preloadedRsaSetupFolderId, name: preloadedRsaSetupFolderName || undefined }]);
    }

    if (!preloadedRsaSetup || appliedPreloadSetupKeyRef.current === folderKey) return;
    appliedPreloadSetupKeyRef.current = folderKey;
    const setup = preloadedRsaSetup;
    setMapOverlayLocalConfig((prev) => ({
      ...prev,
      wkt: setup.wkt ?? prev?.wkt ?? '',
      extent: setup.extent ? { ...setup.extent } : prev?.extent,
      originXY: setup.originXY ? { ...setup.originXY } : prev?.originXY,
      originLatLng: setup.originLatLng ? { ...setup.originLatLng } : prev?.originLatLng,
      scaleLatPerUnit: setup.scaleLatPerUnit ?? prev?.scaleLatPerUnit,
      scaleLngPerUnit: setup.scaleLngPerUnit ?? prev?.scaleLngPerUnit,
      state: setup.state ?? prev?.state ?? '',
      routeNumber: setup.routeNumber ?? prev?.routeNumber ?? '',
      pathNameSegment: setup.pathNameSegment ?? prev?.pathNameSegment ?? '',
    }));
    setWktValidation(parseSetupWktValidation(setup));
    if (String(setup.wkt ?? '').trim()) {
      setWktInputLocked(true);
    }
  }, [projectId, preloadedRsaSetup, preloadedRsaSetupFolderId, preloadedRsaSetupFolderName]);

  // Load setup config (state, wkt) from MongoDB when project loads
  const mongoSetupLoadedRef = useRef(null);
  useEffect(() => {
    if (!projectId || mongoSetupLoadedRef.current === projectId) return;
    mongoSetupLoadedRef.current = projectId;
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const mongoUrl = `${baseUrl}/api/projects/${projectId}/rsa-lanes-mongo`;
    let cancelled = false;
    axios.get(mongoUrl).then((res) => {
      if (cancelled) return;
      const data = res.data;
      if (!data || !data.setupConfig) return;
      const cfg = data.setupConfig;
      setMapOverlayLocalConfig((prev) => ({
        ...prev,
        state: cfg.state || prev?.state || '',
        wkt: cfg.wkt || prev?.wkt || '',
      }));
    }).catch(() => { /* no mongo data */ });
    return () => { cancelled = true; };
  }, [projectId]);

  // Load setup from .rsa/setup.json for this project when user has selected exactly one map-overlay folder (System Drive only)
  useEffect(() => {
    if (!useSystemGoogleDrive || !projectId) return;
    const folders = mapOverlaySelectedFolders || [];
    if (folders.length === 0) {
      lastLoadedSetupKeyRef.current = null;
      return;
    }
    const folderId = folders.length === 1 ? (folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId) : null;
    const loadKey = folderId && projectId ? `${folderId}:${projectId}` : null;
    if (!loadKey || loadKey === lastLoadedSetupKeyRef.current) return;
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const apiBase = baseUrl ? `${API_BASE_URL}/api/google-drive/rsa` : '/api/google-drive/rsa';
    setLoadingSetupFromFolder(true);
    axios.get(`${apiBase}/setup`, { params: { folder_id: folderId, project_id: projectId } })
      .then((res) => {
        const setup = res.data?.setup;
        if (setup && typeof setup === 'object') {
          lastLoadedSetupKeyRef.current = loadKey;
          setMapOverlayLocalConfig((prev) => ({
            ...prev,
            wkt: setup.wkt ?? prev?.wkt ?? '',
            extent: setup.extent ? { ...setup.extent } : prev?.extent,
            originXY: setup.originXY ? { ...setup.originXY } : prev?.originXY,
            originLatLng: setup.originLatLng ? { ...setup.originLatLng } : prev?.originLatLng,
            scaleLatPerUnit: setup.scaleLatPerUnit ?? prev?.scaleLatPerUnit,
            scaleLngPerUnit: setup.scaleLngPerUnit ?? prev?.scaleLngPerUnit,
            state: setup.state ?? prev?.state ?? '',
            routeNumber: setup.routeNumber ?? prev?.routeNumber ?? '',
            pathNameSegment: setup.pathNameSegment ?? prev?.pathNameSegment ?? '',
          }));
          setWktValidation(parseSetupWktValidation(setup));
          if (String(setup.wkt ?? '').trim()) {
            setWktInputLocked(true);
          }
          showNotification('Project setup loaded from .rsa/setup.json.');
        }
      })
      .catch(() => { /* no setup for this project or error: keep current config */ })
      .finally(() => setLoadingSetupFromFolder(false));
  }, [projectId, mapOverlaySelectedFolders, useSystemGoogleDrive, showNotification]);

  // --- State for storage connection modal ---
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionProvider, setConnectionProvider] = useState(null);
  
  // --- State for main content tabs ---
  const [activeMainTab, setActiveMainTab] = useState(() => normalizeRsaMainTab(initialActiveMainTab));
  const [hasVisitedSetupTab, setHasVisitedSetupTab] = useState(() => normalizeRsaMainTab(initialActiveMainTab) === 'setup');
  const [routesSourceFolderId, setRoutesSourceFolderId] = useState('');
  const [routesFolderId, setRoutesFolderId] = useState('');
  const [modelFolderId, setModelFolderId] = useState('');
  const [routesTaskFoldersByCode, setRoutesTaskFoldersByCode] = useState({});
  const [routesXmlFiles, setRoutesXmlFiles] = useState(() => readRoutesXmlCache(effectiveProjectId));
  const [routesSourceFiles, setRoutesSourceFiles] = useState([]);
  const [routesStateByDriveId, setRoutesStateByDriveId] = useState({});
  const [modelFiles, setModelFiles] = useState([]);
  const [routesExistingNamesByTask, setRoutesExistingNamesByTask] = useState({});
  const [routesExistingXmlFilesByTask, setRoutesExistingXmlFilesByTask] = useState({});
  const [routesMatrixSelection, setRoutesMatrixSelection] = useState({});
  const [routesMatrixLoading, setRoutesMatrixLoading] = useState(false);
  const [routesMatrixSaving, setRoutesMatrixSaving] = useState(false);
  const [showRoutesUploadModal, setShowRoutesUploadModal] = useState(false);
  const [showModelUploadModal, setShowModelUploadModal] = useState(false);
  const [existingSourceFolderId, setExistingSourceFolderId] = useState('');
  const [existingSourceFiles, setExistingSourceFiles] = useState([]);
  const [existingSourceLoading, setExistingSourceLoading] = useState(false);
  const [showExistingSourceUploadModal, setShowExistingSourceUploadModal] = useState(false);
  const [routesMatrixError, setRoutesMatrixError] = useState('');
  const [routesStatusExpanded, setRoutesStatusExpanded] = useState(true);
  const [modelStatusExpanded, setModelStatusExpanded] = useState(true);
  const [existingSourceStatusExpanded, setExistingSourceStatusExpanded] = useState(true);
  const [uploadTableSelection, setUploadTableSelection] = useState({ routes: [], model: [], source: [] });
  const [uploadFileDeleteCandidate, setUploadFileDeleteCandidate] = useState(null);
  const [uploadFileDeletePendingIds, setUploadFileDeletePendingIds] = useState([]);
  const [deliverablesError, setDeliverablesError] = useState('');
  const [deliverablesFolderInfo, setDeliverablesFolderInfo] = useState({ driveId: '', treeId: '', name: 'Deliverables' });
  const [deliverablesFolderResolving, setDeliverablesFolderResolving] = useState(false);
  const [deliverablesSelectedFolder, setDeliverablesSelectedFolder] = useState(null);
  const [deliverablesCurrentPath, setDeliverablesCurrentPath] = useState([]);
  const [showDeliverablesUploadModal, setShowDeliverablesUploadModal] = useState(false);
  const [deliverablesSelectionState, setDeliverablesSelectionState] = useState({
    selectedFiles: [],
    selectedCount: 0,
    hasSelectedFiles: false,
    canDeleteSelection: false,
  });
  const deliverablesExplorerRef = useRef(null);
  const routesMatrixHasLocalChangesRef = useRef(false);
  const { permissions: userPermissions, loading: permissionsLoading } = useUserPermissions();
  const DEVELOPER_TABS = ['setup', 'paths', 'dashboard'];

  useEffect(() => {
    routesMatrixHasLocalChangesRef.current = false;
    setRoutesSourceFolderId('');
    setRoutesFolderId('');
    setModelFolderId('');
    setRoutesTaskFoldersByCode({});
    setRoutesExistingNamesByTask({});
    setRoutesExistingXmlFilesByTask({});
    setRoutesMatrixSelection({});
    setRoutesMatrixError('');
    setRoutesXmlFiles(readRoutesXmlCache(effectiveProjectId));
    setRoutesSourceFiles([]);
    setModelFiles([]);
    setRoutesStatusExpanded(true);
    setModelStatusExpanded(true);
    setUploadTableSelection({ routes: [], model: [] });
    setUploadFileDeleteCandidate(null);
    setUploadFileDeletePendingIds([]);
    setDeliverablesError('');
    setDeliverablesFolderInfo({ driveId: '', treeId: '', name: 'Deliverables' });
    setDeliverablesFolderResolving(false);
    setDeliverablesSelectedFolder(null);
    setDeliverablesCurrentPath([]);
    setShowDeliverablesUploadModal(false);
    setDeliverablesSelectionState({
      selectedFiles: [],
      selectedCount: 0,
      hasSelectedFiles: false,
      canDeleteSelection: false,
    });
  }, [effectiveProjectId]);

  useEffect(() => {
    const xmlRouteFiles = (Array.isArray(routesSourceFiles) ? routesSourceFiles : [])
      .filter((file) => isXmlFileName(file?.name) && normalizeDriveFolderId(file?.googleDriveId));

    if (xmlRouteFiles.length === 0) {
      setRoutesStateByDriveId({});
      return;
    }

    let cancelled = false;
    const isProductionDomain = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;

    Promise.all(xmlRouteFiles.map(async (file) => {
      const driveId = normalizeDriveFolderId(file?.googleDriveId);
      if (!driveId) {
        return null;
      }

      try {
        const mediaUrl = baseUrl
          ? `${API_BASE_URL}/api/google-drive/file/${encodeURIComponent(driveId)}/media`
          : `/api/google-drive/file/${encodeURIComponent(driveId)}/media`;
        const response = await axios.get(mediaUrl, { responseType: 'text' });
        const xmlText = typeof response?.data === 'string' ? response.data : '';
        return [driveId, readStateFromXml(xmlText)];
      } catch (error) {
        console.warn(`Failed to read XML state for ${file?.name || driveId}:`, error);
        return [driveId, ''];
      }
    })).then((entries) => {
      if (cancelled) {
        return;
      }

      const nextStateByDriveId = {};
      entries.forEach((entry) => {
        if (!Array.isArray(entry) || entry.length < 2) {
          return;
        }
        const [driveId, stateValue] = entry;
        if (driveId) {
          nextStateByDriveId[driveId] = stateValue || '';
        }
      });
      setRoutesStateByDriveId(nextStateByDriveId);
    });

    return () => {
      cancelled = true;
    };
  }, [routesSourceFiles]);

  useEffect(() => {
    if (!isRsaModelProject || activeMainTab !== 'deliverables' || !effectiveProjectId) {
      return undefined;
    }

    let cancelled = false;
    setDeliverablesFolderResolving(true);
    setDeliverablesError('');
    resolveDeliverablesFolderForProject(
      effectiveProjectId,
      Array.isArray(projectFilesSnapshot) && projectFilesSnapshot.length > 0 ? projectFilesSnapshot : null
    )
      .then((resolvedInfo) => {
        if (cancelled) return;
        setDeliverablesFolderInfo({
          driveId: String(resolvedInfo?.driveId ?? '').trim(),
          treeId: String(resolvedInfo?.treeId ?? '').trim(),
          name: String(resolvedInfo?.name ?? 'Deliverables').trim() || 'Deliverables',
        });
        if (!resolvedInfo?.driveId && !resolvedInfo?.treeId) {
          setDeliverablesError('Deliverables folder could not be resolved for this project.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDeliverablesFolderInfo({ driveId: '', treeId: '', name: 'Deliverables' });
        setDeliverablesError('Failed to resolve Deliverables folder for this project.');
      })
      .finally(() => {
        if (!cancelled) {
          setDeliverablesFolderResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeMainTab, effectiveProjectId, isRsaModelProject, projectFilesSnapshot]);

  const effectiveDeliverablesFolderDriveId = String(deliverablesFolderInfo.driveId ?? '').trim();
  const effectiveDeliverablesFolderTreeId = String(
    deliverablesFolderInfo.treeId || (effectiveDeliverablesFolderDriveId ? `gd_${effectiveDeliverablesFolderDriveId}` : '')
  ).trim();

  const deliverablesScopedRootFolder = useMemo(() => {
    if (!effectiveDeliverablesFolderTreeId && !effectiveDeliverablesFolderDriveId) {
      return null;
    }
    return {
      id: effectiveDeliverablesFolderTreeId || `gd_${effectiveDeliverablesFolderDriveId}`,
      googleDriveId: effectiveDeliverablesFolderDriveId || null,
      name: deliverablesFolderInfo.name || 'Deliverables',
    };
  }, [deliverablesFolderInfo.name, effectiveDeliverablesFolderDriveId, effectiveDeliverablesFolderTreeId]);

  useEffect(() => {
    if (!isRsaModelProject || activeMainTab !== 'deliverables' || !deliverablesScopedRootFolder) {
      return;
    }
    setDeliverablesSelectedFolder({
      id: deliverablesScopedRootFolder.id,
      name: deliverablesScopedRootFolder.name,
    });
    setDeliverablesCurrentPath([{ id: deliverablesScopedRootFolder.id, name: deliverablesScopedRootFolder.name }]);
  }, [activeMainTab, deliverablesScopedRootFolder, isRsaModelProject]);

  useEffect(() => {
    const normalizedInitialTab = normalizeRsaMainTab(initialActiveMainTab);
    setActiveMainTab((currentTab) => (
      currentTab === normalizedInitialTab ? currentTab : normalizedInitialTab
    ));
  }, [initialActiveMainTab]);

  useEffect(() => {
    if (activeMainTab === 'setup') {
      setHasVisitedSetupTab(true);
    }
  }, [activeMainTab]);

  useEffect(() => {
    if (typeof onActiveMainTabChange === 'function') {
      onActiveMainTabChange(activeMainTab);
    }
  }, [activeMainTab, onActiveMainTabChange]);
  const hasDevelopersGroup =
    Array.isArray(userPermissions?.groups) &&
    userPermissions.groups.some((g) => String(g).trim().toLowerCase() === 'developers');
  // Non-developers must not land on Setup/Paths/Dashboard on rsa-existing projects - switch to Analysis tab.
  // RSA model projects use a wizard where all tabs are user-facing, so this restriction does not apply there.
  useEffect(() => {
    if (permissionsLoading || !userPermissions) return;
    if (isRsaModelProject) return;
    if (!hasDevelopersGroup && DEVELOPER_TABS.includes(activeMainTab)) {
      setActiveMainTab('analysis');
    }
  }, [permissionsLoading, userPermissions, hasDevelopersGroup, activeMainTab, isRsaModelProject]);

  // RSA model projects expose Setup + Routes + Analysis + Dashboard tabs.
  useEffect(() => {
    if (!isRsaModelProject) return;
    if (!RSA_MODEL_ALLOWED_MAIN_TABS.includes(activeMainTab)) {
      setActiveMainTab('upload');
    }
  }, [isRsaModelProject, activeMainTab]);

  const routesTabTaskOptions = useMemo(
    () => (Array.isArray(visibleTaskOptions)
      ? visibleTaskOptions.filter((taskOption) => Boolean(TASK_OPTION_TO_FOLDER_CODE[taskOption]))
      : []),
    [visibleTaskOptions]
  );

  const toggleRoutesMatrixCell = useCallback((routeFileDriveId, taskOption) => {
    if (!routeFileDriveId || !taskOption) return;
    routesMatrixHasLocalChangesRef.current = true;
    setRoutesMatrixSelection((prev) => {
      const row = prev[routeFileDriveId] || {};
      return {
        ...prev,
        [routeFileDriveId]: {
          ...row,
          [taskOption]: !Boolean(row[taskOption]),
        },
      };
    });
  }, []);

  const loadRoutesTabData = useCallback(async ({ preserveLocalSelection = true, manageLoadingState = true } = {}) => {
    if (!effectiveProjectId) {
      setRoutesSourceFolderId('');
      setRoutesFolderId('');
      setModelFolderId('');
      setRoutesTaskFoldersByCode({});
      setRoutesXmlFiles([]);
      setRoutesSourceFiles([]);
      setModelFiles([]);
      writeRoutesXmlCache(effectiveProjectId, []);
      setRoutesExistingNamesByTask({});
      setRoutesExistingXmlFilesByTask({});
      setRoutesMatrixSelection({});
      setRoutesMatrixError('');
      return;
    }

    if (manageLoadingState) {
      setRoutesMatrixLoading(true);
    }
    setRoutesMatrixError('');
    try {
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const cacheBust = `_ts=${Date.now()}`;
      const filesUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
        : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;

      const filesResponse = await axios.get(filesUrl);
      const files = filesResponse?.data?.files || [];
      const {
        hasSourceFolder,
        sourceGoogleDriveIds,
      } = deriveTaskMetadataFromProjectFiles(files);

      if (!hasSourceFolder || !Array.isArray(sourceGoogleDriveIds) || sourceGoogleDriveIds.length === 0) {
        setRoutesSourceFolderId('');
        setRoutesFolderId('');
        setModelFolderId('');
        setRoutesTaskFoldersByCode({});
        setRoutesXmlFiles([]);
        setRoutesSourceFiles([]);
        setModelFiles([]);
        writeRoutesXmlCache(effectiveProjectId, []);
        setRoutesExistingNamesByTask({});
        setRoutesExistingXmlFilesByTask({});
        setRoutesMatrixSelection({});
        setRoutesMatrixError('Source folder is missing or not connected to Google Drive.');
        return;
      }

      const sourceChildrenResponses = await Promise.all(
        sourceGoogleDriveIds.map((sourceFolderId) => {
          const childrenUrl = baseUrl
            ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
            : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
          return axios.get(childrenUrl).catch(() => ({ data: { items: [] } }));
        })
      );

      const sourceContexts = sourceGoogleDriveIds.map((sourceFolderId, idx) => {
        const items = sourceChildrenResponses[idx]?.data?.items || [];
        const childFolders = items.filter((item) => isFolderItem(item));
        const routesFolder = childFolders.find(
          (folder) => normalizeFolderName(getFileName(folder)) === 'ROUTES'
        ) || null;
        const modelFolder = childFolders.find(
          (folder) => normalizeFolderName(getFileName(folder)) === 'MODEL'
        ) || null;
        const taskFoldersByCode = {};

        for (const folder of childFolders) {
          const folderCode = normalizeFolderName(getFileName(folder));
          if (!TASK_FOLDER_CODE_TO_OPTION[folderCode]) {
            continue;
          }
          taskFoldersByCode[folderCode] = {
            id: getFileId(folder),
            name: getFileName(folder) || folderCode,
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(folder)),
          };
        }

        return {
          sourceFolderId: sourceFolderId,
          routesFolder,
          modelFolder,
          taskFoldersByCode,
        };
      });

      const selectedSourceContext = sourceContexts.find((ctx) => Boolean(ctx?.routesFolder)) || sourceContexts[0] || null;
      if (!selectedSourceContext) {
        setRoutesSourceFolderId('');
        setRoutesFolderId('');
        setModelFolderId('');
        setRoutesTaskFoldersByCode({});
        setRoutesXmlFiles([]);
        setRoutesSourceFiles([]);
        setModelFiles([]);
        writeRoutesXmlCache(effectiveProjectId, []);
        setRoutesExistingNamesByTask({});
        setRoutesExistingXmlFilesByTask({});
        setRoutesMatrixSelection({});
        setRoutesMatrixError('Could not resolve Source folder contents.');
        return;
      }

      const selectedSourceFolderId = normalizeDriveFolderId(selectedSourceContext.sourceFolderId);
      const selectedRoutesFolderId = normalizeDriveFolderId(
        getGoogleDriveId(selectedSourceContext.routesFolder)
      );
      const selectedModelFolderId = normalizeDriveFolderId(
        getGoogleDriveId(selectedSourceContext.modelFolder)
      );

      setRoutesSourceFolderId(selectedSourceFolderId);
      setModelFolderId(selectedModelFolderId);
      setRoutesTaskFoldersByCode(selectedSourceContext.taskFoldersByCode || {});

      if (!selectedRoutesFolderId) {
        setRoutesFolderId('');
        setRoutesXmlFiles([]);
        setRoutesSourceFiles([]);
        setModelFiles([]);
        writeRoutesXmlCache(effectiveProjectId, []);
        setRoutesExistingNamesByTask({});
        setRoutesExistingXmlFilesByTask({});
        setRoutesMatrixSelection({});
        setRoutesMatrixError('Routes folder was not found under Source.');
        return;
      }

      setRoutesFolderId(selectedRoutesFolderId);

      // --- Prefetch all sub-folders in one server-side parallel call ---
      const allFolderIds = [selectedRoutesFolderId];
      if (selectedModelFolderId) allFolderIds.push(selectedModelFolderId);
      for (const taskOption of routesTabTaskOptions) {
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const taskFolderId = normalizeDriveFolderId(
          selectedSourceContext?.taskFoldersByCode?.[folderCode]?.googleDriveId
        );
        if (taskFolderId) allFolderIds.push(taskFolderId);
      }
      const uniqueFolderIds = [...new Set(allFolderIds)];
      const prefetchUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-prefetch?root_ids=${encodeURIComponent(uniqueFolderIds.join(','))}&${cacheBust}`
        : `/api/projects/${effectiveProjectId}/google-drive-prefetch?root_ids=${encodeURIComponent(uniqueFolderIds.join(','))}&${cacheBust}`;
      const prefetchResponse = await axios.get(prefetchUrl).catch(() => ({ data: { folders: {} } }));
      const prefetchedFolders = prefetchResponse?.data?.folders || {};

      // --- Extract Routes contents from prefetch ---
      const routesItems = prefetchedFolders[selectedRoutesFolderId] || [];
      const routeXmlRows = routesItems
        .filter((item) => !isFolderItem(item) && isXmlFileName(getFileName(item)))
        .map((item) => ({
          id: getFileId(item),
          googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
          name: String(getFileName(item) || '').trim(),
          parentDriveId: normalizeDriveFolderId(item?.parentDriveId) || selectedRoutesFolderId,
          modified: String(item?.modified || '').trim(),
          created: String(item?.created || '').trim(),
        }))
        .filter((item) => item.googleDriveId && item.name)
        .sort((a, b) => a.name.localeCompare(b.name));
      const sourceRouteFiles = routesItems
        .filter((item) => !isFolderItem(item))
        .map((item) => ({
          id: getFileId(item),
          googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
          name: String(getFileName(item) || '').trim(),
          parentDriveId: normalizeDriveFolderId(item?.parentDriveId) || selectedRoutesFolderId,
          modified: String(item?.modified || '').trim(),
          created: String(item?.created || '').trim(),
        }))
        .filter((item) => item.googleDriveId && item.name && (isXmlFileName(item.name) || isDxfFileName(item.name)))
        .sort((a, b) => {
          const aTime = new Date(a.modified || a.created || 0).getTime();
          const bTime = new Date(b.modified || b.created || 0).getTime();
          if (aTime !== bTime) return bTime - aTime;
          return a.name.localeCompare(b.name);
        });

      // --- Extract Model contents from prefetch ---
      let nextModelFiles = [];
      if (selectedModelFolderId) {
        const modelItems = prefetchedFolders[selectedModelFolderId] || [];
        nextModelFiles = modelItems
          .filter((item) => !isFolderItem(item))
          .map((item) => ({
            id: getFileId(item),
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
            name: String(getFileName(item) || '').trim(),
            parentDriveId: normalizeDriveFolderId(item?.parentDriveId) || selectedModelFolderId,
            modified: String(item?.modified || '').trim(),
            created: String(item?.created || '').trim(),
          }))
          .filter((item) => item.googleDriveId && item.name)
          .sort((a, b) => {
            const aTime = new Date(a.modified || a.created || 0).getTime();
            const bTime = new Date(b.modified || b.created || 0).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return a.name.localeCompare(b.name);
          });
      }

      // --- Extract task folder contents from prefetch ---
      const existingNamesByTask = {};
      const existingXmlFilesByTask = {};
      for (const taskOption of routesTabTaskOptions) {
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const taskFolderId = normalizeDriveFolderId(
          selectedSourceContext?.taskFoldersByCode?.[folderCode]?.googleDriveId
        );
        if (!taskFolderId) {
          existingNamesByTask[taskOption] = new Set();
          existingXmlFilesByTask[taskOption] = [];
          continue;
        }

        const taskItems = prefetchedFolders[taskFolderId] || [];
        const existingXmlItems = taskItems
          .filter((item) => !isFolderItem(item) && isXmlFileName(getFileName(item)))
          .map((item) => ({
            id: getFileId(item),
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
            name: String(getFileName(item) || '').trim(),
            parentDriveId: normalizeDriveFolderId(item?.parentDriveId) || taskFolderId,
          }))
          .filter((item) => item.googleDriveId && item.name);
        const namesSet = new Set(
          existingXmlItems
            .map((item) => normalizeFileNameKey(item.name))
            .filter(Boolean)
        );
        existingXmlFilesByTask[taskOption] = existingXmlItems;
        existingNamesByTask[taskOption] = namesSet;
      }

      const nextSelection = {};
      for (const row of routeXmlRows) {
        const rowSelection = {};
        const fileNameKey = normalizeFileNameKey(row.name);
        for (const taskOption of routesTabTaskOptions) {
          rowSelection[taskOption] = Boolean(existingNamesByTask[taskOption]?.has(fileNameKey));
        }
        nextSelection[row.googleDriveId] = rowSelection;
      }

      setRoutesXmlFiles(routeXmlRows);
      setRoutesSourceFiles(sourceRouteFiles);
      setModelFiles(nextModelFiles);
      writeRoutesXmlCache(effectiveProjectId, routeXmlRows);
      setRoutesExistingNamesByTask(existingNamesByTask);
      setRoutesExistingXmlFilesByTask(existingXmlFilesByTask);
      setRoutesMatrixSelection((prev) => {
        if (!preserveLocalSelection || !routesMatrixHasLocalChangesRef.current) {
          return nextSelection;
        }

        const mergedSelection = { ...nextSelection };
        for (const [routeDriveId, previousRow] of Object.entries(prev || {})) {
          if (!mergedSelection[routeDriveId] || !previousRow || typeof previousRow !== 'object') {
            continue;
          }
          mergedSelection[routeDriveId] = {
            ...mergedSelection[routeDriveId],
            ...previousRow,
          };
        }
        return mergedSelection;
      });
      if (routeXmlRows.length === 0) {
        setRoutesMatrixError('No XML files were found in Source/Routes.');
      }
    } catch (error) {
      console.error('Failed to load Routes matrix data:', error);
      setRoutesSourceFolderId('');
      setRoutesFolderId('');
      setModelFolderId('');
      setRoutesTaskFoldersByCode({});
      setRoutesXmlFiles([]);
      setRoutesSourceFiles([]);
      setModelFiles([]);
      writeRoutesXmlCache(effectiveProjectId, []);
      setRoutesExistingNamesByTask({});
      setRoutesExistingXmlFilesByTask({});
      setRoutesMatrixSelection({});
      setRoutesMatrixError(error?.response?.data?.error || 'Failed to load Routes table.');
    } finally {
      if (manageLoadingState) {
        setRoutesMatrixLoading(false);
      }
    }
  }, [effectiveProjectId, routesTabTaskOptions]);

  const loadExistingSourceData = useCallback(async ({ manageLoadingState = true } = {}) => {
    if (isRsaModelProject || !effectiveProjectId) {
      setExistingSourceFolderId('');
      setExistingSourceFiles([]);
      return;
    }

    if (manageLoadingState) {
      setExistingSourceLoading(true);
    }

    try {
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const cacheBust = `_ts=${Date.now()}`;
      const filesUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
        : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;
      const filesResponse = await axios.get(filesUrl);
      const files = filesResponse?.data?.files || [];
      const { sourceGoogleDriveIds } = deriveTaskMetadataFromProjectFiles(files);
      const sourceFolderDriveId = normalizeDriveFolderId(sourceGoogleDriveIds?.[0]);

      if (!sourceFolderDriveId) {
        setExistingSourceFolderId('');
        setExistingSourceFiles([]);
        return;
      }

      const sourceContentsUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderDriveId)}&${cacheBust}`
        : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderDriveId)}&${cacheBust}`;
      const sourceContentsResponse = await axios.get(sourceContentsUrl);
      const nextSourceFiles = (sourceContentsResponse?.data?.items || [])
        .filter((item) => !isFolderItem(item))
        .map((item) => ({
          id: getFileId(item),
          googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
          name: String(getFileName(item) || '').trim(),
          parentDriveId: normalizeDriveFolderId(item?.parentDriveId) || sourceFolderDriveId,
          modified: String(item?.modified || '').trim(),
          created: String(item?.created || '').trim(),
        }))
        .filter((item) => item.googleDriveId && item.name && isLasLazFileName(item.name))
        .sort((a, b) => {
          const aTime = new Date(a.modified || a.created || 0).getTime();
          const bTime = new Date(b.modified || b.created || 0).getTime();
          if (aTime !== bTime) return bTime - aTime;
          return a.name.localeCompare(b.name);
        });

      setExistingSourceFolderId(sourceFolderDriveId);
      setExistingSourceFiles(nextSourceFiles);
    } catch (error) {
      console.error('Failed to load existing-project Source files:', error);
      setExistingSourceFolderId('');
      setExistingSourceFiles([]);
    } finally {
      if (manageLoadingState) {
        setExistingSourceLoading(false);
      }
    }
  }, [effectiveProjectId, isRsaModelProject]);

  useEffect(() => {
    if (!effectiveProjectId || isRsaModelProject) {
      setExistingSourceFolderId('');
      setExistingSourceFiles([]);
      setExistingSourceLoading(false);
      setShowExistingSourceUploadModal(false);
      setUploadTableSelection((prev) => ({ ...prev, source: [] }));
    }
  }, [effectiveProjectId, isRsaModelProject]);

  const saveRoutesMatrixSelection = useCallback(async () => {
    if (!effectiveProjectId) {
      showNotification('Select a project first.');
      return { ok: false, reason: 'missing_project' };
    }
    if (!routesFolderId) {
      showNotification('Routes folder was not found under Source.');
      return { ok: false, reason: 'missing_routes_folder' };
    }
    if (!Array.isArray(routesXmlFiles) || routesXmlFiles.length === 0) {
      showNotification('No route XML files found in Source/Routes.');
      return { ok: false, reason: 'missing_route_files' };
    }
    if (!Array.isArray(routesTabTaskOptions) || routesTabTaskOptions.length === 0) {
      showNotification('No analysis options are available for mapping.');
      return { ok: false, reason: 'missing_task_options' };
    }

    const groupedByDestination = {};
    const missingTaskFolders = new Set();
    const selectedNameKeysByTask = {};
    let skippedAlreadyExisting = 0;
    for (const taskOption of routesTabTaskOptions) {
      selectedNameKeysByTask[taskOption] = new Set();
    }

    for (const routeFile of routesXmlFiles) {
      const sourceDriveId = normalizeDriveFolderId(routeFile?.googleDriveId);
      if (!sourceDriveId) {
        continue;
      }
      const fileName = String(routeFile?.name || '').trim();
      if (!fileName) {
        continue;
      }
      const fileNameKey = normalizeFileNameKey(fileName);
      const rowSelection = routesMatrixSelection[sourceDriveId] || {};

      for (const taskOption of routesTabTaskOptions) {
        if (!rowSelection[taskOption]) {
          continue;
        }
        selectedNameKeysByTask[taskOption]?.add(fileNameKey);
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const destinationFolderId = normalizeDriveFolderId(
          routesTaskFoldersByCode?.[folderCode]?.googleDriveId
        );
        if (!destinationFolderId) {
          missingTaskFolders.add(taskOption);
          continue;
        }

        const existingNames = routesExistingNamesByTask[taskOption];
        if (existingNames instanceof Set && existingNames.has(fileNameKey)) {
          skippedAlreadyExisting += 1;
          continue;
        }

        if (!groupedByDestination[destinationFolderId]) {
          groupedByDestination[destinationFolderId] = {
            taskOptions: new Set(),
            itemsBySource: new Map(),
          };
        }
        groupedByDestination[destinationFolderId].taskOptions.add(taskOption);
        groupedByDestination[destinationFolderId].itemsBySource.set(sourceDriveId, {
          id: `gd_${sourceDriveId}`,
          googleDriveId: sourceDriveId,
          name: fileName,
          isGoogleDriveItem: true,
          isFolder: false,
          parentDriveId: routesFolderId,
        });
      }
    }

    const destinationEntries = Object.entries(groupedByDestination)
      .map(([destinationFolderId, group]) => ({
        destinationFolderId,
        taskOptions: Array.from(group.taskOptions),
        items: Array.from(group.itemsBySource.values()),
      }))
      .filter((entry) => entry.items.length > 0);

    const deleteEntries = routesTabTaskOptions
      .map((taskOption) => {
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const destinationFolderId = normalizeDriveFolderId(
          routesTaskFoldersByCode?.[folderCode]?.googleDriveId
        );
        if (!destinationFolderId) {
          return null;
        }

        const selectedNames = selectedNameKeysByTask[taskOption] || new Set();
        const existingXmlItems = Array.isArray(routesExistingXmlFilesByTask[taskOption])
          ? routesExistingXmlFilesByTask[taskOption]
          : [];
        const itemsToDelete = existingXmlItems
          .filter((item) => {
            const itemNameKey = normalizeFileNameKey(item?.name);
            if (!itemNameKey) return false;
            return !selectedNames.has(itemNameKey);
          })
          .map((item) => ({
            id: item.id || `gd_${item.googleDriveId}`,
            googleDriveId: item.googleDriveId,
            name: item.name,
            isFolder: false,
            folder_drive_id: destinationFolderId,
            parentDriveId: destinationFolderId,
          }));

        if (itemsToDelete.length === 0) {
          return null;
        }

        return {
          taskOption,
          destinationFolderId,
          items: itemsToDelete,
        };
      })
      .filter(Boolean);

    if (destinationEntries.length === 0 && deleteEntries.length === 0) {
      const missingHint = missingTaskFolders.size > 0
        ? ` Missing RSA folder(s): ${Array.from(missingTaskFolders).join(', ')}.`
        : '';
      if (skippedAlreadyExisting > 0) {
        showNotification(`No matrix changes detected. ${skippedAlreadyExisting} selected file(s) already exist in target folders.${missingHint}`);
      } else {
        showNotification(`No route-to-analysis changes to save.${missingHint}`);
      }
      return {
        ok: missingTaskFolders.size === 0,
        changed: false,
        skippedAlreadyExisting,
        missingTaskFolders: Array.from(missingTaskFolders),
      };
    }

    setRoutesMatrixSaving(true);
    try {
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const pasteUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files/paste`
        : `/api/projects/${effectiveProjectId}/files/paste`;
      const deleteUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-files/delete`
        : `/api/projects/${effectiveProjectId}/google-drive-files/delete`;

      const copyResults = await Promise.all(destinationEntries.map(async (entry) => {
        try {
          const response = await axios.post(pasteUrl, {
            operation: 'copy',
            destination_drive_id: entry.destinationFolderId,
            items: entry.items,
          });
          return {
            destinationFolderId: entry.destinationFolderId,
            taskOptions: entry.taskOptions,
            status: response.status,
            processedCount: Number(response?.data?.processed_count) || 0,
            errors: Array.isArray(response?.data?.errors) ? response.data.errors : [],
          };
        } catch (error) {
          return {
            destinationFolderId: entry.destinationFolderId,
            taskOptions: entry.taskOptions,
            status: error?.response?.status || 500,
            processedCount: Number(error?.response?.data?.processed_count) || 0,
            errors: Array.isArray(error?.response?.data?.errors)
              ? error.response.data.errors
              : [{ error: error?.response?.data?.error || error.message || 'Copy failed' }],
          };
        }
      }));
      const deleteResults = await Promise.all(deleteEntries.map(async (entry) => {
        try {
          const response = await axios.delete(deleteUrl, {
            data: {
              folder_drive_id: entry.destinationFolderId,
              items: entry.items,
            },
            headers: {
              'Content-Type': 'application/json',
            },
          });
          return {
            destinationFolderId: entry.destinationFolderId,
            taskOption: entry.taskOption,
            status: response.status,
            deletedCount: Number(response?.data?.deleted_count) || 0,
            errors: Array.isArray(response?.data?.errors) ? response.data.errors : [],
          };
        } catch (error) {
          return {
            destinationFolderId: entry.destinationFolderId,
            taskOption: entry.taskOption,
            status: error?.response?.status || 500,
            deletedCount: Number(error?.response?.data?.deleted_count) || 0,
            errors: Array.isArray(error?.response?.data?.errors)
              ? error.response.data.errors
              : [{ error: error?.response?.data?.error || error.message || 'Delete failed' }],
          };
        }
      }));

      const totalCopied = copyResults.reduce((sum, result) => sum + (Number(result.processedCount) || 0), 0);
      const totalDeleted = deleteResults.reduce((sum, result) => sum + (Number(result.deletedCount) || 0), 0);
      const copyErrors = copyResults.reduce((sum, result) => {
        const count = Array.isArray(result.errors) ? result.errors.length : 0;
        return sum + count;
      }, 0);
      const deleteErrors = deleteResults.reduce((sum, result) => {
        const count = Array.isArray(result.errors) ? result.errors.length : 0;
        return sum + count;
      }, 0);
      const totalErrors = copyErrors + deleteErrors;

      const syncedAt = Date.now();
      try {
        sessionStorage.setItem(`rsaMileageCachesSynced:${effectiveProjectId}`, String(syncedAt));
      } catch (e) {
        // ignore storage errors
      }
      window.dispatchEvent(new CustomEvent('rsa-mileage-caches-synced', {
        detail: { projectId: effectiveProjectId, syncedAt },
      }));

      routesMatrixHasLocalChangesRef.current = false;
      await loadRoutesTabData({ preserveLocalSelection: false });

      const missingHint = missingTaskFolders.size > 0
        ? ` Missing RSA folder(s): ${Array.from(missingTaskFolders).join(', ')}.`
        : '';
      if (totalErrors > 0) {
        showNotification(
          `Saved with warnings: copied ${totalCopied} file(s), deleted ${totalDeleted} file(s), ${totalErrors} error(s).${missingHint}`
        );
        return {
          ok: false,
          changed: true,
          totalCopied,
          totalDeleted,
          totalErrors,
          missingTaskFolders: Array.from(missingTaskFolders),
        };
      } else {
        const skippedHint = skippedAlreadyExisting > 0
          ? ` ${skippedAlreadyExisting} already existed and were skipped.`
          : '';
        showNotification(
          `Saved route assignments: copied ${totalCopied} file(s), deleted ${totalDeleted} file(s).${skippedHint}${missingHint}`
        );
        // Keep the user in the current routes tab instead of navigating away automatically.
        // setActiveMainTab('analysis'); -- removed to prevent unexpected tab switching.
        return {
          ok: missingTaskFolders.size === 0,
          changed: true,
          totalCopied,
          totalDeleted,
          totalErrors,
          skippedAlreadyExisting,
          missingTaskFolders: Array.from(missingTaskFolders),
        };
      }
    } catch (error) {
      console.error('Failed to save route assignments before run:', error);
      showNotification(
        `Failed to save route assignments: ${error?.response?.data?.error || error.message || 'Unknown error'}`
      );
      return {
        ok: false,
        changed: false,
        reason: 'unexpected_error',
        error,
      };
    } finally {
      setRoutesMatrixSaving(false);
    }
  }, [
    effectiveProjectId,
    routesFolderId,
    routesXmlFiles,
    routesTabTaskOptions,
    routesMatrixSelection,
    routesTaskFoldersByCode,
    routesExistingNamesByTask,
    routesExistingXmlFilesByTask,
    showNotification,
    loadRoutesTabData,
  ]);

  useEffect(() => {
    if (isRsaModelProject && activeMainTab !== 'routes') return;
    if (!isRsaModelProject && activeMainTab !== 'analysis') return;
    loadRoutesTabData();
  }, [isRsaModelProject, activeMainTab, loadRoutesTabData]);

  useEffect(() => {
    if (activeMainTab !== 'upload' || !effectiveProjectId) {
      return;
    }
    if (hasRoutesUploadStatusCache(effectiveProjectId)) {
      const cachedRouteFiles = readRoutesXmlCache(effectiveProjectId);
      setRoutesXmlFiles(cachedRouteFiles);
    }
    loadRoutesTabData();
  }, [activeMainTab, effectiveProjectId, loadRoutesTabData]);

  useEffect(() => {
    if (isRsaModelProject || activeMainTab !== 'upload' || !effectiveProjectId) {
      return;
    }
    setExistingSourceLoading(true);
    loadExistingSourceData();
  }, [isRsaModelProject, activeMainTab, effectiveProjectId, loadExistingSourceData]);

  const handleOpenRoutesUploadModal = useCallback(async () => {
    if (!routesFolderId) {
      await loadRoutesTabData();
    }
    setShowRoutesUploadModal(true);
  }, [routesFolderId, loadRoutesTabData]);

  const handleOpenModelUploadModal = useCallback(async () => {
    if (!modelFolderId) {
      await loadRoutesTabData();
    }
    setShowModelUploadModal(true);
  }, [modelFolderId, loadRoutesTabData]);

  const handleOpenExistingSourceUploadModal = useCallback(async () => {
    if (!existingSourceFolderId) {
      await loadExistingSourceData();
    }
    setShowExistingSourceUploadModal(true);
  }, [existingSourceFolderId, loadExistingSourceData]);

  const refreshRoutesTabDataAfterUpload = useCallback(async () => {
    const retryDelaysMs = [0, 700, 1600];
    setRoutesMatrixLoading(true);
    try {
      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        await loadRoutesTabData({ manageLoadingState: false });
      }
    } finally {
      setRoutesMatrixLoading(false);
    }
  }, [loadRoutesTabData]);

  const refreshExistingSourceDataAfterUpload = useCallback(async () => {
    const retryDelaysMs = [0, 700, 1600];
    setExistingSourceLoading(true);
    try {
      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        await loadExistingSourceData({ manageLoadingState: false });
      }
    } finally {
      setExistingSourceLoading(false);
    }
  }, [loadExistingSourceData]);

  const selectedRouteUploadFiles = useMemo(() => {
    const selectedIds = new Set((uploadTableSelection.routes || []).map((id) => normalizeDriveFolderId(id)).filter(Boolean));
    return (Array.isArray(routesSourceFiles) ? routesSourceFiles : []).filter((file) =>
      selectedIds.has(normalizeDriveFolderId(file?.googleDriveId))
    );
  }, [routesSourceFiles, uploadTableSelection.routes]);

  const selectedModelUploadFiles = useMemo(() => {
    const selectedIds = new Set((uploadTableSelection.model || []).map((id) => normalizeDriveFolderId(id)).filter(Boolean));
    return (Array.isArray(modelFiles) ? modelFiles : []).filter((file) =>
      selectedIds.has(normalizeDriveFolderId(file?.googleDriveId))
    );
  }, [modelFiles, uploadTableSelection.model]);

  const selectedExistingSourceUploadFiles = useMemo(() => {
    const selectedIds = new Set((uploadTableSelection.source || []).map((id) => normalizeDriveFolderId(id)).filter(Boolean));
    return (Array.isArray(existingSourceFiles) ? existingSourceFiles : []).filter((file) =>
      selectedIds.has(normalizeDriveFolderId(file?.googleDriveId))
    );
  }, [existingSourceFiles, uploadTableSelection.source]);

  const uploadDeleteBusy = uploadFileDeletePendingIds.length > 0;

  const setUploadTableSelectionForScope = useCallback((scope, nextIds) => {
    setUploadTableSelection((prev) => ({
      ...prev,
      [scope]: Array.from(new Set((Array.isArray(nextIds) ? nextIds : []).map((id) => normalizeDriveFolderId(id)).filter(Boolean))),
    }));
  }, []);

  const toggleUploadTableFileSelection = useCallback((scope, file) => {
    if (uploadDeleteBusy) return;
    const driveFileId = normalizeDriveFolderId(file?.googleDriveId);
    if (!driveFileId) return;
    setUploadTableSelection((prev) => {
      const current = Array.isArray(prev?.[scope]) ? prev[scope] : [];
      const exists = current.includes(driveFileId);
      return {
        ...prev,
        [scope]: exists
          ? current.filter((id) => id !== driveFileId)
          : [...current, driveFileId],
      };
    });
  }, [uploadDeleteBusy]);

  const handleToggleAllUploadTableFiles = useCallback((scope, files) => {
    if (uploadDeleteBusy) return;
    const normalizedIds = (Array.isArray(files) ? files : [])
      .map((file) => normalizeDriveFolderId(file?.googleDriveId))
      .filter(Boolean);
    if (normalizedIds.length === 0) {
      setUploadTableSelectionForScope(scope, []);
      return;
    }
    setUploadTableSelection((prev) => {
      const current = Array.isArray(prev?.[scope]) ? prev[scope] : [];
      const allSelected = normalizedIds.every((id) => current.includes(id));
      return {
        ...prev,
        [scope]: allSelected ? [] : normalizedIds,
      };
    });
  }, [setUploadTableSelectionForScope, uploadDeleteBusy]);

  const handleRequestUploadFileDelete = useCallback((files, folderDriveId, folderLabel) => {
    const normalizedFiles = (Array.isArray(files) ? files : [files])
      .filter(Boolean)
      .map((file) => ({
        ...file,
        googleDriveId: normalizeDriveFolderId(file?.googleDriveId),
        isFolder: false,
      }))
      .filter((file) => file.googleDriveId);

    if (!effectiveProjectId || normalizedFiles.length === 0) {
      showNotification('Could not remove file: missing Google Drive file ID.');
      return;
    }

    setUploadFileDeleteCandidate({
      files: normalizedFiles,
      folderDriveId: normalizeDriveFolderId(folderDriveId || normalizedFiles[0]?.parentDriveId),
      folderLabel: String(folderLabel || 'Source folder').trim() || 'Source folder',
    });
  }, [effectiveProjectId, showNotification]);

  const removeUploadFilesFromLocalState = useCallback((files) => {
    const driveFileIds = new Set(
      (Array.isArray(files) ? files : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean)
    );

    if (driveFileIds.size === 0) {
      return;
    }

    setRoutesSourceFiles((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (item) => !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId))
      )
    );

    setModelFiles((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (item) => !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId))
      )
    );

    setExistingSourceFiles((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (item) => !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId))
      )
    );

    setRoutesXmlFiles((prev) => {
      const next = (Array.isArray(prev) ? prev : []).filter(
        (item) => !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId))
      );
      writeRoutesXmlCache(effectiveProjectId, next);
      return next;
    });

    setRoutesMatrixSelection((prev) => {
      if (!prev || typeof prev !== 'object') {
        return prev;
      }
      const next = { ...prev };
      driveFileIds.forEach((driveFileId) => {
        delete next[driveFileId];
      });
      return next;
    });

    setUploadTableSelection((prev) => ({
      ...prev,
      routes: (Array.isArray(prev?.routes) ? prev.routes : []).filter((id) => !driveFileIds.has(normalizeDriveFolderId(id))),
      model: (Array.isArray(prev?.model) ? prev.model : []).filter((id) => !driveFileIds.has(normalizeDriveFolderId(id))),
      source: (Array.isArray(prev?.source) ? prev.source : []).filter((id) => !driveFileIds.has(normalizeDriveFolderId(id))),
    }));
  }, [effectiveProjectId]);

  const handleConfirmUploadFileDelete = useCallback(async () => {
    const candidate = uploadFileDeleteCandidate;
    const candidateFiles = Array.isArray(candidate?.files) ? candidate.files : [];
    const driveFileIds = candidateFiles
      .map((file) => normalizeDriveFolderId(file?.googleDriveId))
      .filter(Boolean);
    if (!effectiveProjectId || candidateFiles.length === 0 || driveFileIds.length === 0) {
      return;
    }

    setUploadFileDeletePendingIds(driveFileIds);
    try {
      await fileService.deleteGoogleDriveFiles(
        effectiveProjectId,
        candidateFiles,
        candidate.folderDriveId || candidateFiles[0]?.parentDriveId || null
      );
      removeUploadFilesFromLocalState(candidateFiles);
      if (candidateFiles.length === 1) {
        showNotification(`Removed ${candidateFiles[0].name} from ${candidate.folderLabel}.`, 2000);
      } else {
        showNotification(`Removed ${candidateFiles.length} files from ${candidate.folderLabel}.`, 2000);
      }
    } catch (error) {
      console.error('Failed to remove upload-step file:', error);
      showNotification(
        error?.message
          || `Failed to remove ${candidateFiles.length === 1 ? 'file' : 'files'} from ${candidate.folderLabel}.`
      );
    } finally {
      setUploadFileDeletePendingIds([]);
      setUploadFileDeleteCandidate(null);
    }
  }, [
    effectiveProjectId,
    removeUploadFilesFromLocalState,
    showNotification,
    uploadFileDeleteCandidate,
  ]);

  useEffect(() => {
    const validRouteIds = new Set(
      (Array.isArray(routesSourceFiles) ? routesSourceFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean)
    );
    const validModelIds = new Set(
      (Array.isArray(modelFiles) ? modelFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean)
    );
    const validSourceIds = new Set(
      (Array.isArray(existingSourceFiles) ? existingSourceFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean)
    );
    setUploadTableSelection((prev) => {
      const nextRoutes = (Array.isArray(prev?.routes) ? prev.routes : []).filter((id) => validRouteIds.has(normalizeDriveFolderId(id)));
      const nextModel = (Array.isArray(prev?.model) ? prev.model : []).filter((id) => validModelIds.has(normalizeDriveFolderId(id)));
      const nextSource = (Array.isArray(prev?.source) ? prev.source : []).filter((id) => validSourceIds.has(normalizeDriveFolderId(id)));
      const sameRoutes = nextRoutes.length === (Array.isArray(prev?.routes) ? prev.routes.length : 0);
      const sameModel = nextModel.length === (Array.isArray(prev?.model) ? prev.model.length : 0);
      const sameSource = nextSource.length === (Array.isArray(prev?.source) ? prev.source.length : 0);
      if (sameRoutes && sameModel && sameSource) {
        return prev;
      }
      return {
        ...prev,
        routes: nextRoutes,
        model: nextModel,
        source: nextSource,
      };
    });
  }, [routesSourceFiles, modelFiles, existingSourceFiles]);

  useEffect(() => {
    setRoutesMatrixSelection((prev) => {
      const next = {};
      for (const routeFile of routesXmlFiles) {
        const rowKey = normalizeDriveFolderId(routeFile?.googleDriveId);
        if (!rowKey) continue;
        const previousRow = prev[rowKey] || {};
        const nextRow = {};
        for (const taskOption of routesTabTaskOptions) {
          nextRow[taskOption] = Boolean(previousRow[taskOption]);
        }
        next[rowKey] = nextRow;
      }
      return next;
    });
  }, [routesXmlFiles, routesTabTaskOptions]);

  useEffect(() => {
    setHasSubmittedRsaRun(false);
  }, [effectiveProjectId, selectedTasks]);
  
  // --- State for Dashboard ---
  const [dashboardSelectedRun, setDashboardSelectedRun] = useState(null);
  const [optimisticRuns, setOptimisticRuns] = useState([]);
  const [pendingDashboardRunId, setPendingDashboardRunId] = useState(null);
  const [pendingLocalRunId, setPendingLocalRunId] = useState(null);
  const [dashboardDeletingRunId, setDashboardDeletingRunId] = useState(null);
  const [dashboardDeleteCandidateRunId, setDashboardDeleteCandidateRunId] = useState(null);

  // Connect Dashboard to backend/Redis: load user runs and build projects from runs
  const dashboardUserEmail = sessionStorage.getItem('userEmail') || null;
  const {
    runs: apiRuns,
    loading: dashboardLoading,
    error: dashboardError,
    refresh: refreshDashboardRuns,
    removeRun: removeDashboardRun,
    runProgress: dashboardRunProgress
  } = useUserRuns(dashboardUserEmail, {
    autoRefresh: true,
    refreshInterval: 60000,
    connectToActiveStreams: false,
    projectType: normalizedProjectType || null,
  });

  // Merge API runs with optimistic runs (optimistic = just submitted; dedupe by run_id, API wins)
  const displayRuns = useMemo(() => {
    const apiRunIds = new Set((apiRuns || []).map((r) => r.run_id));
    const onlyOptimistic = (optimisticRuns || []).filter((o) => !apiRunIds.has(o.run_id));
    const merged = [...(apiRuns || []), ...onlyOptimistic];
    merged.sort((a, b) => {
      const tA = a.last_updated?.getTime() ?? a.created_at?.getTime() ?? 0;
      const tB = b.last_updated?.getTime() ?? b.created_at?.getTime() ?? 0;
      return tB - tA;
    });
    return merged;
  }, [apiRuns, optimisticRuns]);

  // Backward compatibility: alias for any leftover references (e.g. hot-reload)
  const dashboardMockRuns = displayRuns;

  // Keep selected run synced with latest run object coming from live updates.
  useEffect(() => {
    if (!dashboardSelectedRun?.run_id) return;
    const latestSelected = displayRuns.find((r) => r.run_id === dashboardSelectedRun.run_id);
    if (latestSelected && latestSelected !== dashboardSelectedRun) {
      setDashboardSelectedRun(latestSelected);
    }
  }, [dashboardSelectedRun, displayRuns]);

  // RSA-model launch UX: after launching a run, select it once it appears in the list.
  // Do not force displayRuns[0] when the user picks another run - that was resetting selection.
  useEffect(() => {
    if (!isRsaModelProject || activeMainTab !== 'dashboard') return;
    if (!Array.isArray(displayRuns) || displayRuns.length === 0) return;

    if (pendingDashboardRunId) {
      const pendingRun = displayRuns.find((r) => r.run_id === pendingDashboardRunId);
      if (pendingRun) {
        if (!dashboardSelectedRun || dashboardSelectedRun.run_id !== pendingRun.run_id) {
          setDashboardSelectedRun(pendingRun);
        }
        setPendingDashboardRunId(null);
      }
      return;
    }

    if (!dashboardSelectedRun?.run_id) {
      setDashboardSelectedRun(displayRuns[0]);
      return;
    }
    const stillThere = displayRuns.some((r) => r.run_id === dashboardSelectedRun.run_id);
    if (!stillThere) {
      setDashboardSelectedRun(displayRuns[0]);
    }
  }, [
    isRsaModelProject,
    activeMainTab,
    displayRuns,
    pendingDashboardRunId,
    dashboardSelectedRun,
  ]);

  // Build project tiles from runs (group by project_id)
  const dashboardProjectsData = useMemo(() => {
    const byProject = new Map();
    for (const run of displayRuns) {
      const id = run.project_id ?? 'global';
      if (!byProject.has(id)) {
        byProject.set(id, {
          id,
          name: run.project_name ?? run.name ?? `Project ${id}`,
          description: `Pipeline runs for project ${id}`,
          total_analyses: 0,
          completed_analyses: 0,
          running_analyses: 0,
          last_updated: run.last_updated ?? run.created_at ?? new Date(),
        });
      }
      const p = byProject.get(id);
      p.total_analyses += 1;
      if (run.status === 'completed') p.completed_analyses += 1;
      if (run.status === 'processing' || run.status === 'running') p.running_analyses += 1;
      const runTime = run.last_updated?.getTime() ?? run.created_at?.getTime();
      if (runTime && runTime > (p.last_updated?.getTime() ?? 0)) p.last_updated = run.last_updated ?? run.created_at;
    }
    return Array.from(byProject.values()).sort((a, b) => (b.last_updated?.getTime() ?? 0) - (a.last_updated?.getTime() ?? 0));
  }, [displayRuns]);

  const handleBrowseDeliverables = useCallback(() => {
    if (typeof onGoToFiles !== 'function') {
      return;
    }

    if (!isRsaModelProject) {
      onGoToFiles();
      return;
    }

    if (effectiveDeliverablesFolderTreeId) {
      onGoToFiles({ id: effectiveDeliverablesFolderTreeId, name: deliverablesFolderInfo.name || 'Deliverables' });
      return;
    }

    onGoToFiles();
  }, [deliverablesFolderInfo.name, effectiveDeliverablesFolderTreeId, isRsaModelProject, onGoToFiles]);

  const previousDeliverablesBreadcrumbStep = useMemo(
    () => (deliverablesCurrentPath.length > 1
      ? deliverablesCurrentPath[deliverablesCurrentPath.length - 2]
      : null),
    [deliverablesCurrentPath]
  );

  const previousDeliverablesBreadcrumbTooltip = previousDeliverablesBreadcrumbStep
    ? `Back to ${previousDeliverablesBreadcrumbStep.name || 'previous folder'}`
    : 'Back';

  const handleDeliverablesRefresh = useCallback(() => {
    deliverablesExplorerRef.current?.handleRefresh?.();
  }, []);

  const handleDeliverablesDownload = useCallback((e) => {
    e.stopPropagation();
    deliverablesExplorerRef.current?.handleDownload?.();
  }, []);

  const handleDeliverablesDelete = useCallback((e) => {
    e.stopPropagation();
    deliverablesExplorerRef.current?.handleDelete?.();
  }, []);

  const handleDeliverablesBreadcrumbClick = useCallback((index, e) => {
    e.stopPropagation();
    const nextPath = deliverablesCurrentPath.slice(0, index + 1);
    if (nextPath.length === 0) {
      return;
    }
    setDeliverablesCurrentPath(nextPath);
    const targetStep = nextPath[nextPath.length - 1];
    setDeliverablesSelectedFolder({
      id: targetStep.id,
      name: targetStep.name,
    });
  }, [deliverablesCurrentPath]);

  const handleDeliverablesBack = useCallback((e) => {
    e.stopPropagation();
    if (!previousDeliverablesBreadcrumbStep) {
      return;
    }
    setDeliverablesSelectedFolder({
      id: previousDeliverablesBreadcrumbStep.id,
      name: previousDeliverablesBreadcrumbStep.name,
    });
  }, [previousDeliverablesBreadcrumbStep]);

  // Prune optimistic runs older than 5 min (API should have picked them up)
  useEffect(() => {
    if (optimisticRuns.length === 0) return;
    const id = setInterval(() => setOptimisticRuns((prev) => prev.filter((o) => {
      const age = Date.now() - (o.created_at?.getTime() ?? o.last_updated?.getTime() ?? 0);
      return age < 5 * 60 * 1000;
    })), 60 * 1000);
    return () => clearInterval(id);
  }, [optimisticRuns.length]);

  // Legacy mock data (kept for reference; Dashboard now uses displayRuns + dashboardProjectsData)
  const dashboardMockAnalysesData = [
    { 
      id: 'analysis_123',
      project_name: 'Highway LiDAR Analysis',
      project_id: 'project_789',
      status: 'running',
      start_time: new Date(Date.now() - 2 * 60 * 60 * 1000),
      progress: { percentage: 65, completed_steps: 3, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'running', progress: 75, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis (EMB)', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_456',
      project_name: 'Urban Road Analysis',
      project_id: 'project_789',
      status: 'completed',
      start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'completed', progress: 100, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'completed', progress: 100, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis (EMB)', status: 'completed', progress: 100, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'completed', progress: 100, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_789',
      project_name: 'Bridge Analysis Project',
      project_id: 'project_452',
      status: 'running',
      start_time: new Date(Date.now() - 30 * 60 * 1000),
      progress: { percentage: 25, completed_steps: 1, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'running', progress: 50, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'pending', progress: 0, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis (EMB)', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    { 
      id: 'analysis_486',
      project_name: 'West Kingman Interchange Analysis',
      project_id: 'project_486',
      status: 'pending',
      start_time: new Date(Date.now() - 15 * 60 * 1000),
      progress: { percentage: 0, completed_steps: 0, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'pending', progress: 0, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'pending', progress: 0, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'pending', progress: 0, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis (EMB)', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_416',
      project_name: 'SR 516 Lake-Orange County Analysis',
      project_id: 'project_416',
      status: 'completed',
      start_time: new Date(Date.now() - 6 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'completed', progress: 100, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'completed', progress: 100, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis (EMB)', status: 'completed', progress: 100, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'completed', progress: 100, description: 'Generating final analysis report' }
      ]
    }
  ];
  
  const dashboardMockProjectsData = [
    { id: 'project_789', name: 'CFX SR-538 Poinciana Pkwy', description: 'Comprehensive analysis of highway sections for maintenance planning', total_analyses: 2, completed_analyses: 1, running_analyses: 1, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_452', name: 'ADOT Climbing Passing RSA', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 1, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_486', name: 'ADOT West Kingman Interchange', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 0, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_416', name: 'CFX SR 516 Lake-Orange County', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 1, running_analyses: 0, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_521', name: 'CFX SR-538 Poinciana Pkwy', description: 'Comprehensive analysis of highway sections for maintenance planning', total_analyses: 3, completed_analyses: 2, running_analyses: 1, last_updated: new Date(Date.now() - 45 * 60 * 1000) },
    { id: 'project_623', name: 'ADOT Climbing Passing RSA', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 2, completed_analyses: 1, running_analyses: 1, last_updated: new Date(Date.now() - 20 * 60 * 1000) },
    { id: 'project_734', name: 'ADOT West Kingman Interchange', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 1, last_updated: new Date(Date.now() - 15 * 60 * 1000) }
  ];
  
  // Auto-select first run if none selected (Dashboard uses displayRuns)
  useEffect(() => {
    if (activeMainTab === 'dashboard' && displayRuns.length > 0 && !dashboardSelectedRun) {
      setDashboardSelectedRun(displayRuns[0]);
    }
  }, [activeMainTab, displayRuns.length]);

  const handleDeleteProject = async (projectId) => {
    const userEmail = sessionStorage.getItem('userEmail');
    if (!userEmail) {
      showNotification('Please log in to remove projects.');
      return;
    }
    try {
      await progressService.deleteProjectRuns(userEmail, projectId);
      setOptimisticRuns((prev) => prev.filter((r) => r.project_id !== projectId));
      refreshDashboardRuns();
      if (dashboardSelectedRun?.project_id === projectId) {
        setDashboardSelectedRun(null);
      }
      showNotification('Project removed from list.');
    } catch (err) {
      console.error('Delete project runs failed:', err);
      showNotification(err.response?.data?.error || 'Failed to remove project.');
    }
  };

  const handleRequestDeleteDashboardRun = useCallback((run) => {
    const runId = String(run?.run_id || run?.full_run_id || '').trim();
    if (!runId) {
      showNotification('Could not remove run: missing run ID.');
      return;
    }
    if (!dashboardUserEmail) {
      showNotification('Please log in to remove runs.');
      return;
    }
    setDashboardDeleteCandidateRunId(runId);
  }, [dashboardUserEmail, showNotification]);

  const handleCancelDeleteDashboardRun = useCallback(() => {
    if (dashboardDeletingRunId) return;
    setDashboardDeleteCandidateRunId(null);
  }, [dashboardDeletingRunId]);

  const handleDeleteDashboardRun = useCallback(async (run) => {
    const runId = String(run?.run_id || run?.full_run_id || '').trim();
    if (!runId) {
      showNotification('Could not remove run: missing run ID.');
      return;
    }
    if (!dashboardUserEmail) {
      showNotification('Please log in to remove runs.');
      return;
    }
    setDashboardDeleteCandidateRunId(null);
    setDashboardDeletingRunId(runId);
    try {
      await removeDashboardRun(runId);
      setOptimisticRuns((prev) => prev.filter((entry) => String(entry?.run_id || '') !== runId));
      if (dashboardSelectedRun?.run_id === runId) {
        setDashboardSelectedRun(null);
      }
      // showNotification('Run removed from dashboard.');
      refreshDashboardRuns(true);
    } catch (err) {
      console.error('Delete run failed:', err);
      showNotification(err.response?.data?.error || err.message || 'Failed to remove run.');
    } finally {
      setDashboardDeletingRunId(null);
    }
  }, [
    dashboardSelectedRun,
    dashboardUserEmail,
    refreshDashboardRuns,
    removeDashboardRun,
    showNotification,
  ]);

  // Function to fetch projects from the API and reset dropdown state.
  const fetchProjects = async () => {
    console.log("🔧 RSAAnalysis - fetchProjects called");
    setIsLoadingProjects(true);
    try {
      // Use API_BASE_URL for local development, relative URL for production (CloudFront)
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/projectsSearch?query=`
        : '/api/projectsSearch?query=';
      console.log("🔧 RSAAnalysis - Making request to:", url);
      console.log("🔧 RSAAnalysis - ENVIRONMENT:", ENVIRONMENT);
      console.log("🔧 RSAAnalysis - API_BASE_URL:", API_BASE_URL);
      const response = await axios.get(url);
      setAllProjects(response.data);
      sessionStorage.setItem('allProjects', JSON.stringify(response.data));
      // Clear dropdown selections and search results
      setSearchResults([]);
      setSelectedProjectId(projectId ?? null);
      setExistingProjectName('');
      console.log("[OK] RSAAnalysis - Projects refreshed from API");
    } catch (error) {
      console.error("❌ RSAAnalysis - Error refreshing projects:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchRsaMileagesConfig = async () => {
    const cacheKey = 'rsaMileagesConfig';
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedGroups = parsed?.groups_mileages;
        const cachedProjects = parsed?.projects_per_group;
        const cachedIsdManeuvers = parsed?.groups_isd_maneuvers;
        const hasCachedGroups = cachedGroups && typeof cachedGroups === 'object' && Object.keys(cachedGroups).length > 0;
        const hasCachedProjects = cachedProjects && typeof cachedProjects === 'object' && Object.keys(cachedProjects).length > 0;
        const hasCachedIsdManeuvers = cachedIsdManeuvers && typeof cachedIsdManeuvers === 'object' && Object.keys(cachedIsdManeuvers).length > 0;
        if (hasCachedGroups && hasCachedProjects) {
          // Use cache for immediate render, then refresh from API below.
          setRsaGroupsMileages(cachedGroups);
          setRsaProjectsPerGroup(cachedProjects);
        }
        if (hasCachedIsdManeuvers) {
          setRsaGroupsIsdManeuvers(cachedIsdManeuvers);
        }
      }
    } catch (cacheError) {
      console.warn('Failed to parse cached RSA mileages config:', cacheError);
    }

    try {
      const url = ENVIRONMENT === 'local'
        ? `${API_BASE_URL}/api/secrets/rsa-mileages-config`
        : '/api/secrets/rsa-mileages-config';
      const response = await axios.get(url);
      const groups = response.data?.groups_mileages;
      const projects = response.data?.projects_per_group;
      const isdManeuvers = response.data?.groups_isd_maneuvers;
      if (groups && typeof groups === 'object') {
        setRsaGroupsMileages(groups);
      }
      if (projects && typeof projects === 'object') {
        setRsaProjectsPerGroup(projects);
      }
      if (isdManeuvers && typeof isdManeuvers === 'object') {
        setRsaGroupsIsdManeuvers(isdManeuvers);
      } else {
        setRsaGroupsIsdManeuvers({});
      }
      const hasGroups = groups && typeof groups === 'object' && Object.keys(groups).length > 0;
      const hasProjects = projects && typeof projects === 'object' && Object.keys(projects).length > 0;
      const hasIsdManeuvers = isdManeuvers && typeof isdManeuvers === 'object' && Object.keys(isdManeuvers).length > 0;
      if (hasGroups || hasProjects || hasIsdManeuvers) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          groups_mileages: groups || {},
          projects_per_group: projects || {},
          groups_isd_maneuvers: isdManeuvers || {}
        }));
      } else {
        sessionStorage.removeItem(cacheKey);
      }
    } catch (error) {
      console.error('Failed to fetch RSA mileages config:', error);
      setRsaGroupsMileages({});
      setRsaProjectsPerGroup({});
      setRsaGroupsIsdManeuvers({});
    }
  };

  const fetchRsaUsageLedgerState = useCallback(async (projectIdForUsage) => {
    const normalizedProjectId = String(projectIdForUsage ?? '').trim();
    if (!normalizedProjectId) {
      setRsaUsageLedgerState(null);
      return;
    }
    setRsaUsageLedgerLoading(true);
    try {
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const url = (ENVIRONMENT === 'production' || isProductionDomain)
        ? `/api/projects/${normalizedProjectId}/rsa-usage-ledger-state`
        : `${API_BASE_URL}/api/projects/${normalizedProjectId}/rsa-usage-ledger-state`;
      const response = await axios.get(url);
      const payload = response.data && typeof response.data === 'object' ? response.data : null;
      setRsaUsageLedgerState(payload);
    } catch (error) {
      console.warn('Failed to fetch RSA usage ledger state:', error);
      setRsaUsageLedgerState(null);
    } finally {
      setRsaUsageLedgerLoading(false);
    }
  }, []);

  // Function to fetch shared drives from the API
  const fetchSharedDrives = async () => {
    try {
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : '/api/google-drive/shared-drives';
      console.log("🔄 RSAAnalysis - Preloading shared drives from:", url);
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      // Cache in sessionStorage for FolderTreePopup to use
      sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
      console.log("[OK] RSAAnalysis - Shared drives preloaded and cached:", drivesArray.length);
    } catch (err) {
      console.error("❌ RSAAnalysis - Error preloading shared drives:", err);
      // Don't show error to user, just log it
    }
  };

  // Helper function to get token (shared with StorageConnectionModal)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log('[OK] [getAuthToken] Got token from Amplify Auth');
          return idToken;
        }
      } catch (amplifyError) {
        console.log('â„¹ï¸ [getAuthToken] Amplify Auth not available, trying other methods:', amplifyError.message);
      }
      
      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem('bimLiveAuthToken'); // Primary key used by this app
      
      if (!token) {
        token = sessionStorage.getItem('token');
      }
      if (!token) {
        token = sessionStorage.getItem('cognito_token');
      }
      if (!token) {
        token = sessionStorage.getItem('idToken');
      }
      if (!token) {
        // Check localStorage as fallback
        token = localStorage.getItem('bimLiveAuthToken') || 
                localStorage.getItem('token') || 
                localStorage.getItem('cognito_token') || 
                localStorage.getItem('idToken');
      }
      
      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'cognito_token' || name === 'token') {
            token = value;
            break;
          }
        }
      }
      
      console.log('🔍 [getAuthToken] Raw token from storage:', token ? `${token.substring(0, 20)}...` : 'null');
      
      if (!token) {
        console.warn('[WARN] [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify');
        console.log('🔍 [getAuthToken] Available sessionStorage keys:', Object.keys(sessionStorage));
        return null;
      }
      
      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log('[OK] [getAuthToken] Token is JSON, parsed structure:', typeof parsed, parsed ? Object.keys(parsed) : 'null');
        
        if (parsed && typeof parsed === 'string') {
          // Token is a JSON stringified string
          console.log('[OK] [getAuthToken] Using parsed string token');
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log('[OK] [getAuthToken] Using parsed.token');
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log('[OK] [getAuthToken] Using parsed.idToken');
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log('[OK] [getAuthToken] Using parsed.accessToken');
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn('[WARN] [getAuthToken] Parsed token is object but no known property found:', parsed);
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log('[OK] [getAuthToken] Token is not JSON, using as-is (raw JWT)');
        return token;
      }
    } catch (e) {
      console.error('❌ [getAuthToken] Error getting auth token:', e);
      return null;
    }
  };

  // Load user's storage connections
  const loadStorageConnections = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        // User not logged in - use system Google Drive
        setUseSystemGoogleDrive(true);
        setSelectedStorageProvider('system_google_drive');
        return;
      }
      
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/connections`
        : '/api/storage/connections';
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const connections = response.data.connections || [];
      setStorageConnections(connections);
      
      // Check for default connection
      const defaultConn = connections.find(c => c.is_default && c.is_active);
      if (defaultConn) {
        setSelectedConnection(defaultConn);
        setSelectedStorageProvider(defaultConn.provider_type);
        setUseSystemGoogleDrive(false);
      } else {
        // No default connection - use system Google Drive
        setSelectedStorageProvider('system_google_drive');
        setUseSystemGoogleDrive(true);
        setSelectedConnection(null);
      }
    } catch (error) {
      console.error('Error loading storage connections:', error);
      // Fallback to system Google Drive
      setUseSystemGoogleDrive(true);
      setSelectedStorageProvider('system_google_drive');
    }
  };

  // Check which providers user has connected
  const hasGoogleDriveConnection = storageConnections.some(c => c.provider_type === 'google_drive' && c.is_active);
  const hasS3Connection = storageConnections.some(c => c.provider_type === 's3' && c.is_active);
  const hasAzureConnection = storageConnections.some(c => c.provider_type === 'azure' && c.is_active);
  const hasGCPConnection = storageConnections.some(c => c.provider_type === 'gcp' && c.is_active);

  // Check for OAuth callback success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storageConnected = urlParams.get('storage_connected');
    const success = urlParams.get('success');
    
    if (storageConnected && success === 'true') {
      // Reload storage connections
      loadStorageConnections();
      
      // Show success message with proper provider name
      const providerNames = {
        'google_drive': 'Google Drive',
        'azure': 'Microsoft Azure',
        'gcp': 'Google Cloud Storage',
        's3': 'AWS S3'
      };
      const providerName = providerNames[storageConnected] || storageConnected;
      alert(`${providerName} connected successfully!`);
      
      // Clean up only the OAuth callback params and preserve the current project/tab state.
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete('storage_connected');
      nextParams.delete('success');
      const nextSearch = nextParams.toString();
      const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // On mount, preload projects and shared drives from API in the background
  // This ensures the dropdowns are ready when the user opens them
  useEffect(() => {
    // First, load from sessionStorage if available (for immediate display)
    const storedProjects = sessionStorage.getItem('allProjects');
    if (storedProjects) {
      const parsedProjects = JSON.parse(storedProjects);
      setAllProjects(parsedProjects);
      setSearchResults([]); // Keep dropdown empty initially
      console.log("[OK] Projects loaded from sessionStorage (cached)");
    }
    
    // Load user's storage connections
    loadStorageConnections();
    fetchRsaMileagesConfig();
    
    // Always fetch fresh data in the background on first mount
    // This ensures the dropdowns are ready when user opens them
    console.log("🔄 Preloading projects and shared drives from API on RSA-Analysis mount...");
    fetchProjects().catch(error => {
      console.error("❌ Failed to preload projects:", error);
      // Don't show error to user, just log it - cached data will be used if available
    });
    
    // Preload shared drives in the background
    fetchSharedDrives().catch(error => {
      console.error("❌ Failed to preload shared drives:", error);
      // Don't show error to user, just log it
    });
  }, []);

  useEffect(() => {
    if (!effectiveProjectId) {
      setRsaUsageLedgerState(null);
      setRsaUsageLedgerLoading(false);
      return;
    }
    fetchRsaUsageLedgerState(effectiveProjectId);
  }, [effectiveProjectId, fetchRsaUsageLedgerState]);

  // Persist requests in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('requests', JSON.stringify(requests));
  }, [requests]);

  // Load persisted requests from localStorage on mount
  useEffect(() => {
    const storedRequests = localStorage.getItem('requests');
    if (storedRequests) {
      setRequests(JSON.parse(storedRequests));
    }
  }, []);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update CSS custom properties for dynamic layout when services change
  useEffect(() => {
    const rightPanels = document.querySelector('.right-panels');
    if (rightPanels) {
      const hasEmbankmentPanel = services.includes('horizontal') || services.includes('embankment');
      rightPanels.style.setProperty('--embankment-panel-width', hasEmbankmentPanel ? '380px' : '0px');
    }
  }, [services]);

  // Filter the cached projects locally as the user type.
  const handleInputChange = (inputValue) => {
    console.log('handleInputChange called with:', inputValue);
    console.log('allProjects length:', allProjects.length);
    console.log('allProjects sample:', allProjects.slice(0, 3));
    
    if (!inputValue) {
      setSearchResults([]);
      setDropdownOpen(false);
      return inputValue;
    }
    
    // Always open dropdown when typing
    setDropdownOpen(true);
    
    const filtered = allProjects.filter(proj =>
      proj.name.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    console.log('Filtered results:', filtered);
    console.log('Setting searchResults to:', filtered);
    setSearchResults(filtered);
    
    return inputValue;
  };

  const resolveRsaMappedProjectId = async (sourceProjectId) => {
    const sourceIdText = String(sourceProjectId ?? '').trim();
    if (!sourceIdText) {
      return sourceProjectId;
    }

    const isProductionDomain = window.location.hostname.includes('123bimlive.com');
    const endpoint = (ENVIRONMENT === 'production' || isProductionDomain)
      ? `/api/projects/${encodeURIComponent(sourceIdText)}/rsa-mapped-project-id`
      : `${API_BASE_URL}/api/projects/${encodeURIComponent(sourceIdText)}/rsa-mapped-project-id`;

    try {
      const response = await axios.get(endpoint, { timeout: 10000 });
      const mappedValue = response?.data?.mapped_project_id ?? response?.data?.effective_project_id;
      const mappedText = String(mappedValue ?? '').trim();
      if (!mappedText) {
        return sourceProjectId;
      }
      if (/^\d+$/.test(mappedText)) {
        return Number(mappedText);
      }
      return mappedText;
    } catch (mappingError) {
      console.warn('Failed to resolve RSA mapped project_id, using source project_id:', mappingError);
      return sourceProjectId;
    }
  };

  // Modified handleSubmit: use new payload structure with analysis_template_ids
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!effectiveProjectId) {
      alert("Please open a project before running analysis.");
      return;
    }

    if (selectedTasks.length === 0) {
      alert("Please select at least one task.");
      return;
    }

    const selectedFolderIds = (selectedFolders || [])
      .map((f) => f.id ?? f.folder_id ?? f.folderId)
      .filter((id) => id != null && id !== '')
      .map(String);

    if (!isRsaModelProject && routesXmlFiles.length === 0 && selectedFolderIds.length === 0) {
      alert("Please select at least one folder from the folder tree.");
      return;
    }

    let requestId = null;
    let localRunId = null;
    try {
      const hasRoutes = routesXmlFiles.length > 0;
      const selectedRouteCountByTask = {};
      for (const taskName of selectedTasks) {
        selectedRouteCountByTask[taskName] = 0;
      }
      if (hasRoutes) {
        for (const routeFile of routesXmlFiles || []) {
          const routeDriveId = normalizeDriveFolderId(routeFile?.googleDriveId);
          if (!routeDriveId) {
            continue;
          }
          const rowSelection = routesMatrixSelection?.[routeDriveId] || {};
          for (const taskName of selectedTasks) {
            if (rowSelection[taskName]) {
              selectedRouteCountByTask[taskName] = (selectedRouteCountByTask[taskName] || 0) + 1;
            }
          }
        }
      }
      const selectedTaskNamesForSubmission = hasRoutes
        ? selectedTasks.filter((taskName) => (selectedRouteCountByTask[taskName] || 0) > 0)
        : [...selectedTasks];

      if (selectedTaskNamesForSubmission.length === 0) {
        const emptySelectionMessage = hasRoutes
          ? 'Select at least one route assignment for the analyses you want to run.'
          : 'Please select at least one task.';
        alert(emptySelectionMessage);
        return;
      }

      const projectRecord = Array.isArray(allProjects)
        ? allProjects.find((proj) => String(proj?.id ?? proj?.Id ?? '') === String(effectiveProjectId))
        : null;
      const projectNameFromState = String(existingProjectName || '').trim();
      const projectNameFromProp = String(projectName || '').trim();
      const projectNameFromRecord = String(projectRecord?.name ?? projectRecord?.Name ?? '').trim();
      const resolvedProjectName = projectNameFromState || projectNameFromProp || projectNameFromRecord;
      const requestProjectName = resolvedProjectName || `Project ${effectiveProjectId}`;
      const payloadProjectId = await resolveRsaMappedProjectId(effectiveProjectId);

      // Build analysis_template_ids array from selected tasks (non-rsa-model default behavior).
      let analysisTemplateIds = selectedTaskNamesForSubmission
        .map(taskName => analysisTemplateIdMapping[taskName])
        .filter(id => id && id !== ''); // Filter out empty or undefined IDs

      if (analysisTemplateIds.length === 0) {
        alert("No valid analysis templates found for selected tasks.");
        return;
      }

      const normalizedProjectTypeForPayload = isRsaModelProject ? 'rsa-model' : 'rsa-existing';
      const user_email = sessionStorage.getItem('userEmail') || '';

      // --- PHASE 1 COMPLETE: inject optimistic run and switch to dashboard ---
      localRunId = `local_${Date.now()}`;
      const optimisticRun = {
        run_id: localRunId,
        project_id: effectiveProjectId,
        project_type: normalizedProjectTypeForPayload,
        project_name: requestProjectName,
        name: requestProjectName,
        description: `Pipeline run for project ${effectiveProjectId}`,
        status: 'pending',
        _isQueuing: true,
        created_at: new Date(),
        last_updated: new Date(),
        user_email,
        services: selectedTaskNamesForSubmission || [],
        overall_progress: 0,
        total_analyses: 1,
        completed_analyses: 0,
        running_analyses: 0,
      };
      setPendingLocalRunId(localRunId);
      setOptimisticRuns(prev => [optimisticRun, ...prev]);
      setActiveMainTab('dashboard');

      let pathFoldersPayload = [];
      let modelFolderIds = [];
      let outputFolderId = '';
      if (hasRoutes) {
        try {
          const rsaModelPayload = await buildRsaFoldersPayloadFromProject(effectiveProjectId);
          pathFoldersPayload = Array.isArray(rsaModelPayload?.path_folders) ? rsaModelPayload.path_folders : [];
          modelFolderIds = Array.isArray(rsaModelPayload?.model_folder_id)
            ? rsaModelPayload.model_folder_id
            : [];
          outputFolderId = String(rsaModelPayload?.output_folder_id ?? '').trim();

          // For rsa-model: enforce index alignment between analysis_template_ids and path_folders.
          // Both arrays are rebuilt from the same ordered selected-task list.
          const pathFolderByConfigType = new Map(
            (pathFoldersPayload || [])
              .map((item) => {
                const configType = String(item?.config_type ?? '').trim().toUpperCase();
                const pathFolderId = String(item?.path_folder_id ?? '').trim();
                if (!configType || !pathFolderId) {
                  return null;
                }
                return [configType, { config_type: configType, path_folder_id: pathFolderId }];
              })
              .filter(Boolean)
          );

          const seenConfigTypes = new Set();
          const alignedRows = [];
          const missingConfigTypes = [];

          for (const taskName of selectedTaskNamesForSubmission) {
            const templateId = String(analysisTemplateIdMapping[taskName] ?? '').trim();
            if (!templateId) {
              continue;
            }
            const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskName];
            const configType = String(RSA_FOLDER_CODE_TO_SUFFIX[folderCode] ?? '').trim().toUpperCase();
            if (!configType || seenConfigTypes.has(configType)) {
              continue;
            }
            seenConfigTypes.add(configType);

            const matchedPathFolder = pathFolderByConfigType.get(configType);
            if (!matchedPathFolder) {
              missingConfigTypes.push(configType);
              continue;
            }

            alignedRows.push({
              template_id: templateId,
              config_type: matchedPathFolder.config_type,
              path_folder_id: matchedPathFolder.path_folder_id,
            });
          }

          if (missingConfigTypes.length > 0) {
            if (localRunId) {
              setOptimisticRuns(prev => prev.filter(r => r.run_id !== localRunId));
              setPendingLocalRunId(null);
            }
            showNotification(`Missing RSA folder(s) for selected config type(s): ${missingConfigTypes.join(', ')}`);
            return;
          }

          if (alignedRows.length === 0) {
            if (localRunId) {
              setOptimisticRuns(prev => prev.filter(r => r.run_id !== localRunId));
              setPendingLocalRunId(null);
            }
            showNotification(
              'No analyses with routes to submit. Add route XML files to the RSA folders for the selected analyses, or deselect analyses that list 0 routes.'
            );
            return;
          }

          const orderedAlignedRows = [...alignedRows].sort((a, b) => a.config_type.localeCompare(b.config_type));

          analysisTemplateIds = orderedAlignedRows.map((row) => row.template_id);
          pathFoldersPayload = orderedAlignedRows.map((row) => ({
            config_type: row.config_type,
            path_folder_id: row.path_folder_id,
          }));
        } catch (folderPayloadError) {
          console.warn('Failed to build rsa-model folder payload:', folderPayloadError);
        }
      } else {
        try {
          const deliverablesInfo = await resolveDeliverablesFolderForProject(
            effectiveProjectId,
            Array.isArray(projectFilesSnapshot) && projectFilesSnapshot.length > 0 ? projectFilesSnapshot : null
          );
          outputFolderId = String(deliverablesInfo?.driveId ?? '').trim();
        } catch (deliverablesResolveError) {
          console.warn('Failed to resolve rsa-existing deliverables folder:', deliverablesResolveError);
        }
      }

      if (!outputFolderId) {
        if (localRunId) {
          setOptimisticRuns(prev => prev.filter(r => r.run_id !== localRunId));
          setPendingLocalRunId(null);
        }
        showNotification("Deliverables folder google_drive_id was not found. Please set it in the project's MySQL row.");
        return;
      }

      let folderIds = hasRoutes
        ? Array.from(
            new Set(
              [
                ...modelFolderIds,
                ...pathFoldersPayload.map((item) => item?.path_folder_id),
              ]
                .filter((id) => id != null && String(id).trim() !== '')
                .map(String)
            )
          )
        : selectedFolderIds;

      if (folderIds.length === 0) {
        alert("No valid folder IDs found for this project.");
        return;
      }

      // Generate a unique id for this request
      requestId = Date.now();
      // Create a new request record with a pending status
      const newRequestEntry = {
        id: requestId,
        projectType: isRsaModelProject ? 'rsa-model' : 'existing',
        folderIds,
        projectName: requestProjectName,
        status: 'pending',
        response: '',
      };
      setRequests(prev => [...prev, newRequestEntry]);

      const setupWktInputValue = document.querySelector('.setup-wkt-input-wrap .setup-wkt-input')?.value;
      const payloadWkt = normalizeWktInput(
        String(setupWktInputValue ?? mapOverlayLocalConfig?.wkt ?? '').trim()
      ) || '';
      const selectedServicesForPayload = Array.from(
        new Set(
          selectedTaskNamesForSubmission
            .map((taskName) => TASK_OPTION_TO_SERVICE[taskName])
            .filter(Boolean)
        )
      );
      const selectedRouteCountByService = selectedServicesForPayload.reduce((acc, serviceName) => {
        acc[serviceName] = 0;
        return acc;
      }, {});
      for (const [taskName, routeCount] of Object.entries(selectedRouteCountByTask)) {
        const serviceName = TASK_OPTION_TO_SERVICE[taskName];
        if (!serviceName || !selectedServicesForPayload.includes(serviceName)) {
          continue;
        }
        selectedRouteCountByService[serviceName] = routeCount;
      }

      const rawRunMetrics = (
        rsaRunMetricsRef.current && typeof rsaRunMetricsRef.current === 'object'
      ) ? rsaRunMetricsRef.current : {};

      const safeMetricsMap = (value) => (value && typeof value === 'object' ? value : {});
      const pickSelectedServiceMetrics = (sourceMap) => {
        const out = {};
        for (const serviceName of selectedServicesForPayload) {
          const num = Number(sourceMap[serviceName]);
          out[serviceName] = Number.isFinite(num) ? num : 0;
        }
        return out;
      };
      const sumMetricValues = (obj) => Object.values(obj || {}).reduce((sum, val) => {
        const num = Number(val);
        return Number.isFinite(num) ? sum + num : sum;
      }, 0);

      const mileageByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.mileage_by_service)
      );
      const maneuversByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.maneuvers_by_service)
      );
      const routeCountByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.route_count_by_service)
      );
      if (hasRoutes) {
        for (const serviceName of selectedServicesForPayload) {
          routeCountByService[serviceName] = Number(selectedRouteCountByService[serviceName]) || 0;
        }
      }

      const runLoggingPayload = {
        trigger_source: 'toggle-btn-rsa-run',
        triggered_at: new Date().toISOString(),
        selected_tasks: [...selectedTaskNamesForSubmission],
        selected_services: [...selectedServicesForPayload],
        submitted_analysis_template_ids: [...analysisTemplateIds],
        analysis_metrics: {
          mileage_by_service: mileageByService,
          maneuvers_by_service: maneuversByService,
          route_count_by_service: routeCountByService,
          mileage_total_miles: sumMetricValues(mileageByService),
          maneuvers_total: sumMetricValues(maneuversByService),
          route_total: sumMetricValues(routeCountByService),
          selected_project_id: effectiveProjectId,
          metrics_generated_at: rawRunMetrics.metrics_generated_at || new Date().toISOString(),
        },
      };
      const processPayload = hasRoutes
        ? {
            project_id: payloadProjectId,
            project_type: normalizedProjectTypeForPayload,
            projectType: normalizedProjectTypeForPayload,
            source_project_id: effectiveProjectId,
            folder_ids: Array.from(
              new Set(
                [
                  ...(pathFoldersPayload || []).map((item) => item?.path_folder_id),
                  ...(modelFolderIds || []),
                ]
                  .filter((id) => id != null && String(id).trim() !== '')
                  .map(String)
              )
            ),
            analysis_template_ids: analysisTemplateIds,
            horizontal_data: horizontalData || {},
            embankment_data: EmbankmentData || {},
            path_folders: pathFoldersPayload,
            output_folder_id: outputFolderId,
            wkt: payloadWkt,
            project_name: requestProjectName,
            user_email,
            selected_task_names: [...selectedTaskNamesForSubmission],
            selected_services: [...selectedServicesForPayload],
            trigger_source: 'toggle-btn-rsa-run',
            run_logging: runLoggingPayload,
          }
        : {
            project_id: payloadProjectId,
            project_type: normalizedProjectTypeForPayload,
            projectType: normalizedProjectTypeForPayload,
            source_project_id: effectiveProjectId,
            project_name: requestProjectName,
            folder_ids: folderIds,
            analysis_template_ids: analysisTemplateIds,
            horizontal_data: horizontalData || {},
            embankment_data: EmbankmentData || {},
            path_data:
              rsaPathsHook.paths.find((p) => p.pathData?.geometry?.coordinates?.length >= 2)?.pathData
              ?? null,
            output_folder_id: outputFolderId,
            user_email,
            selected_task_names: [...selectedTaskNamesForSubmission],
            selected_services: [...selectedServicesForPayload],
            trigger_source: 'toggle-btn-rsa-run',
            run_logging: runLoggingPayload,
          };

      // New server endpoint for RSA analysis
      // Use relative URL for production (goes through CDN /rsa/* behavior), full URL for local/server
      // Check if we're on production domain (123bimlive.com) to ensure correct routing
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const processUrl = (ENVIRONMENT === 'production' || isProductionDomain)
        ? '/rsa/process'
        : `${processApi}/process`;
      
      console.log('ðŸš€ Making API call to process endpoint:', processUrl);
      console.log('ðŸ”§ ENVIRONMENT:', ENVIRONMENT);
      console.log('ðŸ”§ isProductionDomain:', isProductionDomain);
      console.log('ðŸ”§ window.location.hostname:', window.location.hostname);
      console.log('ðŸ“¦ Payload:', processPayload);
      
      const processResponse = await axios.post(
        processUrl,
        processPayload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('[OK] Process API call successful:', processResponse.data);
      setHasSubmittedRsaRun(true);

      // Update the request record for the existing project.
      setRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, status: 'completed', response: `Process completed: ${processResponse.data.message}` }
            : req
        )
      );

      // Optimistic UI: upgrade the queuing tile or add a new run tile to Dashboard
      const runId = processResponse.data?.run_id;
      if (runId && (processResponse.status === 200 || processResponse.status === 202)) {
        if (localRunId) {
          // Upgrade the queuing placeholder to a real processing run
          setOptimisticRuns(prev =>
            prev.map(r =>
              r.run_id === localRunId
                ? { ...r, run_id: runId, status: 'processing', _isQueuing: false, running_analyses: 1 }
                : r
            )
          );
          setPendingLocalRunId(null);
        } else {
          // Non-RSA-model: no placeholder was created, add fresh optimistic entry
          const newRun = {
            run_id: runId,
            project_id: effectiveProjectId,
            project_type: normalizedProjectTypeForPayload,
            project_name: requestProjectName,
            name: requestProjectName,
            description: `Pipeline run for project ${effectiveProjectId}`,
            status: 'processing',
            created_at: new Date(),
            last_updated: new Date(),
            user_email: user_email,
            services: selectedTaskNamesForSubmission || [],
            overall_progress: 0,
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 1,
          };
          setOptimisticRuns(prev => [newRun, ...prev]);
          setDashboardSelectedRun(newRun);
        }
        refreshDashboardRuns();
        if (isRsaModelProject) {
          setPendingDashboardRunId(runId);
        }
      } else if (isRsaModelProject && (processResponse.status === 200 || processResponse.status === 202)) {
        // Fallback: launch succeeded but run_id not returned immediately.
        if (localRunId) {
          setOptimisticRuns(prev => prev.filter(r => r.run_id !== localRunId));
          setPendingLocalRunId(null);
        }
        refreshDashboardRuns();
        setPendingDashboardRunId(null);
      }

      fetchRsaUsageLedgerState(effectiveProjectId);

      // Show success message - user stays on RSA-Analysis page (or moved to Dashboard)
      showNotification('Analysis submitted successfully!');
    } catch (error) {
      console.error('Process API call failed:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Update the request record with an error message if the API call fails.
      if (requestId != null) {
        setRequests(prev =>
          prev.map(req =>
            req.id === requestId
              ? { ...req, status: 'error', response: error.response?.data?.message || 'There was an error submitting the payload(s).' }
              : req
          )
        );
      }
      
      // Remove queuing placeholder tile if it exists
      if (localRunId) {
        setOptimisticRuns(prev => prev.filter(r => r.run_id !== localRunId));
        setPendingLocalRunId(null);
      }
      // Don't redirect on error - show error to user instead
      showNotification(`Error submitting analysis: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  // Save RSAPATH XML files to Source folder + persist lanes to MongoDB
  const saveLasSeparationPathToDrive = async () => {
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    const xmlState = String(mapOverlayLocalConfig?.state ?? '').trim();
    let xmlLanes = rsaPathsHook.lanes.filter(
      (l) => (l.waypoints?.length >= 2) || (l.pathData?.geometry?.coordinates?.length >= 2)
    );
    // When no explicit lanes exist, promote qualifying paths to lane-like objects
    if (xmlLanes.length === 0) {
      xmlLanes = rsaPathsHook.paths
        .filter((p) => (p.waypoints?.length >= 2) || (p.pathData?.geometry?.coordinates?.length >= 2))
        .map((p) => ({
          ...p,
          pathId: p.id,
          lanePosition: 'LL',
          direction: 'P',
          speedSegments: [],
        }));
    }
    if (xmlLanes.length === 0) {
      showNotification('Add at least one lane with waypoints before saving.');
      return;
    }
    if (!wkt) {
      showNotification('Enter WKT CRS in Setup before saving.');
      return;
    }
    if (!xmlState) {
      showNotification('Fill in State in Setup before saving.');
      return;
    }

    setSavingLasSeparationPath(true);
    try {
      const escXml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
      const lanePositionLabels = { LL: 'Left Lane', CL: 'Center Lane', RL: 'Right Lane' };
      const proj4FromValidate = wktValidation?.crs?.proj4;
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const uploadUrl = baseUrl ? `${API_BASE_URL}/api/google-drive/upload-json` : '/api/google-drive/upload-json';

      const allPaths = rsaPathsHook.paths;
      const routeNameCounter = {};
      const pathSegmentMap = {};
      for (const p of allPaths) {
        const name = String(p.label ?? '').trim();
        if (!routeNameCounter[name]) routeNameCounter[name] = 1;
        pathSegmentMap[p.id] = routeNameCounter[name];
        routeNameCounter[name] += 1;
      }

      // Resolve Source/Routes folder for XML upload (create Routes if needed)
      let sourceFolderId = null;
      try {
        const sourceId = await fetchProjectSetupFolderId(projectId);
        if (sourceId) {
          const subfolderUrl = baseUrl ? `${API_BASE_URL}/api/google-drive/get-or-create-subfolder` : '/api/google-drive/get-or-create-subfolder';
          const routesRes = await axios.post(subfolderUrl, {
            parent_id: sourceId,
            folder_name: 'Routes'
          }, { headers: { 'Content-Type': 'application/json' } });
          sourceFolderId = routesRes.data?.folder_id || null;
        }
      } catch (err) {
        console.warn('Could not resolve Source/Routes folder for XML upload:', err);
      }

      let xmlUploadCount = 0;
      for (const lane of xmlLanes) {
        try {
          const parentPath = allPaths.find((p) => p.id === lane.pathId);
          const routeNumber = String(lane.label ?? '').trim();
          if (!routeNumber) continue;
          const segmentNumber = pathSegmentMap[lane.pathId] ?? 1;
          const direction = lane.direction ?? 'P';
          const lanePosition = lane.lanePosition ?? 'LL';

          let laneWaypoints;
          if (lane.waypoints?.length >= 2) {
            laneWaypoints = lane.waypoints;
          } else {
            laneWaypoints = lane.pathData.geometry.coordinates.map(
              (c) => ({ lat: c[1], lng: c[0] })
            );
          }
          const points10ft = getPathCoordinatesEveryFeet(laneWaypoints, 10);
          if (!points10ft || points10ft.length === 0) continue;

          const projected = await latLngToProjectedXYAsync(points10ft, wkt, 0, proj4FromValidate);
          if (!projected.length || projected.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) continue;

          const laneCoords = lane.pathData?.geometry?.coordinates;
          const totalMi = (laneCoords?.length >= 2 ? getPathLengthMiles(laneCoords) : getPathLengthMiles(lane.waypoints)) || 0;

          const speedSegments = Array.isArray(lane.speedSegments)
            ? [...lane.speedSegments].filter((seg) => Number.isFinite(Number(seg?.mileMark))).sort((a, b) => Number(a.mileMark) - Number(b.mileMark))
            : [];
          const firstSpeed = speedSegments.length > 0 && Number.isFinite(Number(speedSegments[0].speedMph)) ? Number(speedSegments[0].speedMph) : 45;

          const pathId = `${routeNumber}-${segmentNumber}-${lanePosition}-${direction}`;
          const note = lanePositionLabels[lanePosition] || 'Lane';
          const beginMile = direction === 'N' ? totalMi.toFixed(4) : '0.0000';
          const endMile = direction === 'N' ? '0.0000' : totalMi.toFixed(4);

          let speedLimitsXml = '';
          if (speedSegments.length > 0) {
            speedLimitsXml = speedSegments.map((seg, idx) => {
              const startMile = Number(seg.mileMark);
              const endMileSeg = idx < speedSegments.length - 1 ? Number(speedSegments[idx + 1].mileMark) : totalMi;
              const speed = Number.isFinite(Number(seg.speedMph)) && Number(seg.speedMph) > 0 ? Number(seg.speedMph) : 45;
              return `    <SPEED_AREA Start="${startMile.toFixed(4)}" End="${endMileSeg.toFixed(4)}" Speed="${speed}" />`;
            }).join('\n');
          }

          const pointsXml = projected
            .map(([x, y, z]) => `        <P>${Number(x).toFixed(2)},${Number(y).toFixed(2)},${Number(z || 0).toFixed(2)}</P>`)
            .join('\n');

          const xml = `<?xml version='1.0' encoding='utf-8'?>
<RSAPATH>
  <DIRECTION>${escXml(direction)}</DIRECTION>
  <ROUTE_NUMB>${escXml(routeNumber)}</ROUTE_NUMB>
  <PATH_UNITS>Meter</PATH_UNITS>
  <SPEED_LIMIT>${firstSpeed}</SPEED_LIMIT>
  <SPEED_UNITS>MPH</SPEED_UNITS>
  <CYCLE>2024</CYCLE>
  <STATE>${escXml(xmlState.toUpperCase())}</STATE>
  <APERTURE>0.25</APERTURE>
  <SPEED_LIMITS>
${speedLimitsXml}
  </SPEED_LIMITS>
  <INTERSECTION_POINTS></INTERSECTION_POINTS>
  <PATH_GROUP>
    <PATH>
      <PATH_ID>${escXml(pathId)}</PATH_ID>
      <NOTE>${escXml(note)}</NOTE>
      <CODE>${escXml(lanePosition)}</CODE>
      <BEGIN_MILE>${beginMile}</BEGIN_MILE>
      <END_MILE>${endMile}</END_MILE>
      <CAD_HANDLE>45B,45A</CAD_HANDLE>
      <POINTS>
${pointsXml}
      </POINTS>
    </PATH>
  </PATH_GROUP>
</RSAPATH>
`;
          const xmlFilename = `${xmlState.toUpperCase()}_${routeNumber}_${segmentNumber}_${lanePosition}_${direction}.xml`;
          if (sourceFolderId) {
            await axios.post(uploadUrl, {
              folder_id: sourceFolderId,
              filename: xmlFilename,
              content: xml
            }, { headers: { 'Content-Type': 'application/json' } });
            xmlUploadCount += 1;
          }
        } catch (xmlErr) {
          console.warn('XML upload failed for lane', lane.id, xmlErr);
        }
      }
      if (xmlUploadCount > 0) {
        showNotification(`Uploaded ${xmlUploadCount} XML file(s) to Source/Routes folder.`);
      } else if (!sourceFolderId) {
        showNotification('Could not resolve Source/Routes folder — XML files were not uploaded.');
      }

      // ── Save lanes to MongoDB ──────────────────────────────────────────────
      try {
        const mongoUrl = isProductionDomain
          ? `/api/projects/${projectId}/rsa-lanes-mongo`
          : `${API_BASE_URL}/api/projects/${projectId}/rsa-lanes-mongo`;
        await axios.post(mongoUrl, {
          paths: rsaPathsHook.paths,
          lanes: rsaPathsHook.lanes,
          setupConfig: {
            state: xmlState,
            wkt: mapOverlayLocalConfig?.wkt ?? '',
          },
        }, { headers: { 'Content-Type': 'application/json' } });
        showNotification('Lanes saved to database.');
      } catch (mongoErr) {
        console.warn('MongoDB save failed:', mongoErr);
      }
    } catch (e) {
      console.error('saveLasSeparationPathToDrive', e);
      showNotification('Failed to save: ' + (e.message || String(e)));
    } finally {
      setSavingLasSeparationPath(false);
    }
  };

  // Create Ortho Maps: build LasSeparationPath.json from path/lane data, upload to LAS folder, run pipeline
  const createOrthoMaps = async (pathId) => {
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    if (!wkt) {
      showNotification('Enter WKT CRS in Setup before creating ortho maps.');
      return;
    }

    const folderId = effectiveSetupFolderId;
    if (!folderId) {
      showNotification('Select a LAS folder before creating ortho maps.');
      return;
    }

    // Find the target path by ID
    const targetPath = rsaPathsHook.paths.find((p) => p.id === pathId);
    if (!targetPath) {
      showNotification('Path not found.');
      return;
    }

    // Find best source geometry: prefer lanes of this path, then the path itself
    let sourceLane = rsaPathsHook.lanes.find(
      (l) => l.pathId === pathId && ((l.waypoints?.length >= 2) || (l.pathData?.geometry?.coordinates?.length >= 2))
    );
    if (!sourceLane) {
      sourceLane = targetPath;
      if (!((sourceLane.waypoints?.length >= 2) || (sourceLane.pathData?.geometry?.coordinates?.length >= 2))) {
        showNotification('Draw a path with at least 2 waypoints before creating ortho maps.');
        return;
      }
    }

    setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: true }));
    try {
      const proj4FromValidate = wktValidation?.crs?.proj4;
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const baseUrl = (ENVIRONMENT === 'production' || isProductionDomain) ? '' : API_BASE_URL;
      const sanitizedName = sanitizePathLabel(targetPath.label);
      console.log('[createOrthoMaps] pathId=', pathId, 'label=', targetPath.label, 'sanitized=', sanitizedName, 'folderId=', folderId);

      // Get path geometry in lat/lng — prefer the full road-snapped polyline
      let waypoints;
      const pathCoords = sourceLane.pathData?.geometry?.coordinates;
      if (Array.isArray(pathCoords) && pathCoords.length >= 2) {
        waypoints = pathCoords.map((c) => ({ lat: c[1], lng: c[0] }));
      } else if (sourceLane.waypoints?.length >= 2) {
        waypoints = sourceLane.waypoints;
      } else {
        showNotification('No path geometry found.');
        return;
      }

      // Quick check: does the path overlap any LAS bounding box?
      if (lasBoundingBoxes.length > 0) {
        const pathOverlapsLas = waypoints.some((wp) =>
          lasBoundingBoxes.some((b) =>
            wp.lat >= b.south && wp.lat <= b.north && wp.lng >= b.west && wp.lng <= b.east
          )
        );
        if (!pathOverlapsLas) {
          showNotification('Path is outside all LAS bounding boxes — no ortho tiles can be created. Move the path to overlap the LAS data.');
          return;
        }
      }

      // Project to XY coordinates
      const points10ft = getPathCoordinatesEveryFeet(waypoints, 10);
      if (!points10ft || points10ft.length === 0) {
        showNotification('Could not interpolate path coordinates.');
        return;
      }
      const projected = await latLngToProjectedXYAsync(points10ft, wkt, 0, proj4FromValidate);
      if (!projected.length || projected.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) {
        showNotification('Coordinate projection failed — check WKT CRS.');
        return;
      }

      // Build GeoJSON
      const refLabel = targetPath.label || 'Path';
      const geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { ref: refLabel },
          geometry: {
            type: 'LineString',
            coordinates: projected.map(([x, y, z]) => [x, y, z || 0]),
          },
        }],
      };

      const geojsonStr = JSON.stringify(geojson, null, 2);

      showNotification(`Starting ortho maps pipeline for ${targetPath.label}…`);

      // Submit async pipeline job with path_name
      const pipelineUrl = baseUrl ? `${API_BASE_URL}/api/rsa/ortho-maps-pipeline` : '/api/rsa/ortho-maps-pipeline';
      const submitRes = await axios.post(pipelineUrl, {
        folder_id: folderId,
        geojson_content: geojsonStr,
        path_name: sanitizedName,
      }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 });

      if (submitRes.status !== 202 || !submitRes.data?.job_id) {
        showNotification('Unexpected response from pipeline: ' + (submitRes.data?.message || submitRes.status));
        return;
      }

      const jobId = submitRes.data.job_id;
      showNotification(`Ortho maps pipeline running for ${targetPath.label}…`);

      // Poll for job status — stream tiles onto map as they're uploaded
      const statusBaseUrl = baseUrl ? `${API_BASE_URL}/api/rsa/ortho-maps-pipeline` : '/api/rsa/ortho-maps-pipeline';
      let lastMessage = '';
      const displayedTileIds = new Set();
      const pollResult = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${statusBaseUrl}/${jobId}/status`, { timeout: 10000 });
            const { status, progress, message, tiles } = statusRes.data;
            if (message && message !== lastMessage) {
              showNotification(`Ortho maps (${targetPath.label}): ${message} (${progress || 0}%)`);
              lastMessage = message;
            }
            setOrthoProgressByPath((prev) => ({ ...prev, [pathId]: { progress: progress || 0, message: message || '' } }));
            // Stream tiles onto map as they arrive — use local_url for instant display
            if (Array.isArray(tiles) && tiles.length > 0) {
              const newTiles = tiles.filter((t) => (t?.local_url || t?.png_file_id) && !displayedTileIds.has(t.name));
              if (newTiles.length > 0) {
                const wktStr = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
                const p4 = wktValidation?.crs?.proj4;
                for (const tile of newTiles) {
                  try {
                    const corners = tile.projected_corners;
                    if (Array.isArray(corners) && corners.length === 4) {
                      const xy = corners.map((p) => [Number(p?.x), Number(p?.y)]);
                      if (xy.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y))) {
                        const quad = await projectedPointsToLatLngAsync(xy, wktStr, p4);
                        const b = latLngPointsToBounds(quad);
                        if (b) {
                          const localUrl = tile.local_url
                            ? (baseUrl ? `${API_BASE_URL}${tile.local_url}` : tile.local_url)
                            : null;
                          setOrthoTiles((prev) => [...prev, {
                            name: tile.name,
                            pngFileId: tile.png_file_id || null,
                            localUrl,
                            bounds: b,
                            quad,
                          }]);
                          displayedTileIds.add(tile.name);
                        }
                      }
                    }
                  } catch (_) { /* skip tile on projection error */ }
                }
              }
            }
            if (status === 'completed') {
              clearInterval(poll);
              resolve(statusRes.data);
            } else if (status === 'failed') {
              clearInterval(poll);
              reject(new Error(message || 'Pipeline failed'));
            }
          } catch (pollErr) {
            console.warn('[createOrthoMaps] poll error (will retry):', pollErr.message);
          }
        }, 2000);
        setTimeout(() => { clearInterval(poll); reject(new Error('Pipeline timed out after 1 hour')); }, 3600000);
      });

      console.log('[createOrthoMaps] pipeline completed:', pollResult);
      showNotification(pollResult.message || `Ortho maps created for ${targetPath.label}.`);

      // Backfill Drive file IDs into tile state for persistence (local URLs are still used for display)
      if (Array.isArray(pollResult.tiles) && pollResult.tiles.length > 0) {
        setOrthoTiles((prev) => {
          let changed = false;
          const updated = prev.map((t) => {
            if (t.pngFileId) return t;
            const finalTile = pollResult.tiles.find((ft) => ft.name === t.name);
            if (finalTile?.png_file_id) {
              changed = true;
              return { ...t, pngFileId: finalTile.png_file_id };
            }
            return t;
          });
          return changed ? updated : prev;
        });
      }

      // Update per-path status — tiles are already loaded via streaming
      setMaps2000ByPath((prev) => ({ ...prev, [pathId]: true }));
      checkMaps2000ForPaths();
    } catch (e) {
      console.error('[createOrthoMaps] ERROR:', e);
      const errMsg = e.response?.data?.error || e.message || String(e);
      showNotification('Failed to create ortho maps: ' + errMsg);
    } finally {
      setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: false }));
      setOrthoProgressByPath((prev) => { const next = { ...prev }; delete next[pathId]; return next; });
    }
  };

  // Legacy service mapping (kept for backward compatibility if needed)
  const serviceMapping = {
    'Tiling': 'tiling',
    'CSF': 'csf',
    'Road Detection': 'road_detection',
    'Car Removal': 'car_removal',
    'File Transfer': 'file_transfer',
    'Analysis': 'create_rsa',
    'PSD Classification': 'psd_classification',
    'Horizontal': 'horizontal',
    'Embankment': 'embankment',
    'Automatic Reports':'automatic_reports',
    // New task name mappings (with abbreviations)
    'Stopping Sight Distance (SSD)': 'stopping_sight_distance',
    'Passing Sight Distance (PSD)': 'passing_sight_distance',
    'Flat Spot Analysis (FSA)': 'flat_spot_analysis',
    'Intersection Sight Distance (ISD)': 'intersection_sight_distance',
    'Sunglare Analysis (GLR)': 'sunglare_analysis',
    'Guardrails Warrant (GRW)': 'guardrails_warrant',
    'Horizontal Curve Analysis (HCA)': 'horizontal',
    'Existing Guardrails (EXG)': 'existing_guardrails',
    'Embankment Analysis (EMB)': 'embankment',
    'Clear Zone Analysis (CLZ)': 'clear_zone_analysis'
  };

  // Function to handle clear button click
  const handleClearClick = () => {
    setShowClearPopup(true);
  };

  // Function to clear all form data
  const handleClearConfirm = () => {
    // Reset all form states
    setExistingProjectName('');
    setSelectedProjectId(projectId ?? null);
    setServices([]);
    setAnalysisTypes([]);
    setSelectedTasks([]);
    setSelectedFolders([]);
    setSelectedDriveName('');
    setLidarType('');
    setHorizontalData(null);
    setEmbankmentData(null);
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
    rsaPathsHook.actions.reset();
    setGroupSize(25);
    setShowClearPopup(false);
  };

  // Function to cancel clear operation
  const handleClearCancel = () => {
    setShowClearPopup(false);
  };

  // Map edits the active segment's waypoints.
  // Uses refs so stale closures (e.g. async geocoding callbacks) always see current state.
  const pathSetWaypoints = useCallback((wpsOrUpdater) => {
    const entityType = activeRsaEntityTypeRef.current;
    const lane = activeRsaLaneRef.current;
    const path = activeRsaPathRef.current;

    if (entityType === 'lane' && lane?.id) {
      const current = Array.isArray(lane.waypoints) ? lane.waypoints : [];
      const newWaypoints = typeof wpsOrUpdater === 'function' ? wpsOrUpdater(current) : wpsOrUpdater;
      rsaPathsHook.actions.setLaneWaypoints(
        lane.id,
        Array.isArray(newWaypoints) ? newWaypoints : current
      );
      return;
    }
    if (!path?.id) {
      const current = EMPTY_WAYPOINTS;
      const newWaypoints = typeof wpsOrUpdater === 'function' ? wpsOrUpdater(current) : wpsOrUpdater;
      if (!Array.isArray(newWaypoints) || newWaypoints.length === 0) return;
      const createdPathId = rsaPathsHook.actions.addPath();
      rsaPathsHook.actions.setPathWaypoints(createdPathId, newWaypoints);
      return;
    }
    const current = Array.isArray(path.waypoints) ? path.waypoints : [];
    const newWaypoints = typeof wpsOrUpdater === 'function' ? wpsOrUpdater(current) : wpsOrUpdater;
    rsaPathsHook.actions.setPathWaypoints(
      path.id,
      Array.isArray(newWaypoints) ? newWaypoints : current
    );
  }, [rsaPathsHook.actions]); // refs are stable; only actions needed in deps

  const pathOnPathChange = useCallback((data) => {
    const entityType = activeRsaEntityTypeRef.current;
    const lane = activeRsaLaneRef.current;
    const path = activeRsaPathRef.current;

    if (entityType === 'lane' && lane?.id) {
      if (!data) {
        rsaPathsHook.actions.setLaneData(lane.id, null);
        return;
      }
      const prevProps = lane?.pathData?.properties && typeof lane.pathData.properties === 'object'
        ? lane.pathData.properties
        : {};
      const nextProps = data?.properties && typeof data.properties === 'object'
        ? data.properties
        : {};
      rsaPathsHook.actions.setLaneData(lane.id, {
        ...data,
        properties: {
          ...prevProps,
          ...nextProps,
          generatedLane: true,
          ...(lane.creationMode === 'manual' ? { manualLane: true } : {}),
          ...(lane.sourcePathId ? { sourcePathId: lane.sourcePathId } : {}),
          ...(lane.side ? { side: lane.side } : {}),
          ...(lane.offsetFeet != null ? { offsetFeet: lane.offsetFeet } : {}),
          ...(lane.directionMode ? { directionMode: lane.directionMode } : {}),
        },
      });
      return;
    }

    if (path?.id) {
      if (!data) {
        rsaPathsHook.actions.setPathData(path.id, null);
        return;
      }
      const prevProps = path?.pathData?.properties && typeof path.pathData.properties === 'object'
        ? path.pathData.properties
        : {};
      const nextProps = data?.properties && typeof data.properties === 'object'
        ? data.properties
        : {};
      rsaPathsHook.actions.setPathData(path.id, {
        ...data,
        properties: {
          ...prevProps,
          ...nextProps,
        },
      });
    }
  }, [rsaPathsHook.actions]); // refs are stable; only actions needed in deps

  /**
   * createOffsetLaneForPath — new-model variant of createOffsetLane.
   * Accepts { sourcePathId, directionMode, side, offsetFeet } (RsaPath IDs, not array indices).
   * Computes the same WKT offset geometry then saves the result into rsaPathsHook.
   */
  const createOffsetLaneForPath = useCallback(async ({ sourcePathId, directionMode, side, offsetFeet: offsetFeetRaw }) => {
    const sourcePath = rsaPathsHook.paths.find((p) => p.id === sourcePathId);
    const sourceCoords = sourcePath?.pathData?.geometry?.coordinates;
    if (!Array.isArray(sourceCoords) || sourceCoords.length < 2) {
      showNotification('Selected source path has no line geometry.');
      return;
    }
    const wkt = normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim());
    if (!wkt) {
      showNotification('Enter WKT CRS in Setup -> Local (x, y + WKT) before creating lane offsets.');
      return;
    }
    const parsedOffsetFeet = Number(offsetFeetRaw);
    if (!Number.isFinite(parsedOffsetFeet) || parsedOffsetFeet < 0) {
      showNotification('Offset must be zero or greater in feet.');
      return;
    }
    const baseLatLng = sourceCoords
      .map((c) => {
        const lng = Array.isArray(c) ? Number(c[0]) : Number(c?.lng);
        const lat = Array.isArray(c) ? Number(c[1]) : Number(c?.lat);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);
    if (baseLatLng.length < 2) { showNotification('Source path coordinates are invalid.'); return; }

    const orderedLatLng = directionMode === 'reverse' ? [...baseLatLng].reverse() : baseLatLng;
    const proj4FromValidate = wktValidation?.crs?.proj4;
    const projected = await latLngToProjectedXYAsync(orderedLatLng, wkt, 0, proj4FromValidate);
    if (!Array.isArray(projected) || projected.length < 2) { showNotification('Failed to project source path for lane offset.'); return; }

    const projectedXY = projected.map((p) => [Number(p?.[0]), Number(p?.[1])]).filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
    if (projectedXY.length < 2) { showNotification('Projected source path is invalid.'); return; }

    const unitsPerFoot = getProjectedUnitsPerFoot({ wkt, proj4String: proj4FromValidate });
    const absOffsetUnits = Math.abs(parsedOffsetFeet) * unitsPerFoot;
    const signedOffsetUnits = (side === 'right' ? -1 : 1) * absOffsetUnits;
    const offsetXY = offsetPolylineXY(projectedXY, signedOffsetUnits);
    if (offsetXY.length < 2) { showNotification('Could not compute offset lane geometry.'); return; }

    const offsetLatLng = await projectedPointsToLatLngAsync(offsetXY, wkt, proj4FromValidate);
    if (!Array.isArray(offsetLatLng) || offsetLatLng.length < 2) { showNotification('Could not transform offset lane back to map coordinates.'); return; }

    const sampledWaypoints = sampleLaneWaypoints(offsetLatLng);
    const sideLabel = side === 'right' ? 'Right Shoulder' : 'Left Shoulder';
    rsaPathsHook.actions.addLane(sourcePath.id, {
      laneType: side === 'right' ? 'right-shoulder' : 'left-shoulder',
      label: sideLabel,
      waypoints: sampledWaypoints,
      pathData: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: offsetLatLng.map((p) => [p.lng, p.lat]) },
        properties: { generatedLane: true, sourcePathId, side, offsetFeet: parsedOffsetFeet, directionMode },
      },
      creationMode: 'offset',
      sourcePathId,
      side,
      offsetFeet: parsedOffsetFeet,
      directionMode,
    });
    showNotification(`Created ${sideLabel} lane from "${sourcePath.label}" (${parsedOffsetFeet} ft).`);
  }, [rsaPathsHook.paths, rsaPathsHook.lanes, rsaPathsHook.actions, mapOverlayLocalConfig?.wkt, wktValidation?.crs?.proj4, showNotification]);

  const startManualLaneCreation = useCallback((pathIdFromUi) => {
    const existingManualLane = activeManualLaneId
      ? rsaPathsHook.lanes.find((lane) => lane.id === activeManualLaneId && lane.creationMode === 'manual')
      : null;

    if (existingManualLane) {
      rsaPathsHook.actions.setActiveLane(existingManualLane.id);
      if (!manualLaneCreationActive) {
        setManualLaneCreationActive(true);
        showNotification('Manual lane mode enabled. Continue clicking on the map to extend the same lane.');
      }
      return;
    }

    const parentPathId =
      pathIdFromUi
      || activeRsaPath?.id
      || rsaPathsHook.paths[0]?.id
      || null;
    if (!parentPathId) {
      showNotification('Add and select a path before starting a manual lane.');
      return;
    }

    const createdLaneId = rsaPathsHook.actions.addLane(parentPathId, {
      laneType: 'custom',
      label: `Manual Lane ${(rsaPathsHook.lanes.length || 0) + 1}`,
      creationMode: 'manual',
      sourcePathId: parentPathId,
      waypoints: [],
      pathData: null,
    });
    setActiveManualLaneId(createdLaneId);
    setManualLaneCreationActive(true);
    showNotification('Manual lane mode enabled. Click on the map to add lane points.');
  }, [
    activeManualLaneId,
    activeRsaPath,
    manualLaneCreationActive,
    rsaPathsHook.actions,
    rsaPathsHook.lanes,
    rsaPathsHook.paths,
    showNotification,
  ]);

  const stopManualLaneCreation = useCallback(() => {
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
    showNotification('Manual lane mode stopped.');
  }, [showNotification]);

  useEffect(() => {
    if (activeManualLaneId == null) return;
    const lane = rsaPathsHook.lanes.find((entry) => entry.id === activeManualLaneId);
    if (lane?.creationMode === 'manual') return;
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
  }, [rsaPathsHook.lanes, activeManualLaneId]);

  useEffect(() => {
    if (pathsPanelMode === 'path-creator') return;
    if (!manualLaneCreationActive) return;
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
  }, [pathsPanelMode, manualLaneCreationActive]);

  const fullPathPathData = null;
  const isGeneratedLaneActive = activeRsaEntityType === 'lane' && Boolean(activeRsaLane);
  const isManualLaneEditingActive =
    manualLaneCreationActive &&
    activeManualLaneId != null &&
    activeRsaLane?.id === activeManualLaneId &&
    activeRsaLane?.creationMode === 'manual';
  const activeManualLanePointCount = useMemo(() => {
    if (activeManualLaneId == null) return 0;
    const lane = rsaPathsHook.lanes.find((entry) => entry.id === activeManualLaneId);
    const points = lane?.waypoints;
    return Array.isArray(points) ? points.length : 0;
  }, [rsaPathsHook.lanes, activeManualLaneId]);
  const persistentLaneOverlays = useMemo(
    () =>
      rsaLegacySegments
        .map((seg, idx) => ({ seg, idx }))
        .filter(
          ({ seg, idx }) =>
            idx !== activeRsaLegacyIndex &&
            seg?.visible !== false &&
            Array.isArray(seg?.pathData?.geometry?.coordinates) &&
            seg.pathData.geometry.coordinates.length >= 2
        )
        .map(({ seg, idx }) => ({
          pathData: seg.pathData,
          color: SEGMENT_COLORS[(seg.colorIndex ?? idx) % SEGMENT_COLORS.length],
        })),
    [rsaLegacySegments, activeRsaLegacyIndex]
  );
  const latestRoutesUploadTime = useMemo(() => {
    const latest = (routesSourceFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  }, [routesSourceFiles]);
  const latestModelUploadTime = useMemo(() => {
    const latest = (modelFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  }, [modelFiles]);
  const latestExistingSourceUploadTime = useMemo(() => {
    const latest = (existingSourceFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || '';
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime() ? stamp : best;
    }, '');
    return latest ? formatUploadTimestamp(latest) : 'No uploads yet';
  }, [existingSourceFiles]);
  const hasExistingRoutesReady = useMemo(
    () => rsaLegacySegments.some((segment) => (
      (Array.isArray(segment?.pathData?.geometry?.coordinates) && segment.pathData.geometry.coordinates.length >= 2)
      || (Array.isArray(segment?.waypoints) && segment.waypoints.length >= 2)
    )),
    [rsaLegacySegments]
  );
  const canProceedToSetup = isRsaModelProject
    ? routesXmlFiles.length > 0 && modelFiles.length > 0
    : existingSourceFiles.length > 0;
  const existingStepComplete = useMemo(() => ({
    upload: existingSourceFiles.length > 0 || routesXmlFiles.length > 0,
    setup: hasVisitedSetupTab && wktInputLocked && Boolean(String(mapOverlayLocalConfig?.wkt ?? '').trim()),
    paths: hasExistingRoutesReady,
    analysis: hasSubmittedRsaRun,
  }), [
    existingSourceFiles.length,
    routesXmlFiles.length,
    hasExistingRoutesReady,
    hasSubmittedRsaRun,
    hasVisitedSetupTab,
    mapOverlayLocalConfig?.wkt,
    wktInputLocked,
  ]);

  return (
    <RsaPathsContext.Provider value={{ ...rsaPathsHook, SEGMENT_COLORS }}>
    <div className="w100RSA">
      {/* Slide-down notification from top */}
      {notification.message && (
        <div
          className={`rsa-slide-notification ${notification.leaving ? 'rsa-slide-notification-leaving' : ''}`}
          role="alert"
        >
          <img
            src={rdvShadowImage}
            alt="Notification icon"
            className="rsa-slide-notification-icon"
          />
          <span className="rsa-slide-notification-message">{notification.message}</span>
        </div>
      )}
      <div className={`main-content-area ${activeMainTab === 'dashboard' ? 'main-content-area-full-width' : ''}`}>
        {/* Non-developers on a gated tab: useEffect above switches to Analysis - no Navigate to /projects */}
        {/* Navigation: wizard stepper for RSA model, classic tabs for other project types */}
        {isRsaModelProject ? (
          (() => {
            const wizardSteps = [
              { key: 'upload',    label: 'Upload Files',   hint: 'Add your route XML files', iconClass: 'fa-solid fa-arrow-up-from-bracket' },
              { key: 'setup',     label: 'Setup',      hint: 'Set coordinate system (WKT)', iconClass: 'fa-solid fa-gear' },
              { key: 'routes',    label: 'Tasks & Routes',  hint: 'Select analyses and assign files', iconClass: 'fa-solid fa-list-check' },
              { key: 'dashboard', label: 'Dashboard',  hint: 'Run analysis and view results', iconClass: 'fa-solid fa-chart-pie' },
              { key: 'deliverables', label: 'Deliverables', hint: 'Browse generated output files' },
            ];
            const stepComplete = {
              upload:    routesXmlFiles.length > 0,
              setup:     hasVisitedSetupTab && wktInputLocked && Boolean(mapOverlayLocalConfig?.wkt),
              routes:    hasSubmittedRsaRun,
              dashboard: false,
            };
            return (
              <div className="rsa-wizard-stepper">
                {wizardSteps.map((step, idx) => {
                  const isActive = activeMainTab === step.key;
                  const isDone = stepComplete[step.key];
                  return (
                    <React.Fragment key={step.key}>
                      <button
                        type="button"
                        className={`rsa-wizard-step${isActive ? ' rsa-wizard-step-active' : ''}${isDone ? ' rsa-wizard-step-done' : ''}${idx === 0 ? ' rsa-wizard-step-first' : ''}${idx === wizardSteps.length - 1 ? ' rsa-wizard-step-last' : ''}${step.key === 'dashboard' ? ' rsa-wizard-step-dashboard' : ''}${step.key === 'deliverables' ? ' rsa-wizard-step-deliverables' : ''}`}
                        onClick={() => setActiveMainTab(step.key)}
                        title={step.hint}
                      >
                        <span className="rsa-wizard-step-label">
                          {step.iconClass && (
                            <i
                              className={`rsa-wizard-step-icon rsa-wizard-step-icon-step ${step.iconClass}`}
                              aria-hidden
                            />
                          )}
                          {step.key === 'deliverables' && <i className="fa-solid fa-cube" aria-hidden />}
                          {step.label}
                        </span>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <div className="main-content-tabs">
            {/* Other project types: Upload - Setup - Paths - Analysis - Dashboard */}
            <button
              type="button"
              className={`main-content-tab main-content-tab-upload ${activeMainTab === 'upload' ? 'active' : ''}${existingStepComplete.upload ? ' main-content-tab-done' : ''}`}
              onClick={() => setActiveMainTab('upload')}
            >
              <i className="fa-solid fa-arrow-up-from-bracket" />
              <span>Upload Files</span>
            </button>
            <button
              type="button"
              className={`main-content-tab main-content-tab-setup ${activeMainTab === 'setup' ? 'active' : ''}${existingStepComplete.setup ? ' main-content-tab-done' : ''}`}
              onClick={() => setActiveMainTab('setup')}
            >
              <i className="fa-solid fa-gear" />
              <span>Setup</span>
            </button>
            <button
              type="button"
              className={`main-content-tab main-content-tab-paths ${activeMainTab === 'paths' ? 'active' : ''}${existingStepComplete.paths ? ' main-content-tab-done' : ''}`}
              onClick={() => setActiveMainTab('paths')}
            >
              <i className="fa-solid fa-map-location-dot" />
              <span>Routes</span>
            </button>
            <button
              type="button"
              className={`main-content-tab main-content-tab-analysis ${activeMainTab === 'analysis' ? 'active' : ''}${existingStepComplete.analysis ? ' main-content-tab-done' : ''}`}
              onClick={() => setActiveMainTab('analysis')}
            >
              <i className="fas fa-chart-line" />
              <span>Analysis</span>
            </button>
            <button
              type="button"
              className={`main-content-tab main-content-tab-dashboard ${activeMainTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('dashboard')}
            >
              <i className="fas fa-chart-pie" />
              <span>Dashboard</span>
            </button>
            <button
              type="button"
              className={`main-content-tab main-content-tab-deliverables ${activeMainTab === 'deliverables' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('deliverables')}
            >
              <i className="fa-solid fa-cube" />
              <span>Deliverables</span>
            </button>
          </div>
        )}
        
        {/* Tab Content Container */}
        <div className="main-content-tabs-container">

          {/* ── Upload Files tab panel ──────────────────── */}
          <div className={`main-content-tab-panel main-content-tab-panel-upload rsa-wizard-step-page${activeMainTab === 'upload' ? ' active' : ''}`}>
              <div className="rsa-upload-step-panel rsa-wizard-step-content">
                <div className="rsa-upload-step-body">
                  {isRsaModelProject ? (
                    <>
                  <div className="rsa-upload-step-section">
                    <button
                      type="button"
                      className={`rsa-upload-step-status rsa-upload-step-status-toggle${routesStatusExpanded ? ' expanded' : ''}`}
                      onClick={() => setRoutesStatusExpanded((prev) => !prev)}
                      aria-expanded={routesStatusExpanded}
                    >
                      <div className="rsa-upload-step-status-copy">
                        <div className="rsa-upload-step-status-copy-header">
                          <span
                            className={`rsa-upload-step-status-chevron${routesStatusExpanded ? ' expanded' : ''}`}
                            aria-hidden="true"
                          >
                            <i className="fas fa-chevron-down" aria-hidden />
                          </span>
                          <p className="rsa-upload-step-heading">Upload your route files</p>
                        </div>
                        <p className="rsa-upload-step-hint">
                          Upload DXF files into the project's <strong>Source / Routes</strong> folder.
                        </p>
                      </div>
                      <span className="rsa-upload-step-status-row">
                        <span className="rsa-upload-step-status-summary">
                        {routesMatrixLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin rsa-upload-step-status-icon" aria-hidden />
                            <span className="rsa-upload-step-status-text">Checking Source / Routes…</span>
                          </>
                        ) : routesSourceFiles.length > 0 ? (
                          <>
                            <i className="fas fa-circle-check rsa-upload-step-status-icon ready" aria-hidden />
                            <span className="rsa-upload-step-status-text">
                              {routesXmlFiles.length} Route Files, {routesSourceFiles.filter((file) => isDxfFileName(file.name)).length} DXF
                              <span className="rsa-upload-step-status-subtext">Latest upload: {latestRoutesUploadTime}</span>
                            </span>
                            <span className="rsa-upload-step-status-count">{routesSourceFiles.length} file{routesSourceFiles.length !== 1 ? 's' : ''}</span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty" aria-hidden />
                            <span className="rsa-upload-step-status-text">
                              No files found in Source / Routes yet
                              <span className="rsa-upload-step-status-subtext">Latest upload: {latestRoutesUploadTime}</span>
                            </span>
                          </>
                        )}
                        </span>
                        <span className="rsa-upload-step-status-actions">
                          <span
                            className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRoutesUploadModal();
                            }}
                          >
                            <i className="fas fa-upload" aria-hidden /> Upload DXF / RSA Route Files
                          </span>
                        </span>
                      </span>
                    </button>

                    {!routesMatrixLoading && routesStatusExpanded && routesSourceFiles.length > 0 && (
                      <div className="rsa-upload-step-files-table-wrap">
                        <div className="rsa-upload-step-files-table-toolbar">
                          <div className="rsa-upload-step-files-table-toolbar-meta">
                            <span className="rsa-upload-step-files-table-toolbar-title">Source / Routes files</span>
                            <span className="rsa-upload-step-files-table-toolbar-subtext">
                              {selectedRouteUploadFiles.length > 0
                                ? `${selectedRouteUploadFiles.length} selected`
                                : 'Click rows to select multiple files'}
                            </span>
                          </div>
                          <div className="rsa-upload-step-files-table-toolbar-actions">
                            {selectedRouteUploadFiles.length >= 2 && (
                              <button
                                type="button"
                                className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                onClick={() => handleRequestUploadFileDelete(selectedRouteUploadFiles, routesFolderId, 'Source / Routes')}
                                disabled={uploadDeleteBusy}
                              >
                                Remove selected<i className="fas fa-trash" aria-hidden />
                              </button>
                            )}
                          </div>
                        </div>
                        <table className="rsa-upload-step-files-table">
                          <thead>
                            <tr>
                              <th className="rsa-upload-step-files-select-col">
                                <button
                                  type="button"
                                  className={`rsa-upload-step-files-select-toggle${
                                    selectedRouteUploadFiles.length === routesSourceFiles.length ? ' is-selected' : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleAllUploadTableFiles('routes', routesSourceFiles);
                                  }}
                                  disabled={uploadDeleteBusy}
                                  aria-label={selectedRouteUploadFiles.length === routesSourceFiles.length ? 'Clear all selected route files' : 'Select all route files'}
                                >
                                  <i className="fas fa-check" aria-hidden />
                                </button>
                              </th>
                              <th className="rsa-upload-step-files-index-col">#</th>
                              <th>File</th>
                              <th className="rsa-upload-step-files-type-col">Type</th>
                              <th className="rsa-upload-step-files-state-col">State</th>
                              <th className="rsa-upload-step-files-time-col">Uploaded</th>
                              <th className="rsa-upload-step-files-action-col">Remove</th>
                            </tr>
                          </thead>
                          <tbody>
                            {routesSourceFiles.map((routeFile, index) => {
                              const routeFileId = normalizeDriveFolderId(routeFile.googleDriveId);
                              const isSelected = (uploadTableSelection.routes || []).includes(routeFileId);
                              const routeFileState = isXmlFileName(routeFile?.name)
                                ? (routesStateByDriveId[routeFileId] || '...')
                                : '-';
                              return (
                              <tr
                                key={routeFile.googleDriveId || routeFile.id || routeFile.name || index}
                                className={`rsa-upload-step-files-row${isSelected ? ' is-selected' : ''}`}
                                onClick={() => toggleUploadTableFileSelection('routes', routeFile)}
                                aria-selected={isSelected}
                              >
                                <td className="rsa-upload-step-files-select-cell">
                                  <button
                                    type="button"
                                    className={`rsa-upload-step-files-select-toggle${isSelected ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleUploadTableFileSelection('routes', routeFile);
                                    }}
                                    disabled={uploadDeleteBusy}
                                    aria-label={isSelected ? `Deselect ${routeFile.name}` : `Select ${routeFile.name}`}
                                  >
                                    <i className="fas fa-check" aria-hidden />
                                  </button>
                                </td>
                                <td className="rsa-upload-step-files-index-cell">{index + 1}</td>
                                <td className="rsa-upload-step-files-name-cell" title={routeFile.name}>
                                  {getDisplayFileName(routeFile.name)}
                                </td>
                                <td>{isDxfFileName(routeFile.name) ? 'DXF' : 'XML'}</td>
                                <td className="rsa-upload-step-files-state-cell" title={routeFileState}>
                                  {routeFileState}
                                </td>
                                <td className="rsa-upload-step-files-time-cell">
                                  {formatUploadTimestamp(routeFile.modified || routeFile.created)}
                                </td>
                                <td className="rsa-upload-step-files-action-cell">
                                  <button
                                    type="button"
                                    className="rsa-upload-step-files-delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRequestUploadFileDelete(routeFile, routesFolderId, 'Source / Routes');
                                    }}
                                    disabled={uploadDeleteBusy}
                                    title={`Delete ${routeFile.name}`}
                                    aria-label={`Delete ${routeFile.name}`}
                                  >
                                    <i
                                      className={`fas ${
                                        uploadFileDeletePendingIds.includes(routeFileId)
                                          ? 'fa-spinner fa-spin'
                                          : 'fa-trash'
                                      }`}
                                      aria-hidden
                                    />
                                  </button>
                                </td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="rsa-upload-step-section" style={{ order: 1 }}>
                    <button
                      type="button"
                      className={`rsa-upload-step-status rsa-upload-step-status-toggle${modelStatusExpanded ? ' expanded' : ''}`}
                      onClick={() => setModelStatusExpanded((prev) => !prev)}
                      aria-expanded={modelStatusExpanded}
                    >
                      <div className="rsa-upload-step-status-copy">
                        <div className="rsa-upload-step-status-copy-header">
                          <span
                            className={`rsa-upload-step-status-chevron${modelStatusExpanded ? ' expanded' : ''}`}
                            aria-hidden="true"
                          >
                            <i className="fas fa-chevron-down" aria-hidden />
                          </span>
                          <p className="rsa-upload-step-heading">Upload your model file</p>
                        </div>
                        <p className="rsa-upload-step-hint">
                          Upload the Model file into <strong>Source / Model</strong>.
                        </p>
                      </div>
                      <span className="rsa-upload-step-status-row">
                        <span className="rsa-upload-step-status-summary">
                        {routesMatrixLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin rsa-upload-step-status-icon" aria-hidden />
                            <span className="rsa-upload-step-status-text">Checking Source / Model…</span>
                          </>
                        ) : modelFolderId && modelFiles.length > 0 ? (
                          <>
                            <i className="fas fa-circle-check rsa-upload-step-status-icon ready" aria-hidden />
                            <span className="rsa-upload-step-status-text">
                              Model files detected
                              <span className="rsa-upload-step-status-subtext">Latest upload: {latestModelUploadTime}</span>
                            </span>
                            <span className="rsa-upload-step-status-count">{modelFiles.length} file{modelFiles.length !== 1 ? 's' : ''}</span>
                          </>
                        ) : modelFolderId ? (
                          <>
                            <i className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty" aria-hidden />
                            <span className="rsa-upload-step-status-text">
                              No files found in Source / Model yet
                              <span className="rsa-upload-step-status-subtext">Latest upload: {latestModelUploadTime}</span>
                            </span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty" aria-hidden />
                            <span className="rsa-upload-step-status-text">Source / Model folder was not found</span>
                          </>
                        )}
                        </span>
                        <span className="rsa-upload-step-status-actions">
                          <span
                            className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!routesMatrixLoading && modelFolderId) {
                                handleOpenModelUploadModal();
                              }
                            }}
                            title={modelFolderId ? 'Upload model files into Source / Model' : 'Source / Model folder was not found'}
                          >
                            <i className="fas fa-upload" aria-hidden /> Upload Model File
                          </span>
                        </span>
                      </span>
                    </button>

                    {!routesMatrixLoading && modelStatusExpanded && modelFiles.length > 0 && (
                      <div className="rsa-upload-step-files-table-wrap">
                        <div className="rsa-upload-step-files-table-toolbar">
                          <div className="rsa-upload-step-files-table-toolbar-meta">
                            <span className="rsa-upload-step-files-table-toolbar-title">Source / Model files</span>
                            <span className="rsa-upload-step-files-table-toolbar-subtext">
                              {selectedModelUploadFiles.length > 0
                                ? `${selectedModelUploadFiles.length} selected`
                                : 'Click rows to select multiple files'}
                            </span>
                          </div>
                          <div className="rsa-upload-step-files-table-toolbar-actions">
                            <button
                              type="button"
                              className="rsa-upload-step-files-toolbar-btn"
                              onClick={() => handleToggleAllUploadTableFiles('model', modelFiles)}
                              disabled={uploadDeleteBusy}
                            >
                              {selectedModelUploadFiles.length === modelFiles.length ? 'Clear all' : 'Select all'}
                            </button>
                            {selectedModelUploadFiles.length >= 2 && (
                              <button
                                type="button"
                                className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                onClick={() => handleRequestUploadFileDelete(selectedModelUploadFiles, modelFolderId, 'Source / Model')}
                                disabled={uploadDeleteBusy}
                              >
                                <i className="fas fa-trash" aria-hidden /> Remove selected
                              </button>
                            )}
                          </div>
                        </div>
                        <table className="rsa-upload-step-files-table">
                          <thead>
                            <tr>
                              <th className="rsa-upload-step-files-select-col">
                                <button
                                  type="button"
                                  className={`rsa-upload-step-files-select-toggle${
                                    selectedModelUploadFiles.length === modelFiles.length ? ' is-selected' : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleAllUploadTableFiles('model', modelFiles);
                                  }}
                                  disabled={uploadDeleteBusy}
                                  aria-label={selectedModelUploadFiles.length === modelFiles.length ? 'Clear all selected model files' : 'Select all model files'}
                                >
                                  <i className="fas fa-check" aria-hidden />
                                </button>
                              </th>
                              <th className="rsa-upload-step-files-index-col">#</th>
                              <th>Model file</th>
                              <th className="rsa-upload-step-files-time-col">Uploaded</th>
                              <th className="rsa-upload-step-files-action-col">Remove</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modelFiles.map((modelFile, index) => {
                              const modelFileId = normalizeDriveFolderId(modelFile.googleDriveId);
                              const isSelected = (uploadTableSelection.model || []).includes(modelFileId);
                              return (
                              <tr
                                key={modelFile.googleDriveId || modelFile.id || modelFile.name || index}
                                className={`rsa-upload-step-files-row${isSelected ? ' is-selected' : ''}`}
                                onClick={() => toggleUploadTableFileSelection('model', modelFile)}
                                aria-selected={isSelected}
                              >
                                <td className="rsa-upload-step-files-select-cell">
                                  <button
                                    type="button"
                                    className={`rsa-upload-step-files-select-toggle${isSelected ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleUploadTableFileSelection('model', modelFile);
                                    }}
                                    disabled={uploadDeleteBusy}
                                    aria-label={isSelected ? `Deselect ${modelFile.name}` : `Select ${modelFile.name}`}
                                  >
                                    <i className="fas fa-check" aria-hidden />
                                  </button>
                                </td>
                                <td className="rsa-upload-step-files-index-cell">{index + 1}</td>
                                <td className="rsa-upload-step-files-name-cell" title={modelFile.name}>
                                  {getDisplayFileName(modelFile.name)}
                                </td>
                                <td className="rsa-upload-step-files-time-cell">
                                  {formatUploadTimestamp(modelFile.modified || modelFile.created)}
                                </td>
                                <td className="rsa-upload-step-files-action-cell">
                                  <button
                                    type="button"
                                    className="rsa-upload-step-files-delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRequestUploadFileDelete(modelFile, modelFolderId, 'Source / Model');
                                    }}
                                    disabled={uploadDeleteBusy}
                                    title={`Delete ${modelFile.name}`}
                                    aria-label={`Delete ${modelFile.name}`}
                                  >
                                    <i
                                      className={`fas ${
                                        uploadFileDeletePendingIds.includes(modelFileId)
                                          ? 'fa-spinner fa-spin'
                                          : 'fa-trash'
                                      }`}
                                      aria-hidden
                                    />
                                  </button>
                                </td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                    </>
                  ) : (
                    <div className="rsa-upload-step-section">
                      <button
                        type="button"
                        className={`rsa-upload-step-status rsa-upload-step-status-toggle${existingSourceStatusExpanded ? ' expanded' : ''}`}
                        onClick={() => setExistingSourceStatusExpanded((prev) => !prev)}
                        aria-expanded={existingSourceStatusExpanded}
                      >
                        <div className="rsa-upload-step-status-copy">
                          <div className="rsa-upload-step-status-copy-header">
                            <span
                              className={`rsa-upload-step-status-chevron${existingSourceStatusExpanded ? ' expanded' : ''}`}
                              aria-hidden="true"
                            >
                              <i className="fas fa-chevron-down" aria-hidden />
                            </span>
                            <p className="rsa-upload-step-heading">Upload your LAS/LAZ files</p>
                          </div>
                          <p className="rsa-upload-step-hint">
                            Upload LAS/LAZ files into the project's <strong>Source</strong> folder.
                          </p>
                        </div>
                        <span className="rsa-upload-step-status-row">
                          <span className="rsa-upload-step-status-summary">
                          {existingSourceLoading ? (
                            <>
                              <i className="fas fa-spinner fa-spin rsa-upload-step-status-icon" aria-hidden />
                              <span className="rsa-upload-step-status-text">Checking Source…</span>
                            </>
                          ) : existingSourceFolderId && existingSourceFiles.length > 0 ? (
                            <>
                              <i className="fas fa-circle-check rsa-upload-step-status-icon ready" aria-hidden />
                              <span className="rsa-upload-step-status-text">
                                LAS/LAZ files detected
                                <span className="rsa-upload-step-status-subtext">Latest upload: {latestExistingSourceUploadTime}</span>
                              </span>
                              <span className="rsa-upload-step-status-count">{existingSourceFiles.length} file{existingSourceFiles.length !== 1 ? 's' : ''}</span>
                            </>
                          ) : existingSourceFolderId ? (
                            <>
                              <i className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty" aria-hidden />
                              <span className="rsa-upload-step-status-text">
                                No LAS/LAZ files found in Source yet
                                <span className="rsa-upload-step-status-subtext">Latest upload: {latestExistingSourceUploadTime}</span>
                              </span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty" aria-hidden />
                              <span className="rsa-upload-step-status-text">Source folder was not found</span>
                            </>
                          )}
                          </span>
                          <span className="rsa-upload-step-status-actions">
                            <span
                              className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                              role="button"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!existingSourceLoading && existingSourceFolderId) {
                                  handleOpenExistingSourceUploadModal();
                                }
                              }}
                              title={existingSourceFolderId ? 'Upload LAS/LAZ files into Source' : 'Source folder was not found'}
                            >
                              <i className="fas fa-upload" aria-hidden /> Upload LAS / LAZ Files
                            </span>
                          </span>
                        </span>
                      </button>

                      {!existingSourceLoading && existingSourceStatusExpanded && existingSourceFiles.length > 0 && (
                        <div className="rsa-upload-step-files-table-wrap">
                          <div className="rsa-upload-step-files-table-toolbar">
                            <div className="rsa-upload-step-files-table-toolbar-meta">
                              <span className="rsa-upload-step-files-table-toolbar-title">Source files</span>
                              <span className="rsa-upload-step-files-table-toolbar-subtext">
                                {selectedExistingSourceUploadFiles.length > 0
                                  ? `${selectedExistingSourceUploadFiles.length} selected`
                                  : 'Click rows to select multiple files'}
                              </span>
                            </div>
                            <div className="rsa-upload-step-files-table-toolbar-actions">
                              <button
                                type="button"
                                className="rsa-upload-step-files-toolbar-btn"
                                onClick={() => handleToggleAllUploadTableFiles('source', existingSourceFiles)}
                                disabled={uploadDeleteBusy}
                              >
                                {selectedExistingSourceUploadFiles.length === existingSourceFiles.length ? 'Clear all' : 'Select all'}
                              </button>
                              {selectedExistingSourceUploadFiles.length >= 2 && (
                                <button
                                  type="button"
                                  className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                  onClick={() => handleRequestUploadFileDelete(selectedExistingSourceUploadFiles, existingSourceFolderId, 'Source')}
                                  disabled={uploadDeleteBusy}
                                >
                                  <i className="fas fa-trash" aria-hidden /> Remove selected
                                </button>
                              )}
                            </div>
                          </div>
                          <table className="rsa-upload-step-files-table">
                            <thead>
                              <tr>
                                <th className="rsa-upload-step-files-select-col">
                                  <button
                                    type="button"
                                    className={`rsa-upload-step-files-select-toggle${
                                      selectedExistingSourceUploadFiles.length === existingSourceFiles.length ? ' is-selected' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleAllUploadTableFiles('source', existingSourceFiles);
                                    }}
                                    disabled={uploadDeleteBusy}
                                    aria-label={selectedExistingSourceUploadFiles.length === existingSourceFiles.length ? 'Clear all selected source files' : 'Select all source files'}
                                  >
                                    <i className="fas fa-check" aria-hidden />
                                  </button>
                                </th>
                                <th className="rsa-upload-step-files-index-col">#</th>
                                <th>File</th>
                                <th className="rsa-upload-step-files-type-col">Type</th>
                                <th className="rsa-upload-step-files-time-col">Uploaded</th>
                                <th className="rsa-upload-step-files-action-col">Remove</th>
                              </tr>
                            </thead>
                            <tbody>
                              {existingSourceFiles.map((sourceFile, index) => {
                                const sourceFileId = normalizeDriveFolderId(sourceFile.googleDriveId);
                                const isSelected = (uploadTableSelection.source || []).includes(sourceFileId);
                                return (
                                <tr
                                  key={sourceFile.googleDriveId || sourceFile.id || sourceFile.name || index}
                                  className={`rsa-upload-step-files-row${isSelected ? ' is-selected' : ''}`}
                                  onClick={() => toggleUploadTableFileSelection('source', sourceFile)}
                                  aria-selected={isSelected}
                                >
                                  <td className="rsa-upload-step-files-select-cell">
                                    <button
                                      type="button"
                                      className={`rsa-upload-step-files-select-toggle${isSelected ? ' is-selected' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleUploadTableFileSelection('source', sourceFile);
                                      }}
                                      disabled={uploadDeleteBusy}
                                      aria-label={isSelected ? `Deselect ${sourceFile.name}` : `Select ${sourceFile.name}`}
                                    >
                                      <i className="fas fa-check" aria-hidden />
                                    </button>
                                  </td>
                                  <td className="rsa-upload-step-files-index-cell">{index + 1}</td>
                                  <td className="rsa-upload-step-files-name-cell" title={sourceFile.name}>
                                    {sourceFile.name}
                                  </td>
                                  <td>{String(sourceFile.name || '').trim().toLowerCase().endsWith('.laz') ? 'LAZ' : 'LAS'}</td>
                                  <td className="rsa-upload-step-files-time-cell">
                                    {formatUploadTimestamp(sourceFile.modified || sourceFile.created)}
                                  </td>
                                  <td className="rsa-upload-step-files-action-cell">
                                    <button
                                      type="button"
                                      className="rsa-upload-step-files-delete-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestUploadFileDelete(sourceFile, existingSourceFolderId, 'Source');
                                      }}
                                      disabled={uploadDeleteBusy}
                                      title={`Delete ${sourceFile.name}`}
                                      aria-label={`Delete ${sourceFile.name}`}
                                    >
                                      <i
                                        className={`fas ${
                                          uploadFileDeletePendingIds.includes(sourceFileId)
                                            ? 'fa-spinner fa-spin'
                                            : 'fa-trash'
                                        }`}
                                        aria-hidden
                                      />
                                    </button>
                                  </td>
                                </tr>
                              );})}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rsa-step-footer rsa-step-footer--upload">
                  <div className="rsa-step-footer-group">
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                      disabled
                      aria-disabled="true"
                      title="This is the first step"
                    >
                      <i className="fas fa-arrow-left" aria-hidden /> Back
                    </button>
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                      onClick={() => setActiveMainTab('setup')}
                      disabled={!canProceedToSetup}
                      title={canProceedToSetup
                        ? 'Continue to setup'
                        : (isRsaModelProject
                            ? 'Upload route XML files and at least one model file first'
                            : 'Upload at least one LAS/LAZ file into Source first')}
                    >
                      Next: Setup <i className="fas fa-arrow-right" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload modal targeting Source/Routes */}
              <UploadModal
                isOpen={showRoutesUploadModal}
                onClose={() => setShowRoutesUploadModal(false)}
                onUpload={async () => {
                  setShowRoutesUploadModal(false);
                  await refreshRoutesTabDataAfterUpload();
                }}
                projectId={effectiveProjectId}
                currentFolderId={routesFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={routesFolderId || null}
                projectName={null}
                currentFolderName="Source / Routes"
                warnOnExistingRouteXml
              />
              <UploadModal
                isOpen={showModelUploadModal}
                onClose={() => setShowModelUploadModal(false)}
                onUpload={async () => {
                  setShowModelUploadModal(false);
                  await refreshRoutesTabDataAfterUpload();
                }}
                projectId={effectiveProjectId}
                currentFolderId={modelFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={modelFolderId || null}
                projectName={null}
                currentFolderName="Source / Model"
              />
              <UploadModal
                isOpen={showExistingSourceUploadModal}
                onClose={() => setShowExistingSourceUploadModal(false)}
                onUpload={async () => {
                  setShowExistingSourceUploadModal(false);
                  await refreshExistingSourceDataAfterUpload();
                }}
                projectId={effectiveProjectId}
                currentFolderId={existingSourceFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={existingSourceFolderId || null}
                projectName={null}
                currentFolderName="Source"
                accept=".las,.laz"
              />
              <DeleteConfirmModal
                isOpen={Boolean(uploadFileDeleteCandidate)}
                onClose={() => setUploadFileDeleteCandidate(null)}
                onConfirm={handleConfirmUploadFileDelete}
                items={Array.isArray(uploadFileDeleteCandidate?.files) ? uploadFileDeleteCandidate.files : []}
              />
            </div>

          {/* ── Analysis Tab Panel ──────────────────── */}
          <div className={`main-content-tab-panel main-content-tab-panel-analysis ${activeMainTab === 'analysis' ? 'active' : ''}`}>
            {isLoadingVisibleTaskOptions && (
              <div
                className="analysis-tab-panel-loading"
                role="status"
                aria-live="polite"
                aria-busy="true"

              >
                <i className="fas fa-spinner fa-spin analysis-tab-panel-loading-spinner" aria-hidden />
                <span className="analysis-tab-panel-loading-text" style={{ paddingLeft: '10px' }}>Loading project analysis options...</span>
              </div>
            )}
            <div className="tab-content-RSA">
         {/* Toggle Buttons for Required Services */}
     <div className="tasks-selection-container">
      <p className='pHeader'>Select Required Tasks:</p>
                 <div className="tasks-buttons-container">
        {!isLoadingVisibleTaskOptions && visibleTaskOptions.length === 0 && (
          <p className="summary-empty">No RSA task folders were found under Source.</p>
        )}
        {!isLoadingVisibleTaskOptions && visibleTaskOptions.map((type) => {
          const templateId = analysisTemplateIdMapping[type];
          const isSelected = selectedTasks.includes(type);
          const taskImage = TASK_IMAGE_CONFIG[type]?.image || null;
          const taskInfoImage = TASK_INFO_IMAGE_CONFIG[type] || null;
          
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                if (templateId) {
                  if (isSelected) {
                    setSelectedTasks(prev => prev.filter(t => t !== type));
                    // Also remove from services for backward compatibility
                    const serviceValue = serviceMapping[type];
                    if (serviceValue) {
                      setServices(prev => prev.filter(s => s !== serviceValue));
                    }
                  } else {
                    setSelectedTasks(prev => [...prev, type]);
                    // Also add to services for backward compatibility
                    const serviceValue = serviceMapping[type];
                    if (serviceValue) {
                      setServices(prev => [...prev, serviceValue]);
                    }
                  }
                } else {
                  console.warn(`No analysis template ID found for: ${type}`);
                }
              }}
              className={`toggle-btn-rsa task-button ${isSelected ? 'selected' : ''} ${taskImage ? 'task-button-with-image' : ''}`}
              title={type}
            >
              {taskImage ? (
                <>
                  <img src={taskImage} alt={type} className="task-button-image" loading="lazy" />
                  {isSelected && (
                    <span className="task-button-selected-icon" aria-hidden="true">
                      <i className="fa-solid fa-circle-check"></i>
                    </span>
                  )}
                  <span className="task-button-image-label">{type}</span>
                </>
              ) : (
                type
              )}
            </button>
          );
        })}
      </div>
    </div>



      {/* Render the Folder Tree Popup Modal */}
        {showFolderTreePopup && (
          <FolderTreePopup
            onClose={() => setShowFolderTreePopup(false)}
            onFolderSelect={(folders) => setSelectedFolders(Array.isArray(folders) ? folders : [folders])}
            onDriveSelect={(driveName) => setSelectedDriveName(driveName)}
            storageConnection={selectedConnection}
            useSystemDrive={useSystemGoogleDrive}
            projectId={effectiveProjectId}
          />
        )}

        {/* Storage Connection Modal */}
        {showConnectionModal && connectionProvider && (
          <StorageConnectionModal
            onClose={() => {
              setShowConnectionModal(false);
              setConnectionProvider(null);
            }}
            provider={connectionProvider}
            onConnectionSuccess={() => {
              // Reload connections after successful connection
              loadStorageConnections();
            }}
          />
        )}

       {/* Render the Clear Confirmation Popup Modal */}
       {showClearPopup && (
         <div className="TreeModalOverlay">
           <div className="TreeModalContainer">
             <div className="modal-header">
               <h2 className="modal-title">Clear Form</h2>
             </div>
             
             <div className="modal-content">
               <p className="clear-popup-message">
                 Are you sure you want to completely clear the page? This action cannot be undone.
               </p>
             </div>

             <div className="modal-actions">
               <button className="modal-btn modal-btn-secondary" onClick={handleClearCancel}>
                 Cancel
               </button>
               <button className="modal-btn modal-btn-primary" onClick={handleClearConfirm}>
                 Yes, Clear All
               </button>
             </div>
           </div>
         </div>
       )}

       {taskInfoModal.isOpen && taskInfoModal.image && (
         <div
           className="rsa-modal-overlay task-info-modal-overlay"
           role="dialog"
           aria-modal="true"
           aria-labelledby="task-info-modal-title"
           onClick={closeTaskInfoModal}
         >
           <div className="rsa-modal task-info-modal" onClick={(e) => e.stopPropagation()}>
             <div className="task-info-modal-header">
               <h3 id="task-info-modal-title" className="task-info-modal-title">
                 {taskInfoModal.title}
               </h3>
               <button
                 type="button"
                 className="task-info-modal-close"
                 onClick={closeTaskInfoModal}
                 aria-label="Close info image"
               >
                 <i className="fa-solid fa-xmark" aria-hidden />
               </button>
             </div>
             <div className="task-info-modal-body">
               <img
                 src={taskInfoModal.image}
                 alt={`${taskInfoModal.title} reference`}
                 className="task-info-modal-image"
                 loading="lazy"
               />
             </div>
           </div>
         </div>
       )}
              {!isRsaModelProject && (
                <div className="rsa-step-footer rsa-step-footer--routes">
                  <div className="rsa-step-footer-group">
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                      onClick={() => setActiveMainTab('paths')}
                    >
                      <i className="fas fa-arrow-left" aria-hidden /> Back: Routes
                    </button>
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                      onClick={() => setActiveMainTab('dashboard')}
                      disabled={selectedTasks.length === 0}
                      title={selectedTasks.length === 0 ? 'Select at least one task to continue' : ''}
                    >
                      Next: Dashboard <i className="fas fa-arrow-right" aria-hidden />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isRsaModelProject && (
            <div className={`main-content-tab-panel main-content-tab-panel-routes rsa-wizard-step-page ${activeMainTab === 'routes' ? 'active' : ''}`}>
              <div className="tab-content-RSA tab-content-RSA-routes rsa-wizard-step-content">

                {isLoadingVisibleTaskOptions && (
                  <div className="analysis-tab-panel-loading" role="status" aria-live="polite" aria-busy="true"                 
                  style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%'
                }}>
                    <i className="fas fa-spinner fa-spin analysis-tab-panel-loading-spinner" aria-hidden />
                    <span className="analysis-tab-panel-loading-text" style={{ paddingLeft: '10px' }}>Loading analysis options…</span>
                  </div>
                )}
                {!isLoadingVisibleTaskOptions && visibleTaskOptions.length === 0 && (
                  <p className="summary-empty">No RSA task folders found under Source. Upload files and refresh.</p>
                )}
                <div className="tasks-buttons-container">
                  {!isLoadingVisibleTaskOptions && visibleTaskOptions.map((type) => {
                    const templateId = analysisTemplateIdMapping[type];
                    const isSelected = selectedTasks.includes(type);
                    const taskImage = TASK_IMAGE_CONFIG[type]?.image || null;
                    return (
                      <button
                        key={`step3-task-${type}`}
                        type="button"
                        onClick={() => {
                          if (templateId) {
                            if (isSelected) {
                              setSelectedTasks(prev => prev.filter(t => t !== type));
                              const serviceValue = serviceMapping[type];
                              if (serviceValue) setServices(prev => prev.filter(s => s !== serviceValue));
                            } else {
                              setSelectedTasks(prev => [...prev, type]);
                              const serviceValue = serviceMapping[type];
                              if (serviceValue) setServices(prev => [...prev, serviceValue]);
                            }
                          }
                        }}
                        className={`toggle-btn-rsa task-button ${isSelected ? 'selected' : ''} ${taskImage ? 'task-button-with-image' : ''}`}
                        title={type}
                      >
                        {taskImage ? (
                          <>
                            <img src={taskImage} alt={type} className="task-button-image" loading="lazy" />
                            {isSelected && (
                              <span className="task-button-selected-icon" aria-hidden="true">
                                <i className="fa-solid fa-circle-check" />
                              </span>
                            )}
                            <span className="task-button-image-label">{type}</span>
                          </>
                        ) : type}
                      </button>
                    );
                  })}
                </div>
                <div className="rsa-step-footer rsa-step-footer--routes">
                  <div className="rsa-step-footer-group">
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                      onClick={() => setActiveMainTab('setup')}
                    >
                      <i className="fas fa-arrow-left" aria-hidden /> Back: Setup
                    </button>
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                      onClick={() => setActiveMainTab('dashboard')}
                      disabled={selectedTasks.length === 0}
                      title={selectedTasks.length === 0 ? 'Select at least one task to continue' : ''}
                    >
                      Next: Dashboard <i className="fas fa-arrow-right" aria-hidden />
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Setup Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-setup rsa-wizard-step-page ${activeMainTab === 'setup' ? 'active' : ''}`}>
            {(preloadRsaSetupLoading || loadingSetupFromFolder) && (
              <div className="setup-section-loading-overlay" aria-busy="true">
                <i className="fas fa-spinner fa-spin setup-section-loading-spinner" aria-hidden />
                <span className="setup-section-loading-text">Loading Project Setup...</span>
              </div>
            )}
            <div className="tab-content-RSA rsa-wizard-step-content">
              <section className="setup-section setup-section-map-overlay">
                <h3 className="setup-section-title">Coordinate system setup</h3>

                <div className="setup-local-config">
                    <div className="setup-wkt-subtitle-row">
                      <h4 className="setup-subtitle">WKT (Well-known text)</h4>
                      <button
                        type="button"
                        className="setup-wkt-help-info-btn"
                        onClick={() => setWktRequirementsHelpModalOpen(true)}
                        aria-label="Supported coordinate system format (WKT) - open help"
                        title="Supported coordinate system format (WKT)"
                      >
                        <i className="fa-solid fa-circle-info" aria-hidden />
                      </button>
                    </div>
                    <div className="setup-wkt-input-wrap">
                      <textarea
                        className={`setup-wkt-input setup-input${wktInputLocked ? ' setup-wkt-input-locked' : ''}`}
                        placeholder="e.g. POLYGON((...)) or SRID / CRS description"
                        value={mapOverlayLocalConfig.wkt ?? ''}
                        onChange={(e) => setMapOverlayLocalConfig((c) => ({ ...c, wkt: e.target.value }))}
                        readOnly={wktInputLocked}
                        rows={3}
                      />
                      <div className="setup-wkt-actions">
                        {wktInputLocked ? (
                          <button type="button" className="setup-wkt-btn setup-wkt-btn-edit" onClick={() => setWktInputLocked(false)}><i className="fas fa-pen" aria-hidden /> Edit WKT</button>
                        ) : (
                          <button type="button" className="setup-wkt-btn setup-wkt-btn-save" onClick={handleSaveWktClick}><i className="fas fa-floppy-disk" aria-hidden /> Save WKT</button>
                        )}
                      </div>
                    </div>
                    {!isRsaModelProject && (
                      <div className="setup-existing-project-fields">
                        <label className="setup-existing-project-field">
                          <span className="setup-existing-project-field-label">State</span>
                          <input
                            type="text"
                            className="setup-input setup-existing-project-input"
                            value={mapOverlayLocalConfig.state ?? ''}
                            onChange={(e) => setMapOverlayLocalConfig((c) => ({ ...c, state: e.target.value }))}
                            placeholder="Enter state"
                          />
                        </label>
                      </div>
                    )}
                  </div>
              </section>

              {/* Save Setup overwrite confirmation modal */}
              {showSaveSetupOverwriteModal && (
                <div className="rsa-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rsa-save-setup-modal-title">
                  <div className="rsa-modal rsa-save-setup-modal">
                    <h3 id="rsa-save-setup-modal-title" className="rsa-modal-title">Overwrite project setup?</h3>
                    <p className="rsa-modal-message">
                      This action will overwrite the current project setup in the registry. Do you want to continue?
                    </p>
                    <div className="rsa-modal-actions">
                      <button type="button" className="setup-wkt-btn setup-wkt-btn-edit" onClick={handleSaveSetupOverwriteCancel}>
                        No
                      </button>
                      <button type="button" className="setup-wkt-btn setup-wkt-btn-save" onClick={handleSaveSetupOverwriteConfirm}>
                        Yes
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
            {/* Setup footer */}
            <div className="rsa-step-footer rsa-step-footer--setup">
              <div className="rsa-step-footer-group">
                <button
                  type="button"
                  className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                  onClick={() => setActiveMainTab('upload')}
                >
                  <i className="fas fa-arrow-left" aria-hidden /> Back: Upload Files
                </button>
                <button
                  type="button"
                  className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                  onClick={() => setActiveMainTab(isRsaModelProject ? 'routes' : 'paths')}
                >
                  {isRsaModelProject ? 'Next: Tasks & Routes' : 'Next: Paths'} <i className="fas fa-arrow-right" aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {!isRsaModelProject && (
            <>
              {/* Paths Tab Panel - PathSelectorMap only mounts when tab is active to avoid Loader singleton conflicts */}
              <div className={`main-content-tab-panel main-content-tab-panel-paths ${activeMainTab === 'paths' ? 'active' : ''}`}>
            <div className="tab-content-RSA tab-content-RSA-paths">
              <PathSelectorMap
                  pathsPanelMode={pathsPanelMode}
                  onPathsPanelModeChange={setPathsPanelMode}
                  waypoints={activeRsaWaypoints}
                  waypointColor={activeRsaColor}
                  fullPathStrokeColor={SEGMENT_COLORS[0]}
                  fullPathPathData={fullPathPathData}
                  segmentVisibility={rsaSegmentVisibility}
                  activeSegmentIndex={activeRsaLegacyIndex}
                  activeSegmentPathData={activeRsaPathData}
                  freezeActiveSegmentPath={isGeneratedLaneActive}
                  persistentPathOverlays={persistentLaneOverlays}
                  fitToPathData={fitToPathData}
                  onFitToPathComplete={() => setFitToPathData(null)}
                  fitToBounds={fitToBounds}
                  onFitToBoundsComplete={() => setFitToBounds(null)}
                  setWaypoints={pathSetWaypoints}
                  onPathChange={pathOnPathChange}
                  allowAddWaypointOnClick={
                    pathsPanelMode !== 'intersection'
                    && (!isGeneratedLaneActive || isManualLaneEditingActive)
                    && !rsaPathsHook.laneEditMode
                    && !rsaPathsHook.speedEditMode
                  }
                  manualSplineMode={isManualLaneEditingActive}
                  centerOnWaypointClick={isManualLaneEditingActive}
                  initialCenter={[38.8283, -98.5795]}
                  initialZoom={5}
                  isVisible={true}
                  imageOverlays={imageOverlaysForMap}
                  lasBoundingBoxes={lasBoundingBoxesVisible ? lasBoundingBoxes : []}
                  selectedLasBoundingBoxIndex={selectedLasBoundingBoxIndex}
                  overlayOpacity={orthoTilesVisible ? overlayOpacity : 0}
                  onImageOverlaysLoadProgress={onImageOverlaysLoadProgress}
                  intersectionDrawingMode={intersectionDrawingMode}
                  onIntersectionOverlayComplete={(payload) => setIntersectionOverlays((prev) => [...prev, payload])}
                  onIntersectionOverlayUpdate={(id, payload) => setIntersectionOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...payload } : o)))}
                  onIntersectionOverlayRemove={(id) => setIntersectionOverlays((prev) => prev.filter((o) => o.id !== id))}
                  intersectionOverlays={intersectionOverlays}
                  curvePoints={curvePoints}
                  onCurvedLinePointsChange={setCurvePoints}
                  measurePoints={measurePoints}
                  onMeasurePointsChange={setMeasurePoints}
                  curvedLineEditPointCount={curvedLineEditPointCount}
                  laneEditMode={rsaPathsHook.laneEditMode}
                  laneEditWaypoints={rsaPathsHook.laneEditMode && rsaPathsHook.activeLaneId
                    ? (rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId)?.waypoints ?? [])
                    : []}
                  laneEditSelectedIndices={rsaPathsHook.selectedLaneWaypointIndices}
                  onLaneEditSelectionChange={rsaPathsHook.actions.setSelectedLaneWaypoints}
                  onLaneEditWaypointsMoved={(primaryIdx, dLat, dLng) => {
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (lane?.waypoints) laneEditHistory.pushState(lane.waypoints);
                    rsaPathsHook.actions.moveSelectedLaneWaypoints(
                      rsaPathsHook.activeLaneId,
                      dLat,
                      dLng,
                      primaryIdx,
                    );
                  }}
                  onLaneEditWaypointInserted={(insertIdx, lat, lng) => {
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (lane?.waypoints) laneEditHistory.pushState(lane.waypoints);
                    rsaPathsHook.actions.insertLaneWaypoint(
                      rsaPathsHook.activeLaneId,
                      insertIdx,
                      lat,
                      lng,
                    );
                  }}
                  onLaneEditWaypointRemoved={(waypointIdx) => {
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (lane?.waypoints) laneEditHistory.pushState(lane.waypoints);
                    rsaPathsHook.actions.removeLaneWaypoint(
                      rsaPathsHook.activeLaneId,
                      waypointIdx,
                    );
                  }}
                  laneEditCanUndo={laneEditHistory.canUndo}
                  laneEditCanRedo={laneEditHistory.canRedo}
                  onLaneEditUndo={() => {
                    const prev = laneEditHistory.undo();
                    if (prev && rsaPathsHook.activeLaneId) {
                      rsaPathsHook.actions.updateLane(rsaPathsHook.activeLaneId, { waypoints: prev });
                      rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                    }
                  }}
                  onLaneEditRedo={() => {
                    const next = laneEditHistory.redo();
                    if (next && rsaPathsHook.activeLaneId) {
                      rsaPathsHook.actions.updateLane(rsaPathsHook.activeLaneId, { waypoints: next });
                      rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                    }
                  }}
                  onLaneEditDeleteSelected={() => {
                    const selected = rsaPathsHook.selectedLaneWaypointIndices;
                    if (!selected?.length || !rsaPathsHook.activeLaneId) return;
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (!lane?.waypoints) return;
                    laneEditHistory.pushState(lane.waypoints);
                    const toRemove = new Set(selected);
                    const next = lane.waypoints.filter((_, i) => !toRemove.has(i));
                    rsaPathsHook.actions.updateLane(rsaPathsHook.activeLaneId, { waypoints: next });
                    rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                  }}
                  onLaneEditSelectAll={() => {
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (lane?.waypoints?.length) {
                      rsaPathsHook.actions.setSelectedLaneWaypoints(
                        lane.waypoints.map((_, i) => i)
                      );
                    }
                  }}
                  speedEditMode={rsaPathsHook.speedEditMode}
                  speedEditWaypoints={(() => {
                    if (!rsaPathsHook.speedEditMode || !rsaPathsHook.activeLaneId) return [];
                    const lane = rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId);
                    if (!lane) return [];
                    // Use dense pathData coordinates when available (matches the visible polyline)
                    const coords = lane.pathData?.geometry?.coordinates;
                    if (Array.isArray(coords) && coords.length >= 2) {
                      return coords.map((c) => Array.isArray(c) ? { lat: c[1], lng: c[0] } : c);
                    }
                    return lane.waypoints ?? [];
                  })()}
                  speedEditSegments={rsaPathsHook.speedEditMode && rsaPathsHook.activeLaneId
                    ? (rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId)?.speedSegments ?? [])
                    : []}
                  onSpeedBreakpointAdded={(mileMark) => {
                    if (rsaPathsHook.activeLaneId) {
                      rsaPathsHook.actions.addSpeedBreakpoint(rsaPathsHook.activeLaneId, mileMark, 45);
                    }
                  }}
                />
            </div>
              </div>
            </>
          )}

          {/* Dashboard Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-dashboard ${activeMainTab === 'dashboard' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              <AnalysisDashboard
                selectedRun={dashboardSelectedRun}
                setSelectedRun={setDashboardSelectedRun}
                mockProjectsData={dashboardProjectsData}
                displayRuns={displayRuns}
                runProgressMap={dashboardRunProgress}
                loading={dashboardLoading}
                error={dashboardError}
                onBrowseDeliverables={isRsaModelProject && typeof onGoToFiles === 'function' ? handleBrowseDeliverables : null}
                setActiveMainTab={setActiveMainTab}
              />
            </div>
          </div>

          <div className={`main-content-tab-panel main-content-tab-panel-deliverables rsa-wizard-step-page ${activeMainTab === 'deliverables' ? 'active' : ''}`}>
            <div className="tab-content-RSA rsa-deliverables-tab-content">
              {/* <div className="rsa-deliverables-tab-header">
                <p className="pHeader rsa-deliverables-title">Deliverables</p>
                <p className="rsa-deliverables-subtitle">
                  Manage Deliverables files directly here. This tab is intended to replace the separate files panel workflow.
                </p>
                {deliverablesCurrentPath.length > 0 && (
                  <div className="rsa-deliverables-breadcrumbs">
                    {deliverablesCurrentPath.map((pathItem, index) => (
                      <React.Fragment key={`${pathItem.id || pathItem.name}-${index}`}>
                        <button
                          type="button"
                          className={`rsa-deliverables-breadcrumb-item${index === deliverablesCurrentPath.length - 1 ? ' active' : ''}`}
                          onClick={(e) => handleDeliverablesBreadcrumbClick(index, e)}
                          title={pathItem.name}
                        >
                          {pathItem.name}
                        </button>
                        {index < deliverablesCurrentPath.length - 1 && (
                          <span className="rsa-deliverables-breadcrumb-separator" aria-hidden>
                            <i className="fas fa-chevron-right" />
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div> */}
              <div className="rsa-deliverables-explorer-host">
                {deliverablesFolderResolving ? (
                  <div className="empty-folder">
                    <i className="fas fa-spinner fa-spin" />
                    <span>Resolving Deliverables folder...</span>
                  </div>
                ) : !deliverablesScopedRootFolder ? (
                  <div className="empty-folder">
                    <i className="fas fa-circle-exclamation" />
                    <span>{deliverablesError || 'Deliverables folder could not be resolved for this project.'}</span>
                  </div>
                ) : (
                  <FileExplorer
                    ref={deliverablesExplorerRef}
                    project={{
                      Id: effectiveProjectId,
                      Name: deliverablesScopedRootFolder.name || 'Deliverables',
                    }}
                    footerExtra={
                      <button
                        type="button"
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                        onClick={() => setActiveMainTab('dashboard')}
                      >
                        <i className="fas fa-arrow-left" aria-hidden /> Back: Dashboard
                      </button>
                    }
                    onFileSelect={() => {}}
                    onFolderSelect={setDeliverablesSelectedFolder}
                    currentPath={deliverablesCurrentPath}
                    onBreadcrumbUpdate={setDeliverablesCurrentPath}
                    selectedFolder={deliverablesSelectedFolder}
                    onClearSelection={() => {}}
                    showUploadModal={showDeliverablesUploadModal}
                    onOpenUploadModal={() => setShowDeliverablesUploadModal(true)}
                    onCloseUploadModal={() => setShowDeliverablesUploadModal(false)}
                    currentFolderId={deliverablesSelectedFolder?.id || deliverablesScopedRootFolder.id || effectiveProjectId}
                    scopedRootFolder={deliverablesScopedRootFolder}
                    hideTreePanel
                    onSelectionStateChange={setDeliverablesSelectionState}
                    fileListToolbar={
                      <div className="rsa-deliverables-explorer-toolbar">
                        {previousDeliverablesBreadcrumbStep && (
                          <button
                            type="button"
                            className="rsa-deliverables-toolbar-btn"
                            onClick={handleDeliverablesBack}
                            title={previousDeliverablesBreadcrumbTooltip}
                            aria-label={previousDeliverablesBreadcrumbTooltip}
                          >
                            <i className="fas fa-arrow-left" aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          className="rsa-deliverables-toolbar-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeliverablesRefresh();
                          }}
                          title="Refresh Deliverables"
                        >
                          <i className="fas fa-sync-alt" aria-hidden />
                        </button>
                        {deliverablesSelectionState.canDeleteSelection && (
                          <button
                            type="button"
                            className="rsa-deliverables-toolbar-btn rsa-deliverables-toolbar-btn-danger"
                            onClick={handleDeliverablesDelete}
                            title="Delete selected items"
                          >
                            <i className="fas fa-trash" aria-hidden />
                            {/* <span></span> */}
                          </button>
                        )}
                        {deliverablesSelectionState.hasSelectedFiles && (
                          <button
                            type="button"
                            className="rsa-deliverables-toolbar-btn"
                            onClick={handleDeliverablesDownload}
                            title="Download selected items"
                          >
                            <i className="fas fa-download" aria-hidden />
                            {/* <span></span> */}
                          </button>
                        )}
                        <div className="rsa-deliverables-toolbar-spacer" />
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
          
          {!isRsaModelProject && (
            <>
              {/* Settings Tab Panel */}
              <div className={`main-content-tab-panel main-content-tab-panel-settings ${activeMainTab === 'settings' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              {/* Cloud Storage Service Selector */}
              <PermissionGate group="Admin" fallback={
                <div style={{ padding: '10px', background: '#fee', border: '1px solid #fcc', margin: '10px 0' }}>
                  <p style={{ margin: 0, color: '#c00' }}>
                    Warning: Storage Service Selector is only available to Admin users.
                    <br />
                    <small>Check browser console for permission details.</small>
                  </p>
                </div>
              }>
                <div className='storage-service-selector'>
                  <p className='pHeader'>Select your Cloud Storage Service:</p>
                  <div className="storage-providers-container">
                  {/* System Google Drive - Uses existing service account integration */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'system_google_drive' ? 'selected' : ''}`}
                    type="button"
                    onClick={() => {
                      setSelectedStorageProvider('system_google_drive');
                      setUseSystemGoogleDrive(true);
                      setSelectedConnection(null);
                    }}
                    title="System Google Drive (Service Account)"
                  >
                    <GoogleDriveIcon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>System Drive</span>
                  </button>

                  {/* User's Google Drive - Uses OAuth 2.0 */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'google_drive' ? 'selected' : ''} ${!hasGoogleDriveConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const googleDriveConn = storageConnections.find(c => c.provider_type === 'google_drive' && c.is_active);
                      if (googleDriveConn) {
                        setSelectedStorageProvider('google_drive');
                        setSelectedConnection(googleDriveConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('google_drive');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasGoogleDriveConnection ? 'Click to connect your Google Drive via OAuth 2.0' : 'Your Google Drive (OAuth 2.0)'}
                  >
                    <GoogleDriveIcon className="storage-provider-icon" style={{ width: '18px', height: '18px' }} />
                    <span>Google Drive</span>
                    {!hasGoogleDriveConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* AWS S3 - Uses Access Keys (NOT OAuth) */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 's3' ? 'selected' : ''} ${!hasS3Connection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const s3Conn = storageConnections.find(c => c.provider_type === 's3' && c.is_active);
                      if (s3Conn) {
                        setSelectedStorageProvider('s3');
                        setSelectedConnection(s3Conn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('s3');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasS3Connection ? 'Click to connect S3 with Access Keys' : 'AWS S3 (Access Keys)'}
                  >
                    <AmazonS3Icon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>AWS S3</span>
                    {!hasS3Connection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* Azure Blob Storage - Uses OAuth 2.0 */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'azure' ? 'selected' : ''} ${!hasAzureConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const azureConn = storageConnections.find(c => c.provider_type === 'azure' && c.is_active);
                      if (azureConn) {
                        setSelectedStorageProvider('azure');
                        setSelectedConnection(azureConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('azure');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasAzureConnection ? 'Click to connect Azure via OAuth 2.0' : 'Azure (OAuth 2.0)'}
                  >
                    <AzureIcon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>Microsoft Azure</span>
                    {!hasAzureConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* Google Cloud Storage - Uses OAuth 2.0 or Service Account */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'gcp' ? 'selected' : ''} ${!hasGCPConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const gcpConn = storageConnections.find(c => c.provider_type === 'gcp' && c.is_active);
                      if (gcpConn) {
                        setSelectedStorageProvider('gcp');
                        setSelectedConnection(gcpConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('gcp');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasGCPConnection ? 'Click to connect GCP via OAuth 2.0 or Service Account' : 'GCP (OAuth 2.0)'}
                  >
                    <GoogleCloudIcon className="storage-provider-icon" style={{ width: '22px', height: '22px' }} />
                    <span>Google Cloud Storage</span>
                    {!hasGCPConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>
                </div>
                </div>
              </PermissionGate>
              </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Right side panels - Summary and TabsRSA side by side */}
      <div className={`right-panels${(activeMainTab === 'routes' || activeMainTab === 'analysis') ? ' right-panels-routes' : ''}`}>
        {activeMainTab === 'dashboard' ? (
          /* Dashboard Projects Panel */
          <DashboardProjectsPanel
            runs={displayRuns}
            selectedRun={dashboardSelectedRun}
            onSelectRun={setDashboardSelectedRun}
            onRequestDeleteRun={handleRequestDeleteDashboardRun}
            onCancelDeleteRun={handleCancelDeleteDashboardRun}
            onConfirmDeleteRun={handleDeleteDashboardRun}
            confirmingRunId={dashboardDeleteCandidateRunId}
            deletingRunId={dashboardDeletingRunId}
            runProgressMap={dashboardRunProgress}
          />
        ) : !isRsaModelProject && activeMainTab === 'paths' ? (
          /* Paths Panel */
          <PathsPanel
            mode={pathsPanelMode}
            lasBoundingBoxes={lasBoundingBoxes}
            orthoTiles={orthoTiles}
            lasVisible={lasBoundingBoxesVisible}
            onToggleLasVisibility={() => setLasBoundingBoxesVisible((v) => !v)}
            onClearLas={() => {
              setLasBoundingBoxes([]);
              setSelectedLasBoundingBoxIndex(0);
            }}
            onSelectedLasExtentIndexChange={setSelectedLasBoundingBoxIndex}
            orthoVisible={orthoTilesVisible}
            onToggleOrthoVisibility={() => setOrthoTilesVisible((v) => !v)}
            onClearOrtho={() => {
              if (projectId) {
                saveOrthoTilesToCache(projectId, []);
              }
              setOrthoTiles([]);
              setMaps2000ByPath({});
            }}
            onGoToLasBounds={(b) => setFitToBounds(b)}
            loadingLasBounds={loadingLasBounds}
            loadingOrthoTiles={loadingOrthoTiles}
            orthoImagesLoading={orthoTiles.length > 0 && (imageOverlaysTotal === 0 || imageOverlaysLoadedCount < imageOverlaysTotal)}
            orthoLoadedCount={orthoTiles.length === 0 ? 0 : Math.min(orthoTiles.length, Math.max(0, imageOverlaysLoadedCount))}
            orthoTotal={orthoTiles.length}
            maps2000ByPath={maps2000ByPath}
            loadingMaps2000Check={loadingMaps2000Check}
            onRescanMaps2000={checkMaps2000ForPaths}
            onLoadLasToMap={loadLasToMap}
            hasFoldersSelected={mapOverlaySelectedFolders.length > 0}
            onLoadOrthoMaps={loadOrthoMaps}
            onCreateOffsetLane={createOffsetLaneForPath}
            onStartManualLane={startManualLaneCreation}
            onStopManualLane={stopManualLaneCreation}
            manualLaneCreationActive={manualLaneCreationActive}
            activeManualLanePointCount={activeManualLanePointCount}
            wktAvailable={Boolean(normalizeWktInput(String(mapOverlayLocalConfig?.wkt ?? '').trim()))}
            overlayOpacity={overlayOpacity}
            onOverlayOpacityChange={setOverlayOpacity}
            intersectionDrawingMode={intersectionDrawingMode}
            onIntersectionDrawingModeChange={handleIntersectionDrawingModeChange}
            intersectionOverlays={intersectionOverlays}
            onIntersectionOverlayUpdate={(id, payload) => setIntersectionOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...payload } : o)))}
            onClearIntersectionOverlays={() => setIntersectionOverlays([])}
            onIntersectionOverlayRemove={(id) => setIntersectionOverlays((prev) => prev.filter((o) => o.id !== id))}
            curvePoints={curvePoints}
            measurePoints={measurePoints}
            onResetMeasureDistance={() => setMeasurePoints([])}
            curvedLineEditPointCount={curvedLineEditPointCount}
            onCurvedLineEditPointCountChange={setCurvedLineEditPointCount}
            onGoToSegment={(idxOrPathData) => {
              if (typeof idxOrPathData === 'number') {
                const pathData = rsaLegacySegments[idxOrPathData]?.pathData;
                if (pathData?.geometry?.coordinates?.length) setFitToPathData(pathData);
              } else if (idxOrPathData?.geometry?.coordinates?.length) {
                setFitToPathData(idxOrPathData);
              }
            }}
            onSavePath={saveLasSeparationPathToDrive}
            isSavingPath={savingLasSeparationPath}
            onCreateOrthoMaps={createOrthoMaps}
            creatingOrthoMapsByPath={creatingOrthoMapsByPath}
            orthoProgressByPath={orthoProgressByPath}
            onDeletePath={handleDeletePathWithCleanup}
          />
        ) : (
          <>
            {/* Summary Component - Always visible on the right side */}
            {/* Horizontal/Embankment Tabs - Side by side with summary */}
            {(services.includes('horizontal') || services.includes('embankment')) && (
              <TabsRSA
                services={services}
                lidarType={lidarType}
                onSaveMap={{
                  horizontal: setHorizontalData,
                  embankment: setEmbankmentData
                }}
              />
            )}
            <RSASummary
              activeMainTab={activeMainTab}
              mapOverlayLocalConfig={mapOverlayLocalConfig}
              wktValidation={wktValidation}
              mapOverlaySelectedFolders={mapOverlaySelectedFolders}
              setupFolderAvailable={Boolean(effectiveSetupFolderId)}
              lasBoundingBoxes={lasBoundingBoxes}
              orthoTiles={orthoTiles}
              existingProjectName={existingProjectName}
              selectedProjectId={effectiveProjectId}
              rsaGroupsMileages={rsaGroupsMileages}
              rsaProjectsPerGroup={rsaProjectsPerGroup}
              rsaGroupsIsdManeuvers={rsaGroupsIsdManeuvers}
              services={services}
              selectedFolders={selectedFolders}
              selectedDriveName={selectedDriveName}
              roadDetectionCategory={roadDetectionCategory}
              horizontalData={horizontalData}
              EmbankmentData={EmbankmentData}
              selectedStorageProvider={selectedStorageProvider}
              onSubmit={async (e) => {
                if (routesXmlFiles.length > 0) {
                  const routesSyncResult = await saveRoutesMatrixSelection();
                  if (!routesSyncResult?.ok) {
                    return;
                  }
                }
                await handleSubmit(e);
              }}
              onClear={handleClearClick}
              onSaveSetup={handleSaveSetupClick}
              savingSetup={savingSetup}
              wktInputLocked={wktInputLocked}
              onOpenWktRequirementsHelp={() => setWktRequirementsHelpModalOpen(true)}
              isRsaModelProject={isRsaModelProject}
              rsaModelRouteInfoRef={rsaModelRouteInfoRef}
              rsaRunMetricsRef={rsaRunMetricsRef}
              rsaUsageLedgerState={rsaUsageLedgerState}
              rsaUsageLedgerLoading={rsaUsageLedgerLoading}
              routesSourceFiles={routesSourceFiles}
              routesXmlFiles={routesXmlFiles}
              modelFiles={modelFiles}
              modelFolderId={modelFolderId}
              existingSourceFolderId={existingSourceFolderId}
              existingSourceFiles={existingSourceFiles}
              routesMatrixSelection={routesMatrixSelection}
              onToggleRouteFile={toggleRoutesMatrixCell}
              routesMatrixSaving={routesMatrixSaving}
            />
          </>
        )}
      </div>
      <WktRequirementsHelpModal
        open={wktRequirementsHelpModalOpen}
        onClose={() => setWktRequirementsHelpModalOpen(false)}
      />
    </div>
    </RsaPathsContext.Provider>
  );
}

export default PayloadForm;
